import UserService from "./UserService";
import UtilityService from "./UtilityService";
import Kichiri from "./KichiriService";

import moment from "moment";
import ClientService from "./ClientService";
import PlaidService from "./PlaidService";
import { authedFetch, readJsonAnswer, callRouteThatExplainsItsRefusals, COULD_NOT_REACH_THE_SERVER } from "./apiFetch";
import { CANONICAL } from "../constants/CanonicalCodes";
import TAX_CATEGORIES, { TAX_CARRIED_BY_LABEL, LABELS_MEANING_NO_TAX, TAX_NOT_SPLIT_REASONS } from "../constants/Taxes";


export const PERIOD_BASIS = {
	FULL_YEAR: "full_year",
	FIRST_YEAR: "first_year",
	YEAR_END_CHANGE: "year_end_change",
	FINAL_PERIOD: "final_period",
	INTERIM: "interim",
};

export const CUSTOM_PERIOD_LENGTH = "custom";

export const PERIOD_LENGTH_OPTIONS = [
	{ value: 12, label: "12 months" },
	{ value: 6, label: "6 months" },
	{ value: 3, label: "3 months" },
	{ value: 1, label: "1 month" },
	{ value: CUSTOM_PERIOD_LENGTH, label: "Custom start date" },
];

const LONGEST_LEGAL_TAX_YEAR_IN_DAYS = 371;
const SHORTEST_PERIOD_TREATED_AS_A_FULL_YEAR_IN_DAYS = 350;
const LONGEST_MONTHLY_PERIOD_IN_DAYS = 31;
const REGISTRY_FILE_NAME_LIMIT = 255;
const REQUEST_FAILED_MESSAGE = "The server rejected the request. Check the API log — if this API points at a database without the latest migration, the fiscal period columns are missing.";
const LONGEST_QUARTERLY_PERIOD_IN_DAYS = 92;

// What a bank writes in front of nearly every line. They say how the money moved, never
// who took it, so two rows sharing only these are not the same merchant.
const MONEY_MOVEMENT_WORDS = [
	"interac", "purchase", "payment", "pymt", "transfer", "etransfer", "sent", "received",
	"cheque", "check", "chq", "deposit", "withdrawal", "eft", "pos", "debit", "credit",
	"pre", "auth", "authorized", "automatic", "misc", "miscellaneous", "transaction",
	"from", "to", "the", "and", "for", "of", "at", "on", "in", "by", "with",
];

const derivePeriodTypeFromLength = ({ isChosenInterim, daysInPeriod }) => {
	if (!isChosenInterim) return "annual";
	if (daysInPeriod <= LONGEST_MONTHLY_PERIOD_IN_DAYS) return "monthly";
	if (daysInPeriod <= LONGEST_QUARTERLY_PERIOD_IN_DAYS) return "quarterly";
	return "interim";
};

export const REVIEW_STATUS_FILTERS = {
	NOT_APPROVED: "not_approved",
	APPROVED: "approved",
	HAS_ISSUE: "has_issue",
};

export const REVIEW_STATUS_FILTER_OPTIONS = [
	{ value: "", label: "All statuses" },
	{ value: REVIEW_STATUS_FILTERS.NOT_APPROVED, label: "Not yet approved" },
	{ value: REVIEW_STATUS_FILTERS.APPROVED, label: "Approved" },
	{ value: REVIEW_STATUS_FILTERS.HAS_ISSUE, label: "Has an issue" },
];

export const ARCHIVE_REASONS = {
	DUPLICATE: "duplicate",
	NOT_A_TRANSACTION: "not_a_transaction",
	PARSE_ERROR: "parse_error",
	WRONG_ACCOUNT: "wrong_account",
	STATEMENT_REMOVED: "statement_removed",
	OTHER: "other",
};

export const ARCHIVE_REASON_OPTIONS = [
	{ value: ARCHIVE_REASONS.DUPLICATE, label: "Duplicate of another row" },
	{ value: ARCHIVE_REASONS.NOT_A_TRANSACTION, label: "Not a transaction (summary or header line)" },
	{ value: ARCHIVE_REASONS.PARSE_ERROR, label: "Read incorrectly from the statement" },
	{ value: ARCHIVE_REASONS.WRONG_ACCOUNT, label: "Belongs to another account or entity" },
	{ value: ARCHIVE_REASONS.OTHER, label: "Other" },
];

const ARCHIVE_REASON_LABELS = ARCHIVE_REASON_OPTIONS.reduce((labels, option) => {
	labels[option.value] = option.label;
	return labels;
}, {});

// Set by the tool rather than picked by a person, so it is not in the options a
// preparer chooses from — but the Archived tab still has to be able to name it.
ARCHIVE_REASON_LABELS[ARCHIVE_REASONS.STATEMENT_REMOVED] = "Its bank statement was removed";

const buildDuplicateKey = ({ transaction }) => {
	const date = transaction.date || "";
	const description = String(transaction.description || "").trim().toLowerCase();
	const amount = Number(transaction.amount);
	if (!date || !description || isNaN(amount)) return "";
	return [date, description, amount.toFixed(2)].join("|");
};

const SUSPENSE_ACCOUNT_PATTERN = /suspense/i;
const DEFAULT_SUSPENSE_ACCOUNT_NAME = "9999 - Suspense";
const OPENING_BALANCE_TOLERANCE = 0.005;
const COULD_NOT_READ_STATEMENT = "Couldn't read that statement — check the file and try again.";

export const ISSUE_STATUSES = [
	{ value: "pending", label: "Pending" },
	{ value: "in_progress", label: "In Progress" },
	{ value: "resolved", label: "Resolved" },
];
const DEFAULT_ISSUE_STATUS = "pending";

const UNAMBIGUOUS_DATE_FORMATS = [
	"YYYY-MM-DD", "YYYY/MM/DD", "YYYYMMDD", "YY-MM-DD", "YY/MM/DD",
	"DD-MMM-YYYY", "D-MMM-YYYY", "DD MMM YYYY", "D MMM YYYY",
	"MMM DD, YYYY", "MMM DD YYYY", "MMM D, YYYY", "MMM D YYYY",
	"DD-MMM-YY", "D-MMM-YY", "DD MMM YY", "MMM DD, YY", "MMM DD YY",
	"DD MMMM YYYY", "D MMMM YYYY", "MMMM DD, YYYY", "MMMM DD YYYY",
	"MMMM D, YYYY", "MMMM D YYYY", "DD MMMM YY", "MMMM DD, YY", "MMMM DD YY",
];

const DAY_FIRST_DATE_FORMATS = [
	"DD-MM-YYYY", "D-M-YYYY", "DD/MM/YYYY", "D/M/YYYY", "DD.MM.YYYY", "D.M.YYYY", "DD MM YYYY",
	"DD-MM-YY", "D-M-YY", "DD/MM/YY", "D/M/YY", "DD.MM.YY",
];

const MONTH_FIRST_DATE_FORMATS = [
	"MM-DD-YYYY", "M-D-YYYY", "MM/DD/YYYY", "M/D/YYYY", "MM.DD.YYYY", "M.D.YYYY",
	"MM-DD-YY", "M-D-YY", "MM/DD/YY", "M/D/YY", "MM.DD.YY",
];

const DATE_FORMATS = [...UNAMBIGUOUS_DATE_FORMATS, ...DAY_FIRST_DATE_FORMATS, ...MONTH_FIRST_DATE_FORMATS];

const AMOUNT_TOLERANCE = 0.01;
const DATE_TOLERANCE_DAYS = 3;
const CROSS_CURRENCY_TOLERANCE_RATE = 0.03;

const CURRENCY_NAME_HINTS = [
	{ code: "USD", hints: ["usd", "us dollar", "u.s. dollar", "us$", "$us", "usdollar"] },
	{ code: "EUR", hints: ["eur", "euro", "€"] },
	{ code: "GBP", hints: ["gbp", "sterling", "£"] },
];


function isUnclassified(classification) {
	if (classification === null || classification === undefined) return true;
	let value = UtilityService.toSearchText(classification);
	return value === "" || value === "unclassified" || value === "uncategorized";
}

function stillNeedsAnAccount(transaction) {
	const settledAsATransferLeg = transaction.classification_method === "transfer";
	if (settledAsATransferLeg) return false;

	const hasNoAccountYet = isUnclassified(transaction.classification);
	return hasNoAccountYet;
}

const LEDGER_ANSWERS_THAT_NAME_NO_ACCOUNT = ["Uncategorized", "Needs Review"];
const METHODS_A_LEDGER_OR_PAYEE_RULE_PLACED = ["gl_reference", "payee_rule"];

function theChartLacksTheLedgersAccountForAPlacedRow({ transaction, mappedAccount, chartRow, ownLabelChartRow, clientChart }) {
	const theLedgerNamedAnAccount = !!mappedAccount && !LEDGER_ANSWERS_THAT_NAME_NO_ACCOUNT.includes(mappedAccount);
	const theChartHoldsTheLedgersAccount = !!chartRow;
	const theChartLoaded = Array.isArray(clientChart) && clientChart.length > 0;
	const theRowIsStillWaiting = stillNeedsAnAccount(transaction);
	const isNotHeldBack = !theLedgerNamedAnAccount || theChartHoldsTheLedgersAccount || !theChartLoaded || theRowIsStillWaiting;
	if (isNotHeldBack) return null;

	const aLedgerOrPayeeRulePlacedItBefore = METHODS_A_LEDGER_OR_PAYEE_RULE_PLACED.includes(transaction.classification_method);
	if (!aLedgerOrPayeeRulePlacedItBefore) return "keptAsAGuess";

	const itsOwnLabelIsOnTheChart = !!ownLabelChartRow;
	const howItIsHeldBack = itsOwnLabelIsOnTheChart ? "keptOnTheEarlierLedgerAccount" : "sentBackToReview";
	return howItIsHeldBack;
}

/**
 * Looks across a whole column of raw date strings for one that is unambiguous between
 * day-first and month-first reading — a day or a month component greater than 12 — and reports
 * which convention the column uses. Numeric day/month dates under 13 read correctly either way,
 * so guessing convention from one such row at a time silently swaps day and month on whichever
 * rows just happen to land in that range; one unambiguous row settles it for the whole column.
 * Returns null when no row in the column settles it, so the caller can fall back consistently
 * rather than guess per row.
 *
 * @param {Array} rawDateStrings
 * @returns {Boolean|null} true for day-first, false for month-first, null if undetermined
 */
function detectWhetherTheDateColumnIsDayFirst(rawDateStrings) {
	for (let rawDateString of rawDateStrings || []) {
		if (!rawDateString) continue;
		let trimmed = String(rawDateString).trim();
		if (!trimmed) continue;

		let readsAsUnambiguous = moment(trimmed, UNAMBIGUOUS_DATE_FORMATS, true).isValid();
		if (readsAsUnambiguous) continue;

		let dayFirstReading = moment(trimmed, DAY_FIRST_DATE_FORMATS, true);
		let monthFirstReading = moment(trimmed, MONTH_FIRST_DATE_FORMATS, true);
		let onlyDayFirstWorks = dayFirstReading.isValid() && !monthFirstReading.isValid();
		let onlyMonthFirstWorks = monthFirstReading.isValid() && !dayFirstReading.isValid();
		if (onlyDayFirstWorks) return true;
		if (onlyMonthFirstWorks) return false;
	}
	return null;
}

function normalizeDate(dateString, isColumnDayFirst) {
	if (!dateString) return null;

	let trimmed = dateString.trim();
	if (!trimmed) return null;

	let unambiguousReading = moment(trimmed, UNAMBIGUOUS_DATE_FORMATS, true);
	if (unambiguousReading.isValid()) return unambiguousReading.format("YYYY-MM-DD");

	let preferredFormats = isColumnDayFirst === false ? MONTH_FIRST_DATE_FORMATS : DAY_FIRST_DATE_FORMATS;
	let otherFormats = isColumnDayFirst === false ? DAY_FIRST_DATE_FORMATS : MONTH_FIRST_DATE_FORMATS;

	let preferredReading = moment(trimmed, preferredFormats, true);
	if (preferredReading.isValid()) return preferredReading.format("YYYY-MM-DD");

	let otherReading = moment(trimmed, otherFormats, true);
	if (otherReading.isValid()) return otherReading.format("YYYY-MM-DD");

	let fallback = moment(trimmed);
	if (fallback.isValid()) return fallback.format("YYYY-MM-DD");

	return null;
}

function normalizeAmount(amountString) {
	if (amountString === null || amountString === undefined || amountString === "") return NaN;

	let cleaned = String(amountString).trim().replace(/\s/g, "");

	let isParenthesisNegative = /^\(.*\)$/.test(cleaned);
	cleaned = cleaned.replace(/[()]/g, "");
	cleaned = cleaned.replace(/[$£€¥₹₩₪]/g, "");
	cleaned = cleaned.trim();

	if (!cleaned) return NaN;

	if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(cleaned)) {
		cleaned = cleaned.replace(/\./g, "").replace(",", ".");
	} else {
		cleaned = cleaned.replace(/,/g, "");
	}

	let value = parseFloat(cleaned);
	if (isNaN(value)) return NaN;

	return isParenthesisNegative ? -Math.abs(value) : value;
}


function readRateFromLabel({ taxCode }) {
	const words = String(taxCode).trim().split(" ");
	const percentWord = words.find(word => word.endsWith("%"));
	if (!percentWord) return null;
	const rate = parseFloat(percentWord);
	const isUsableRate = isFinite(rate) && rate > 0;
	const usableRate = isUsableRate ? rate : null;
	return usableRate;
}

function describeTaxCarriedByLabel({ taxCode }) {
	const label = String(taxCode || "").trim();
	const meansNoTax = LABELS_MEANING_NO_TAX.includes(label.toLowerCase());
	if (meansNoTax) return null;

	const known = TAX_CARRIED_BY_LABEL[label];
	if (known) return known;

	const rate = readRateFromLabel({ taxCode: label });
	if (rate === null) return null;

	const takenAtItsWord = { priceIncludesRate: rate, gstHstRate: rate, recoverableShare: 1, provincialRate: 0 };
	return takenAtItsWord;
}

function sumWhatTheBooksPosted({ transactions }) {
	let totalPosted = 0;
	let totalTax = 0;
	for (const transaction of transactions || []) {
		const posted = transaction.postedAmount != null ? transaction.postedAmount : transaction.amount || 0;
		totalPosted += posted;
		totalTax += transaction.taxAmount || 0;
	}
	return { totalPosted, totalTax };
}

function findSuspenseAccount(classifications) {
	for (let classification of classifications) {
		if (SUSPENSE_ACCOUNT_PATTERN.test(classification.name)) return classification;

		let accountNumber = classification.name.split(" - ")[0]?.trim();
		if (/^9{3,}/.test(accountNumber)) return classification;
	}
	return null;
}

function cadEquivalentAmount({ transaction }) {
	const cadAmount = Number(transaction.amount) || 0;
	return cadAmount;
}

function describeLegForPairing({ transaction }) {
	return {
		transaction,
		cadAmount: cadEquivalentAmount({ transaction }),
		accountName: transferAccountName({ transaction }),
		currency: transaction.currency || "CAD",
		happenedAt: new Date(transaction.date).getTime(),
	};
}

function transferLegsMatchInCad({ legA, legB, allowCrossCurrency = true }) {
	if (Math.sign(legA.cadAmount) === Math.sign(legB.cadAmount)) return false;

	const isCrossCurrency = legA.currency !== legB.currency;
	if (isCrossCurrency && !allowCrossCurrency) return false;

	const largerCadValue = Math.max(Math.abs(legA.cadAmount), Math.abs(legB.cadAmount));
	const allowedDifference = isCrossCurrency ? largerCadValue * CROSS_CURRENCY_TOLERANCE_RATE : AMOUNT_TOLERANCE;

	const cadDifference = Math.abs(legA.cadAmount + legB.cadAmount);
	return cadDifference <= allowedDifference;
}

function transferAccountName({ transaction }) {
	return transaction.bankAccountName || transaction.accountName || "";
}

const RELATED_PARTY_DESCRIPTION_PATTERN = /(shareholder|director|related\s*party|due\s*to|due\s*from\s*related|family|spouse|founder)/i;

function isTransferPairCandidate({ transaction }) {
	if (transaction.notATransfer === true) return false;

	const accountName = transferAccountName({ transaction });
	if (!accountName) return false;

	const amount = Number(transaction.amount);
	if (!amount || isNaN(amount)) return false;

	const looksLikeARelatedPartyTransaction = RELATED_PARTY_DESCRIPTION_PATTERN.test(transaction.description || "");
	if (looksLikeARelatedPartyTransaction) return false;

	return true;
}

function buildTransferPairSummary({ transactionA, transactionB }) {
	const outLeg = transactionA.amount < 0 ? transactionA : transactionB;
	const inLeg = transactionA.amount < 0 ? transactionB : transactionA;
	const isCrossCurrency = (outLeg.currency || "CAD") !== (inLeg.currency || "CAD");
	const isApplied = !!inLeg.mirrors_transaction_id || !!outLeg.mirrors_transaction_id;
	const status = isApplied ? "applied" : "proposed";
	const cadValue = Math.abs(cadEquivalentAmount({ transaction: outLeg }));
	return { outLeg, inLeg, cadValue, isCrossCurrency, status };
}

function datesAreWithinTolerance({ legA, legB }) {
	let dayDifference = Math.abs(legA.happenedAt - legB.happenedAt) / 86400000;
	return dayDifference <= DATE_TOLERANCE_DAYS;
}

function buildFullAccountDescription(accountNumber, accountDescription) {
	return `${accountNumber} - ${accountDescription}`;
}

function normalizeConfidence(rawConfidence) {
	if (rawConfidence === null || rawConfidence === undefined) return null;
	if (rawConfidence > 1) return rawConfidence / 100;
	return rawConfidence;
}

function hasNonZeroAmount(transaction) {
	const isNonZeroNumber = !isNaN(transaction.amount) && transaction.amount !== 0;
	return isNonZeroNumber;
}

function isDigitCharacter(character) {
	const isDigit = character >= "0" && character <= "9";
	return isDigit;
}


function buildAccountNumberMap({ generalLedgerEntries, resolveKey }) {
	const accountNumberByKey = {};
	(generalLedgerEntries || []).forEach(entry => {
		const key = resolveKey(entry);
		if (!key) return;
		const accountNumber = entry.accountNumber || UtilityService.getLeadingNumber(entry.account);
		if (accountNumber && !accountNumberByKey[key]) {
			accountNumberByKey[key] = accountNumber;
		}
	});
	return accountNumberByKey;
}

function isMappedToSuspense(transaction) {
	const canonicalCode = transaction.canonical_code || "";
	const classificationName = transaction.classification || "";
	const hasSuspenseCanonicalCode = canonicalCode.startsWith("SUSPENSE.");
	const hasSuspenseClassificationName = SUSPENSE_ACCOUNT_PATTERN.test(classificationName);
	const mappedToSuspense = hasSuspenseCanonicalCode || hasSuspenseClassificationName;
	return mappedToSuspense;
}

function needsSuspenseReview(transaction) {
	const unclassifiedOrSuspense = stillNeedsAnAccount(transaction) || isMappedToSuspense(transaction);
	return unclassifiedOrSuspense;
}

const METHODS_A_PERSON_MUST_SIGN_OFF = ["ai", "unmatched"];
const METHODS_THAT_CAN_BE_ACCEPTED = [...METHODS_A_PERSON_MUST_SIGN_OFF];

function awaitsReviewerSignOff(transaction) {
	const wasGuessedByTheTool = METHODS_A_PERSON_MUST_SIGN_OFF.includes(transaction.classification_method);
	if (!wasGuessedByTheTool) return false;
	if (!transaction.canonical_code) return false;

	const alreadySignedOff = transaction.classification_status === "approved";
	return !alreadySignedOff;
}

function canAcceptClassification(transaction) {
	const couldBeAccepted = METHODS_THAT_CAN_BE_ACCEPTED.includes(transaction.classification_method);
	if (!couldBeAccepted) return false;
	if (!transaction.canonical_code) return false;

	const alreadyAccepted = transaction.classification_status === "approved";
	return !alreadyAccepted;
}

function normalizeReviewIssue(reviewNote) {
	if (Array.isArray(reviewNote)) {
		const hasComments = reviewNote.length > 0;
		return { status: hasComments ? DEFAULT_ISSUE_STATUS : null, comments: reviewNote };
	}
	if (reviewNote && typeof reviewNote === "object") {
		const comments = Array.isArray(reviewNote.comments) ? reviewNote.comments : [];
		const status = comments.length > 0 ? reviewNote.status || DEFAULT_ISSUE_STATUS : null;
		return { status, comments };
	}
	return { status: null, comments: [] };
}

function getTransactionDecisionMethod(transaction) {
	if (transaction.classification_method) return transaction.classification_method;
	if (transaction.confidence != null) return "ai";
	return "manual";
}

function describeDecisionSource(transactions) {
	const methodValues = transactions.map(getTransactionDecisionMethod);
	const methods = new Set(methodValues);
	if (methods.size > 1) return "Mixed";

	const methodList = Array.from(methods);
	const method = methodList[0];
	if (method === "gl_reference") return "GL match";
	if (method === "ai") return "AI";
	if (method === "manual") return "Manual";
	return "Rule";
}

function rankDecisionRisk({ isException, sourceLabel }) {
	if (isException) return 0;
	if (sourceLabel === "Manual" || sourceLabel === "Mixed") return 1;
	if (sourceLabel === "AI" || sourceLabel === "Rule") return 2;
	return 3;
}

function compareDecisionsByRisk(decisionA, decisionB) {
	if (decisionA.riskRank !== decisionB.riskRank) return decisionA.riskRank - decisionB.riskRank;
	return decisionA.classification.localeCompare(decisionB.classification);
}

function rankIssueStatus(status) {
	if (status === "resolved") return 1;
	return 0;
}

function compareIssuesByStatusThenAccount(issueA, issueB) {
	const rankA = rankIssueStatus(issueA.status);
	const rankB = rankIssueStatus(issueB.status);
	if (rankA !== rankB) return rankA - rankB;
	return issueA.account.localeCompare(issueB.account);
}

function carriesAnIssue({ transaction }) {
	const issue = normalizeReviewIssue(transaction.review_note);
	const hasComments = issue.comments.length > 0;
	return hasComments;
}

function describeIssueRows({ transactions }) {
	const firstRow = transactions[0];
	if (!firstRow) return "";

	const description = String(firstRow.description || "").trim();
	const label = [firstRow.date, description].filter(Boolean).join(" · ");
	if (transactions.length === 1) return label;
	return `${label} and ${transactions.length - 1} more`;
}

function buildSuspenseCsvRow(transaction) {
	const emptyReasonForTheClientToFillIn = "";
	const bankAccountName = transaction.bankAccountName || transaction.accountName || "";
	const classificationName = transaction.classification || "";
	const row = [transaction.date, transaction.description, transaction.amount, bankAccountName, classificationName, emptyReasonForTheClientToFillIn];
	return row;
}

function formatGeneralLedgerTaxCode(ratePercent) {
	const roundedRate = Math.round(ratePercent * 10) / 10;
	if (roundedRate <= 0) return "Exempt";
	const taxCode = `Tax ${roundedRate}%`;
	return taxCode;
}

function deriveAccountTaxes({ accountTotals }) {
	const accountTaxes = {};

	for (const [account, totals] of Object.entries(accountTotals)) {
		if (totals.net < 1) continue;
		const ratePercent = (totals.tax / totals.net) * 100;
		accountTaxes[account] = formatGeneralLedgerTaxCode(ratePercent);
	}

	return accountTaxes;
}


function toSignSample(transaction) {
	if (!transaction) return null;
	return { description: transaction.description, amount: transaction.amount };
}

function flipTransactionAmount(transaction) {
	return { ...transaction, amount: -transaction.amount };
}

const ISO_DATE_LENGTH = 10;
const ISO_DATE_DASH_POSITIONS = [4, 7];

function isIsoDateText(text) {
	if (text.length !== ISO_DATE_LENGTH) return false;

	for (let position = 0; position < text.length; position++) {
		const character = text[position];
		const expectsDash = ISO_DATE_DASH_POSITIONS.includes(position);
		const isValidCharacter = expectsDash ? character === "-" : isDigitCharacter(character);
		if (!isValidCharacter) return false;
	}

	return true;
}


const BookkeepingService = {
	JOURNAL_PURPOSES: Object.freeze({ ADJUSTING_ENTRY: "adjusting_entry", OPENING_BALANCE_EQUITY: "opening_balance_equity", ENTERED_BY_CLIENT: "entered_by_client" }),

	/**
	 * Whether a transaction is still waiting for a person to sign off on the tool's guess.
	 *
	 * The tool guessing an account (AI, fuzzy, unmatched) is not the same as a preparer having
	 * agreed with it, so those rows wait in To Review until someone accepts them — which sets
	 * classification_status to approved and leaves classification_method alone, because how the
	 * row was classified does not change when it is accepted.
	 *
	 * This is the one definition. It was written out by hand in four places, and three of them
	 * left out the approved check, so accepting every row emptied the To Review tab while the
	 * banner and the workflow step went on reporting the same fourteen.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Boolean}
	 */
	awaitsReviewerSignOff({ transaction }) {
		const stillWaiting = awaitsReviewerSignOff(transaction);
		return stillWaiting;
	},

	/**
	 * Whether this transaction is still waiting for someone to say which account it belongs to.
	 *
	 * Having no classification is not the same as needing one. A transfer leg that has been
	 * matched is settled: it is one half of money moving between the client's own accounts, the
	 * other half carries the posting, and giving this one an account would record the movement
	 * twice. It has no classification because it should not have one.
	 *
	 * Every surface that asks this question has to ask it the same way. Eight places tested
	 * "classification is empty" in longhand, so approving a transfer removed it from the
	 * Transfers tab and left it sitting in Unclassified, in the Classify count, and in the
	 * arithmetic deciding whether the books were finished — which no amount of work could then
	 * satisfy, because a matched leg never gets a classification.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Boolean}
	 */
	stillNeedsAnAccount({ transaction }) {
		const isStillWaiting = stillNeedsAnAccount(transaction);
		return isStillWaiting;
	},

	/**
	 * Whether a person can still accept this classification.
	 *
	 * Wider than awaitsReviewerSignOff and deliberately so: the tool's own guesses have to be
	 * signed off, but a preparer may also accept one a firm rule, keyword or pattern chose, even
	 * though those never sat in To Review. The two lists are defined so that the sign-off
	 * methods are part of the acceptable ones, which is why they cannot drift apart.
	 *
	 * What both share is the status check. Accepting sets classification_status to approved, so
	 * a row already carrying it has nothing left to accept — offering the action again is the
	 * same stale-state defect that had the banner counting rows the tab had let go.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Boolean}
	 */
	canAcceptClassification({ transaction }) {
		const isStillAcceptable = canAcceptClassification(transaction);
		return isStillAcceptable;
	},

	/**
	 * The classification methods that are the tool's own guess rather than a person's or a
	 * rule's decision. Exposed so a caller asking a different question about the same fact —
	 * how confident the classification is, say — reads the one list instead of retyping it.
	 *
	 * @returns {Array<String>}
	 */
	listMethodsAPersonMustSignOff() {
		return [...METHODS_A_PERSON_MUST_SIGN_OFF];
	},

	/**
	 * Downloads the general ledger workbook for one engagement.
	 *
	 * The workbook is built on the server from the engagement's books — the same postings and
	 * trial balance the statements are drawn from — so the browser names the engagement rather
	 * than sending figures. The rows still travel for the emailed review report, which lists them
	 * beside the CaseWare and AI classifications the tool suggested.
	 *
	 * @param {String} clientId
	 * @param {Number} wpfEngagementId
	 * @param {Array} transactions
	 * @param {Array} priorYearTrialBalance
	 * @param {Object} reviewData
	 * @returns {Promise<void>}
	 */
	async downloadBookkeepingSummaries({ clientId, wpfEngagementId, transactions, priorYearTrialBalance, reviewData, invertSigns = false, sourceFiles = [] }) {

		try {
			let { client } = await ClientService.fetchClient({ clientId });

			let payload = JSON.stringify({
				clientId,
				wpfEngagementId,
				transactions,
				priorYearTrialBalance,
				reviewData,
				invertSigns,
			});
			let formData = new FormData();
			formData.append("payload", payload);
			sourceFiles.forEach(file => formData.append("files", file, file.name));

			let response = await authedFetch(`/tools/bookkeeping/download_summaries`, {
				method: "POST",
				body: formData,
			});

			let blob = await response.blob();

			UtilityService.downloadFile({ blob, fileName: `${client.name} Summary.xlsx` });
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Returns the province name when it matches a known tax province, otherwise an
	 * empty string. Junk values saved by older builds (like abandoned typed text)
	 * are treated as no province so the user is asked to pick a real one.
	 *
	 * @param {String} province
	 * @returns {String}
	 */
	/**
	 * The provinces the tool can actually apply tax to, shaped for a Tools dropdown.
	 * Taken from the tax table itself so the list and the validation can never drift
	 * apart — anything offered here is accepted by getKnownProvinceOrEmpty.
	 *
	 * @returns {Array} [{ value, label }]
	 */
	getProvinceOptions() {
		const provinceOptions = Object.keys(TAX_CATEGORIES).map(province => ({ value: province, label: province }));
		return provinceOptions;
	},

	getKnownProvinceOrEmpty({ province }) {
		const isKnownProvince = Boolean(TAX_CATEGORIES[province]);
		const knownProvince = isKnownProvince ? province : "";
		return knownProvince;
	},

	/**
	 * Returns the classification list with a Suspense account guaranteed: when the
	 * list has no suspense-named or 9xxx account, the default "9999 - Suspense"
	 * entry is appended so every chart of accounts has somewhere to park unclear
	 * transactions.
	 *
	 * @param {Array} classifications
	 * @returns {Array}
	 */
	ensureSuspenseClassification({ classifications }) {
		const existingClassifications = classifications || [];
		const existingSuspenseAccount = findSuspenseAccount(existingClassifications);
		if (existingSuspenseAccount) return existingClassifications;

		const defaultSuspenseAccount = {
			name: DEFAULT_SUSPENSE_ACCOUNT_NAME,
			canonical_code: CANONICAL.CLEARING_ACCOUNT,
			gifi_code: null,
			canonical_label: DEFAULT_SUSPENSE_ACCOUNT_NAME,
		};
		const classificationsWithSuspense = [...existingClassifications, defaultSuspenseAccount];
		return classificationsWithSuspense;
	},

	/**
	 * Groups transactions into allocation decisions a reviewer can judge as one unit:
	 * every transaction sharing a classification and tax code forms one decision, with
	 * its source (GL match, AI, manual, rule), transaction count, total amount, lowest
	 * AI confidence, and review progress. Decisions come back ordered by risk —
	 * exceptions (unclassified or suspense) first, manual next, AI and rules after,
	 * GL matches last.
	 *
	 * A settled transfer leg is not an allocation decision and is left out. It does not post —
	 * the payment is recorded on the other leg — so there is no account to choose for it, and
	 * asking is asking twice about one payment. Counting them here put eleven already-matched
	 * legs into the unresolved queue and overstated it by 11,395.40, while the Transfers tab
	 * had already let them go.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array} decisions: { decisionKey, classification, tax, transactions,
	 *   sourceLabel, isException, isUnclassifiedDecision, reviewedCount, isFullyReviewed,
	 *   totalAmount, lowestConfidence, riskRank }
	 */
	buildReviewDecisions({ transactions }) {
		const decisionsByKey = {};
		const needingADecision = (transactions || []).filter(transaction => transaction.classification_method !== "transfer");

		for (const transaction of needingADecision) {
			const classificationName = isUnclassified(transaction.classification) ? "Unclassified" : transaction.classification;
			const tax = transaction.tax || "";
			const decisionKey = `${classificationName}||${tax}`;
			if (!decisionsByKey[decisionKey]) {
				decisionsByKey[decisionKey] = { decisionKey, classification: classificationName, tax, canonicalCode: null, transactions: [] };
			}
			if (!decisionsByKey[decisionKey].canonicalCode && transaction.canonical_code) {
				decisionsByKey[decisionKey].canonicalCode = transaction.canonical_code;
			}
			decisionsByKey[decisionKey].transactions.push(transaction);
		}

		const decisionGroups = Object.values(decisionsByKey);
		const decisions = decisionGroups.map(decision => {
			const sourceLabel = describeDecisionSource(decision.transactions);
			const isException = decision.transactions.some(needsSuspenseReview);
			const isUnclassifiedDecision = decision.classification === "Unclassified";
			const reviewedTransactions = decision.transactions.filter(transaction => transaction.reviewed_by);
			const reviewedCount = reviewedTransactions.length;
			const totalAmount = decision.transactions.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);
			const { totalPosted, totalTax } = sumWhatTheBooksPosted({ transactions: decision.transactions });
			const allConfidences = decision.transactions.map(transaction => transaction.confidence);
			const knownConfidences = allConfidences.filter(confidence => confidence != null);
			const lowestConfidence = knownConfidences.length > 0 ? Math.min(...knownConfidences) : null;
			const reviewIssues = decision.transactions.map(transaction => normalizeReviewIssue(transaction.review_note));
			// An issue belongs to the group only when it was raised against the whole
			// group. One row's question is that row's, and saying otherwise puts a
			// badge on nine transactions nobody asked anything about.
			const everyRowCarriesTheIssue = reviewIssues.length > 0 && reviewIssues.every(issue => issue.comments.length > 0);
			const issue = everyRowCarriesTheIssue ? reviewIssues[0] : { status: null, comments: [] };
			return {
				...decision,
				noteThread: issue.comments,
				issueStatus: issue.status,
				sourceLabel,
				isException,
				isUnclassifiedDecision,
				reviewedCount,
				isFullyReviewed: reviewedCount === decision.transactions.length,
				totalAmount,
				totalPosted,
				totalTax,
				lowestConfidence,
				riskRank: rankDecisionRisk({ isException, sourceLabel }),
			};
		});

		const sortedDecisions = decisions.sort(compareDecisionsByRisk);
		return sortedDecisions;
	},

	/**
	 * Returns the tax rate a general ledger entry implies — its tax amount as a
	 * percentage of the net amount — rounded to one decimal, or null when there is
	 * no tax or no net amount to divide by.
	 *
	 * @param {Number} taxAmount
	 * @param {Number} netAmount
	 * @returns {Number|null}
	 */
	calculateTaxPercent({ taxAmount, netAmount }) {
		const tax = Math.abs(Number(taxAmount) || 0);
		const net = Math.abs(Number(netAmount) || 0);
		if (tax <= 0 || net <= 0) return null;

		const percent = Math.round((tax / net) * 1000) / 10;
		return percent;
	},

	/**
	 * Groups general ledger reference entries by account into cards a reviewer can
	 * scan the same way as the allocation decisions: each group carries its account
	 * name, its entries, the entry count and the total amount. Groups come back
	 * sorted by account name.
	 *
	 * @param {Array} entries - [{ date, account, description, amount, tax }]
	 * @returns {Array} groups: { account, entries, entryCount, totalAmount }
	 */
	groupGeneralLedgerEntriesByAccount({ entries }) {
		const groupsByAccount = {};

		for (const entry of entries || []) {
			const account = entry.account || "—";
			if (!groupsByAccount[account]) {
				groupsByAccount[account] = { account, entries: [] };
			}
			groupsByAccount[account].entries.push(entry);
		}

		const groups = Object.values(groupsByAccount);
		const summarizedGroups = groups.map(group => {
			const entryCount = group.entries.length;
			const totalAmount = group.entries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
			const entryCarryingACode = group.entries.find(entry => entry.canonical_code);
			const canonicalCode = entryCarryingACode ? entryCarryingACode.canonical_code : null;
			return { ...group, entryCount, totalAmount, canonicalCode };
		});

		const sortedGroups = summarizedGroups.sort((a, b) => a.account.localeCompare(b.account));
		return sortedGroups;
	},

	/**
	 * Filters general ledger reference entries by free text matched against the
	 * account and description. An empty search returns every entry.
	 *
	 * @param {Array} entries - [{ date, account, description, amount, tax }]
	 * @param {String} searchText
	 * @returns {Array}
	 */
	filterGeneralLedgerEntries({ entries, searchText }) {
		const cleanSearch = UtilityService.toSearchText(searchText);
		if (!cleanSearch) return entries || [];

		const safeEntries = entries || [];
		const matchingEntries = safeEntries.filter(entry => {
			const accountText = UtilityService.toSearchText(entry.account);
			const descriptionText = UtilityService.toSearchText(entry.description);
			const matchesAccount = accountText.includes(cleanSearch);
			const matchesDescription = descriptionText.includes(cleanSearch);
			return matchesAccount || matchesDescription;
		});
		return matchingEntries;
	},

	/**
	 * Filters review decisions by search text, source label, and review status
	 * ("reviewed", "needs_review", or empty for all). The search matches the account
	 * name or any of the decision's transaction descriptions, mirroring the prior-year
	 * general ledger filter.
	 *
	 * @param {Array} decisions - Output of buildReviewDecisions
	 * @param {String} searchText
	 * @param {String} sourceLabel
	 * @param {String} reviewStatus
	 * @returns {Array}
	 */
	filterReviewDecisions({ decisions, searchText, sourceLabel, reviewStatus }) {
		const cleanSearch = UtilityService.toSearchText(searchText);
		const showingOnlyFlaggedRows = reviewStatus === REVIEW_STATUS_FILTERS.HAS_ISSUE;

		const matchesStatus = decision => {
			if (!reviewStatus) return true;
			if (reviewStatus === REVIEW_STATUS_FILTERS.APPROVED) return decision.isFullyReviewed;
			if (reviewStatus === REVIEW_STATUS_FILTERS.NOT_APPROVED) return !decision.isFullyReviewed;
			if (showingOnlyFlaggedRows) return (decision.transactions || []).some(transaction => carriesAnIssue({ transaction }));
			return true;
		};

		const safeDecisions = decisions || [];
		const keptDecisions = safeDecisions.filter(decision => {
			const classificationText = UtilityService.toSearchText(decision.classification);
			const matchesClassification = classificationText.includes(cleanSearch);
			const matchesAnyTransactionDescription = (decision.transactions || []).some(transaction => {
				const descriptionText = UtilityService.toSearchText(transaction.description);
				return descriptionText.includes(cleanSearch);
			});
			const matchesSearch = !cleanSearch || matchesClassification || matchesAnyTransactionDescription;
			const matchesSource = !sourceLabel || decision.sourceLabel === sourceLabel;
			return matchesSearch && matchesSource && matchesStatus(decision);
		});

		const nothingNarrowsTheRows = !cleanSearch && !showingOnlyFlaggedRows;
		if (nothingNarrowsTheRows) return keptDecisions;

		const narrowedDecisions = keptDecisions.map(decision => {
			const classificationText = UtilityService.toSearchText(decision.classification);
			const wholeGroupMatchesTheSearch = !cleanSearch || classificationText.includes(cleanSearch);

			let rows = decision.transactions || [];
			if (!wholeGroupMatchesTheSearch) {
				rows = rows.filter(transaction => UtilityService.toSearchText(transaction.description).includes(cleanSearch));
			}
			if (showingOnlyFlaggedRows) rows = rows.filter(transaction => carriesAnIssue({ transaction }));

			if (rows.length === (decision.transactions || []).length) return decision;
			return this.rebuildDecisionAroundTransactions({ decision, transactions: rows });
		});
		return narrowedDecisions;
	},

	/**
	 * Restates a group so its heading describes only the rows left in it. A search that
	 * kept a group because three of its rows matched must not still announce ten
	 * transactions and their total: the count and the amount are what a preparer reads
	 * before acting on the group, and acting on it would otherwise reach rows the search
	 * excluded and the screen never showed.
	 *
	 * @param {Object} params
	 * @param {Object} params.decision - the group as originally built
	 * @param {Array} params.transactions - the rows that survived the search
	 * @returns {Object} the same group, restated around those rows
	 */
	rebuildDecisionAroundTransactions({ decision, transactions }) {
		const totalAmount = transactions.reduce((runningTotal, transaction) => runningTotal + (transaction.amount || 0), 0);
		const { totalPosted, totalTax } = sumWhatTheBooksPosted({ transactions });
		const reviewedCount = transactions.filter(transaction => transaction.reviewed_by).length;
		const knownConfidences = transactions.map(transaction => transaction.confidence).filter(confidence => confidence != null);

		// Every field buildReviewDecisions derives from the transaction list has to be
		// derived again here, or the heading describes rows the filter took away — an
		// exception mark left behind by the one suspense row that was filtered out is a
		// group claiming an unresolved item it no longer holds.
		return {
			...decision,
			transactions,
			totalAmount,
			totalPosted,
			totalTax,
			reviewedCount,
			isFullyReviewed: transactions.length > 0 && reviewedCount === transactions.length,
			lowestConfidence: knownConfidences.length > 0 ? Math.min(...knownConfidences) : null,
			isException: transactions.some(needsSuspenseReview),
			sourceLabel: describeDecisionSource(transactions),
		};
	},

	/**
	 * Filters review decisions to a single account, matched by canonical code rather than
	 * by display name — a decision belongs to the account when any of its transactions
	 * carry that canonical code. Used when a trial-balance row is selected, so the current
	 * side lines up with the trial balance even when the display label differs.
	 *
	 * @param {Array} decisions - Output of buildReviewDecisions
	 * @param {String} canonicalCode
	 * @returns {Array}
	 */
	getDecisionsForCanonicalCode({ decisions, canonicalCode }) {
		const safeDecisions = decisions || [];
		const decisionsForCode = safeDecisions.filter(decision => {
			const hasTransactionWithCode = (decision.transactions || []).some(transaction => transaction.canonical_code === canonicalCode);
			return hasTransactionWithCode;
		});
		return decisionsForCode;
	},

	/**
	 * Filters prior-year general-ledger entries to a single account, matched by canonical
	 * code rather than by the entry's imported label — so selecting a trial-balance row
	 * shows its GL entries even when the GL called the account something different.
	 *
	 * @param {Array} entries - GL entries, each stamped with canonical_code
	 * @param {String} canonicalCode
	 * @returns {Array}
	 */
	filterGeneralLedgerEntriesByCanonicalCode({ entries, canonicalCode }) {
		const safeEntries = entries || [];
		const matchingEntries = safeEntries.filter(entry => entry.canonical_code === canonicalCode);
		return matchingEntries;
	},

	/**
	 * Maps each canonical account to its account number, taken from the imported general
	 * ledger — the number the GL carried on that account (its own column when mapped, else
	 * the leading digits of the account name). Lets the export show the source account
	 * number next to each classified account, matched by canonical code rather than label.
	 *
	 * @param {Array} generalLedgerEntries - GL entries stamped with canonical_code and accountNumber
	 * @returns {Object} { [canonical_code]: accountNumber } — the first non-empty number wins
	 */
	buildAccountNumberByCanonicalCode({ generalLedgerEntries }) {
		const accountNumberByCanonicalCode = buildAccountNumberMap({ generalLedgerEntries, resolveKey: entry => entry.canonical_code });
		return accountNumberByCanonicalCode;
	},

	/**
	 * Maps each general-ledger account name to its account number, taken from the imported
	 * general ledger. Used to number the bank-account rows on the export, whose account is
	 * the bank name (matched by name, since a bank leg carries no classification canonical
	 * code) rather than a classified account.
	 *
	 * @param {Array} generalLedgerEntries - GL entries stamped with accountNumber
	 * @returns {Object} { [account]: accountNumber } — the first non-empty number wins
	 */
	buildAccountNumberByAccountName({ generalLedgerEntries }) {
		const accountNumberByAccountName = buildAccountNumberMap({ generalLedgerEntries, resolveKey: entry => entry.account });
		return accountNumberByAccountName;
	},

	/**
	 * Summarizes how the current transactions were classified, for the download-review
	 * screen. Every transaction lands in exactly one bucket by priority — transfer, then
	 * GL-reference match, then rule-based auto-classification, then unclassified — so the
	 * counts sum to the total. Reports coverage (share of transactions classified) and a
	 * suggestion accuracy (of the rows the tool pre-classified, the share the reviewer
	 * kept as-is), plus the list of manual overrides.
	 *
	 * A transfer is recognised by the pair the server recorded, because these counts are sent
	 * with the download and kept as the engagement's record of the session. They used to be
	 * bucketed by an isTransfer flag that only ever existed in the browser, so a preparer who
	 * reloaded before confirming filed a summary reporting no transfers at all and counting both
	 * legs of each one as classifications the model had suggested.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - the client's bookkeeping transactions
	 * @returns {Object} counts, percentages, and the manual-change list for the review modal
	 */
	buildClassificationSummary({ transactions }) {
		const safeTransactions = transactions || [];
		const totalCount = safeTransactions.length;

		const hasRealClassification = classification => !!classification && classification !== "Unclassified";

		const transferTransactions = safeTransactions.filter(transaction => transaction.classification_method === "transfer");
		const nonTransferTransactions = safeTransactions.filter(transaction => transaction.classification_method !== "transfer");
		const glReferenceTransactions = nonTransferTransactions.filter(transaction => transaction.glClassified);
		const autoClassifiedTransactions = nonTransferTransactions.filter(transaction => !transaction.glClassified && hasRealClassification(transaction.classification));
		const unclassifiedTransactions = nonTransferTransactions.filter(transaction => !transaction.glClassified && !hasRealClassification(transaction.classification));

		const manualChanges = safeTransactions.filter(transaction => {
			if (!hasRealClassification(transaction.classification)) return false;
			if (transaction.aiClassification) return transaction.classification !== transaction.aiClassification;
			if (transaction.cwClassification) return transaction.classification !== transaction.cwClassification;
			return false;
		});

		const transferCount = transferTransactions.length;
		const glReferenceCount = glReferenceTransactions.length;
		const autoClassifiedCount = autoClassifiedTransactions.length;
		const unclassifiedCount = unclassifiedTransactions.length;
		const classifiedCount = totalCount - unclassifiedCount;

		const coveragePercent = totalCount > 0 ? Math.round((classifiedCount / totalCount) * 100) : 0;

		const preClassifiedCount = glReferenceCount + autoClassifiedCount;
		const keptCount = preClassifiedCount - manualChanges.length;
		const suggestionAccuracyPercent = preClassifiedCount > 0 ? Math.round((keptCount / preClassifiedCount) * 100) : null;

		return {
			totalCount,
			glReferenceCount,
			autoClassifiedCount,
			transferCount,
			unclassifiedCount,
			classifiedCount,
			manualChangeCount: manualChanges.length,
			coveragePercent,
			suggestionAccuracyPercent,
			manualChanges,
		};
	},

	/**
	 * Stamps a reviewer's sign-off on the given transactions: reviewed_by and
	 * reviewed_at are set on every transaction in the review set, others untouched.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToReview - The rows being signed off, matched by identity
	 * @param {String} reviewerName
	 * @param {String} reviewedAt - ISO timestamp
	 * @returns {Array}
	 */
	stampReviewOnTransactions({ transactions, transactionsToReview, reviewerName, reviewedAt }) {
		const reviewSet = new Set(transactionsToReview);
		const updatedTransactions = transactions.map(transaction => (reviewSet.has(transaction) ? { ...transaction, reviewed_by: reviewerName, reviewed_at: reviewedAt } : transaction));
		return updatedTransactions;
	},

	/**
	 * Appends a comment to the review-note thread on the given transactions. The
	 * thread is append-only — reviewer questions and preparer answers accumulate
	 * with author and timestamp, so the conversation stays intact as evidence and
	 * survives value changes.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToComment - The rows the comment applies to, matched by identity
	 * @param {String} author
	 * @param {String} text
	 * @param {String} commentedAt - ISO timestamp
	 * @returns {Array}
	 */
	addReviewCommentToTransactions({ transactions, transactionsToComment, author, text, commentedAt }) {
		const cleanText = UtilityService.toTrimmedText(text);
		if (!cleanText) return transactions;

		const commentSet = new Set(transactionsToComment);
		const newComment = { author, at: commentedAt, text: cleanText };
		const updatedTransactions = transactions.map(transaction => {
			if (!commentSet.has(transaction)) return transaction;
			const issue = normalizeReviewIssue(transaction.review_note);
			const status = issue.status || DEFAULT_ISSUE_STATUS;
			const updatedComments = [...issue.comments, newComment];
			return { ...transaction, review_note: { status, comments: updatedComments } };
		});
		return updatedTransactions;
	},

	/**
	 * Sets the status of the issue (comment thread) on the given transactions,
	 * preserving the comments. Used by reviewers tracking issues in the Issues panel.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToUpdate - The rows whose issue status changes, matched by identity
	 * @param {String} status - One of ISSUE_STATUSES values
	 * @returns {Array}
	 */
	setReviewIssueStatusOnTransactions({ transactions, transactionsToUpdate, status }) {
		const updateSet = new Set(transactionsToUpdate);
		const updatedTransactions = transactions.map(transaction => {
			if (!updateSet.has(transaction)) return transaction;
			const issue = normalizeReviewIssue(transaction.review_note);
			return { ...transaction, review_note: { status, comments: issue.comments } };
		});
		return updatedTransactions;
	},

	/**
	 * Collects every allocation decision that has an issue raised (at least one
	 * comment) into a list a reviewer can track, ordered unresolved first then by
	 * account. Each issue carries its account, status, comment thread, latest
	 * comment and the transactions it applies to.
	 *
	 * @param {Array} decisions - Output of buildReviewDecisions
	 * @returns {Array} issues: { decisionKey, account, status, comments, lastComment, commentCount, transactions }
	 */
	/**
	 * Descriptions this file has answered more than one way.
	 *
	 * Rules used to make this impossible: one remembered answer per description meant every
	 * occurrence went the same way, whether or not that way was right. Without them the same
	 * merchant can land in two accounts and nothing would say so, which misstates both lines.
	 *
	 * Being left unanswered counts as one of the ways. Rows are asked about in groups of merchant
	 * and order of magnitude, so eight card payments of about three hundred dollars are one
	 * question and three of about three thousand are another — and when the second question comes
	 * back with no account, an identical description sits classified eight times and unclassified
	 * three times with nothing connecting them. That is the same disagreement as two accounts and
	 * is easier to miss, because an unclassified row looks like work not yet done rather than an
	 * answer that contradicts its neighbours.
	 *
	 * Shown rather than prevented. Genuine splits exist — one merchant selling both fuel and
	 * groceries, or a purchase large enough to capitalise — so this is a question for a preparer
	 * to settle, not an error to correct automatically.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - table rows
	 * @returns {Object} { descriptions, count }
	 */
	findDescriptionsAnsweredMoreThanOneWay({ transactions }) {
		const answersSeenPerDescription = new Map();

		for (const transaction of transactions || []) {
			if (transaction.isArchived) continue;
			if (!transaction.description) continue;

			const answer = this.stillNeedsAnAccount({ transaction })
				? "(left unanswered)"
				: transaction.canonical_code || transaction.classification;
			if (!answer) continue;

			const description = transaction.description.trim().toLowerCase();
			if (!answersSeenPerDescription.has(description)) answersSeenPerDescription.set(description, new Set());
			answersSeenPerDescription.get(description).add(answer);
		}

		const descriptions = [];
		for (const [description, answers] of answersSeenPerDescription) {
			if (answers.size > 1) descriptions.push(description);
		}

		return { descriptions, count: descriptions.length };
	},

	/**
	 * The transactions that are one leg of a transfer the tool has paired.
	 *
	 * A transfer is one movement of money seen twice, so both legs sit in the books unclassified
	 * until the pair is approved — and they were being counted as work to do as well as shown on
	 * the Transfers tab. A preparer looking at eight unclassified rows had no way to tell they
	 * were the same eight payments already waiting for them somewhere else.
	 *
	 * A pair a preparer has rejected does not appear here, because rejecting it is the statement
	 * that these are not two halves of one movement — the rows go back to being ordinary
	 * transactions that need an account, which is what the Unclassified tab is for.
	 *
	 * Takes the pairs rather than the transactions because the caller has usually just worked them
	 * out and caches them — pairing every row again to answer this would be the expensive half of
	 * the question repeated for no reason.
	 *
	 * @param {Object} params
	 * @param {Array} params.pairs - from getTransferPairs
	 * @returns {Set} the leg rows themselves, so a caller can exclude them by identity
	 */
	listTransactionsAlreadySettledAsTransfers({ pairs }) {
		const legs = new Set();

		for (const pair of pairs) {
			if (pair.outLeg) legs.add(pair.outLeg);
			if (pair.inLeg) legs.add(pair.inLeg);
		}

		return legs;
	},

	/**
	 * The transactions that still need an account from a person.
	 *
	 * One question with one answer, because half a dozen places ask it — the Unclassified tab and
	 * its count, the progress figures, the row highlight, and what gets sent to the model. They
	 * were each filtering on their own, so excluding transfers from one of them left the rest
	 * saying something different about the same rows: a tab reading nothing to do beside a banner
	 * reading eight still to classify.
	 *
	 * A leg of a paired transfer is not waiting for an account. It is waiting to be approved as a
	 * transfer, which is a different queue on a different tab; rejecting the pair breaks it and
	 * the rows come back here, which is what makes rejection the way out rather than a dead end.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Set} [params.settledAsTransfers] - from listTransactionsAlreadySettledAsTransfers, when the caller already has it
	 * @returns {Array}
	 */
	listTransactionsStillNeedingAnAccount({ transactions, settledAsTransfers = null }) {
		const legs = settledAsTransfers || this.listTransactionsAlreadySettledAsTransfers({ pairs: this.getTransferPairs({ transactions }) });

		const stillWaiting = (transactions || []).filter(transaction =>
			!transaction.isArchived && stillNeedsAnAccount(transaction) && !legs.has(transaction)
		);
		return stillWaiting;
	},

	/**
	 * Marks the rows the model is about to be asked about, so the table can show them as
	 * being classified.
	 *
	 * Rows are marked by the row itself, never by description. Marking every row that shared a
	 * description with an unplaced row pulled rows the general ledger had already placed into
	 * the model's answer, so one unplaced "MANULIFE DEPOSIT" overwrote every placed one of the
	 * same size.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array} params.rowsStillWaiting - the rows being asked about, as getClassifiedAndUnclassifiedTransactions returns them
	 * @returns {Array} a copy where only those rows carry aiClassifying: true; every other row is the same object
	 */
	markRowsTheModelIsAskedAbout({ transactions, rowsStillWaiting }) {
		const rowsBeingAsked = new Set(rowsStillWaiting);
		const markedTransactions = transactions.map(transaction => (rowsBeingAsked.has(transaction) ? { ...transaction, aiClassifying: true } : transaction));
		return markedTransactions;
	},

	/**
	 * Writes the model's answers onto the latest rows that still wait for an account.
	 *
	 * The classification pass runs for minutes and starts from one copy of the rows, while the
	 * general ledger save, the reload from the server and the tax pass keep replacing them. So an
	 * answer is written onto the rows as they are now, and only onto rows that still need an
	 * account now: a row placed while the model was thinking keeps its account, and an
	 * "Unclassified" answer never lands on a placed row.
	 *
	 * Rows are found by question key, not by identity, because the copy the pass started from may
	 * not carry the ids the server has given the rows since. Only the answer's own fields are
	 * copied; everything else on the row, such as a tax code set meanwhile, stays as it is now.
	 *
	 * @param {Object} params
	 * @param {Array} params.latestTransactions - the rows as the table holds them at the moment of writing
	 * @param {Map} params.answersByQuestionKey - buildClassificationQuestionKey → the answer's fields (classification, classification_label, account_id, aiClassification, tax, confidence, canonical_code, canonical_label, classification_method, cleaned_description, source_evidence)
	 * @returns {Array} the latest rows, the waiting ones with an answer written on and aiClassifying: false; every other row is the same object
	 */
	writeModelAnswersOntoRowsStillWaiting({ latestTransactions, answersByQuestionKey }) {
		const answerFields = ["classification", "classification_label", "account_id", "aiClassification", "tax", "confidence", "canonical_code", "canonical_label", "classification_method", "cleaned_description", "source_evidence"];
		const waitingRows = this.listTransactionsStillNeedingAnAccount({ transactions: latestTransactions });
		const rowsStillWaiting = new Set(waitingRows);

		const answeredTransactions = latestTransactions.map(transaction => {
			if (!rowsStillWaiting.has(transaction)) return transaction;

			const questionKey = this.buildClassificationQuestionKey({ description: transaction.description, amount: transaction.amount });
			const answer = answersByQuestionKey.get(questionKey);
			if (!answer) return transaction;

			const fieldsTheAnswerHolds = answerFields.filter(field => field in answer);
			const answeredRow = { ...transaction, aiClassifying: false };
			fieldsTheAnswerHolds.forEach(field => {
				answeredRow[field] = answer[field];
			});
			return answeredRow;
		});
		return answeredTransactions;
	},

	/**
	 * The rows a general ledger added to an open file may place.
	 *
	 * A ledger may put right what a tool guessed, but never what a person decided. So it may
	 * place a row with no account, or a row the AI, the unmatched pass or an earlier ledger placed
	 * that nobody has approved or reviewed. A manual, transfer, payee-rule or CaseWare row, and any
	 * approved or reviewed row, is never offered to it. Archived rows and the legs of transfers the
	 * server recorded are left out. A leg of a pair the tool merely proposes is offered like any
	 * other row: a proposal is not a decision, and on a fresh import every row, proposed legs
	 * included, is unplaced, so every row is offered, as before.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array} the rows themselves, so a caller can write them back by position
	 */
	listRowsALedgerMayPlace({ transactions }) {
		const methodsAToolChose = [...METHODS_A_PERSON_MUST_SIGN_OFF, "gl_reference"];
		const { legsAlreadySettled } = this.readTransferPairsTheServerRecorded({ transactions: transactions || [] });
		const settledAsTransfers = legsAlreadySettled;
		const waitingRows = this.listTransactionsStillNeedingAnAccount({ transactions, settledAsTransfers });
		const rowsStillWaiting = new Set(waitingRows);

		const rowsALedgerMayPlace = (transactions || []).filter(transaction => {
			if (rowsStillWaiting.has(transaction)) return true;

			const isOutOfTheBooks = transaction.isArchived || settledAsTransfers.has(transaction);
			const wasPlacedByATool = methodsAToolChose.includes(transaction.classification_method);
			const isApproved = transaction.classification_status === "approved";
			const isReviewed = !!transaction.reviewed_by;
			const isAToolGuessNobodyConfirmed = !isOutOfTheBooks && wasPlacedByATool && !isApproved && !isReviewed;
			return isAToolGuessNobodyConfirmed;
		});
		return rowsALedgerMayPlace;
	},

	getReviewIssues({ decisions }) {
		const issues = [];

		for (const decision of decisions || []) {
			const transactionsByThread = new Map();
			for (const transaction of decision.transactions) {
				const issue = normalizeReviewIssue(transaction.review_note);
				if (issue.comments.length === 0) continue;
				const threadKey = JSON.stringify(issue.comments);
				if (!transactionsByThread.has(threadKey)) transactionsByThread.set(threadKey, { issue, transactions: [] });
				transactionsByThread.get(threadKey).transactions.push(transaction);
			}

			for (const [threadKey, raised] of transactionsByThread) {
				const coversTheWholeGroup = raised.transactions.length === decision.transactions.length;
				const comments = raised.issue.comments;
				issues.push({
					issueKey: `${decision.decisionKey}||${threadKey}`,
					decisionKey: decision.decisionKey,
					account: decision.classification,
					rowLabel: coversTheWholeGroup ? "" : describeIssueRows({ transactions: raised.transactions }),
					status: raised.issue.status || DEFAULT_ISSUE_STATUS,
					comments,
					lastComment: comments[comments.length - 1],
					commentCount: comments.length,
					transactions: raised.transactions,
				});
			}
		}

		const sortedIssues = issues.sort(compareIssuesByStatusThenAccount);
		return sortedIssues;
	},

	/**
	 * Removes the reviewer's sign-off from the given transactions.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToClear - The rows losing their sign-off, matched by identity
	 * @returns {Array}
	 */
	clearReviewOnTransactions({ transactions, transactionsToClear }) {
		const clearSet = new Set(transactionsToClear);
		const updatedTransactions = transactions.map(transaction => (clearSet.has(transaction) ? { ...transaction, reviewed_by: null, reviewed_at: null } : transaction));
		return updatedTransactions;
	},

	/**
	 * Reclassifies a set of transactions to a new account as a reviewer decision:
	 * classification, canonical mapping and tax are replaced, the method becomes
	 * manual, and the reviewer's sign-off is stamped in the same step.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToChange - The rows being reclassified, matched by identity
	 * @param {Object} classification - { name, canonical_code, canonical_label, gifi_code }
	 * @param {String} tax
	 * @param {String} reviewerName
	 * @param {String} reviewedAt - ISO timestamp
	 * @returns {Array}
	 */
	reclassifyTransactions({ transactions, transactionsToChange, classification, tax, reviewerName, reviewedAt }) {
		const changeSet = new Set(transactionsToChange);
		const updatedTransactions = transactions.map(transaction => {
			if (!changeSet.has(transaction)) return transaction;
			return {
				...transaction,
				classification: classification.name,
				canonical_code: classification.canonical_code || null,
				canonical_label: classification.canonical_label || classification.name,
				gifi_code: classification.gifi_code || null,
				classification_method: "manual",
				tax,
				reviewed_by: reviewerName,
				reviewed_at: reviewedAt,
			};
		});
		return updatedTransactions;
	},

	/**
	 * Returns the transactions that still sit in suspense: still needing an account, or mapped
	 * to a suspense account (canonical SUSPENSE.* code or a suspense-named account).
	 *
	 * This is the list the client is sent to explain, so a matched transfer leg on it would ask
	 * the client what a movement between two of their own accounts was for — a question the
	 * books have already answered.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array}
	 */
	getSuspenseTransactions({ transactions }) {
		const safeTransactions = transactions || [];
		const suspenseTransactions = safeTransactions.filter(needsSuspenseReview);
		return suspenseTransactions;
	},

	/**
	 * Downloads the suspense/unclassified transactions as a CSV named after the client,
	 * with an empty Reason column for the client to fill in.
	 *
	 * @param {String} clientId
	 * @param {Array} transactions
	 * @returns {Promise<void>}
	 */
	async downloadSuspenseTransactions({ clientId, transactions }) {
		try {
			let { client } = await ClientService.fetchClient({ clientId });

			const suspenseTransactions = this.getSuspenseTransactions({ transactions });
			const headers = ["Date", "Description", "Amount", "Bank Account", "Classification", "Reason"];
			const rows = suspenseTransactions.map(buildSuspenseCsvRow);
			const csvText = UtilityService.buildCsv({ headers, rows });
			const blob = new Blob([csvText], { type: "text/csv" });

			UtilityService.downloadFile({ blob, fileName: `${client.name} Suspense Transactions.csv` });
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Classifies a single transaction via the AI classification API.
	 *
	 * @param {String} clientId
	 * @param {String} transactionDescription
	 * @param {Number} amount
	 * @param {Array} classifications
	 * @param {Object} classificationReferences
	 * @param {String} industry
	 * @param {String} province
	 * @param {Object} generalLedgerReferences
	 * @returns {Promise<Object>} Classification result with classification, tax, confidence
	 */
	async categorizeTransaction({ clientId, transactionDescription, amount, classifications, classificationReferences, industry, province, generalLedgerReferences }) {

		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.classifyBookkeepingTransactions({
				clientId,
				transactionDescription,
				amount,
				classifications,
				classificationReferences,
				industry,
				province,
				generalLedgerReferences,
			}, {}, userData.authToken);

			let json = response.data;
			let result = json && json.data ? json.data : json;

			return result;
		} catch (error) {
			return { classification: "Unclassified", tax: "", confidence: null };
		}
	},

	/**
	 * Uploads a general ledger Excel export (CaseWare or Xero) and returns the parsed flat
	 * rows, column map, and opening balances (parsing runs on the API where exceljs lives).
	 *
	 * @param {Number} clientId
	 * @param {File} file - The .xlsx export
	 * @param {String} source - The accounting system: "caseware", "xero" or "quickbooks"
	 * @returns {Promise<Object>} { headers, rows, columnMap, openingBalances, entityName, layout }, or { error } describing why the export could not be read
	 */
	async parseGlExcel({ clientId, file, source, wpfEngagementId = null }) {
		try {
			let formData = new FormData();
			formData.append("files", file);
			formData.append("source", source);
			if (wpfEngagementId) formData.append("wpfEngagementId", String(wpfEngagementId));
			let response = await authedFetch(`/tools/bookkeeping/parse_gl?clientId=${clientId}`, { method: "POST", body: formData });
			return await readJsonAnswer({ response });
		} catch (error) {
			console.log(error);
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Totals the debits and credits of the opening balances read from a general ledger
	 * export, so the import can show whether they form a balanced trial balance. An
	 * unbalanced opening trial balance is reported rather than rejected — a general
	 * ledger that does not balance is exactly the file a preparer needs to open, so the
	 * difference is surfaced for them to resolve instead of blocking the import.
	 *
	 * @param {Array} openingBalances - [{ debit, credit }] as returned by the GL parse
	 * @returns {Object} { debitTotal, creditTotal, difference, isBalanced }
	 */
	summarizeOpeningBalanceTieOut({ openingBalances }) {
		const roundToCents = value => Math.round((parseFloat(value) || 0) * 100) / 100;
		const lines = openingBalances || [];
		const debitTotal = roundToCents(lines.reduce((runningTotal, line) => runningTotal + roundToCents(line.debit), 0));
		const creditTotal = roundToCents(lines.reduce((runningTotal, line) => runningTotal + roundToCents(line.credit), 0));
		const difference = roundToCents(debitTotal - creditTotal);
		const isBalanced = Math.abs(difference) <= OPENING_BALANCE_TOLERANCE;
		return { debitTotal, creditTotal, difference, isBalanced };
	},

	/**
	 * Parses one PDF bank statement on the API without saving anything: transactions,
	 * opening and closing balances, statement period, and the parser's integrity report
	 * (amounts re-derived from the bank's running-balance column, opening + movements
	 * tied to the stated closing).
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {File} params.file - The PDF statement
	 * @returns {Promise<Object>} Parse payload, or { error } when it could not be parsed
	 */
	async parseBankStatementPdf({ clientId, file }) {
		try {
			const formData = new FormData();
			formData.append("files", file);
			if (clientId) formData.append("clientId", String(clientId));
			const response = await authedFetch(`/tools/bookkeeping/parse_bank_statement?clientId=${clientId}`, { method: "POST", body: formData });
			return await readJsonAnswer({ response });
		} catch (error) {
			console.log(error);
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Builds the verification report for a run of parsed bank statements belonging to one
	 * account: orders them by period, checks each statement's own tie-out (opening plus
	 * movements equals its stated closing, from the parser's integrity report), and checks
	 * continuity between consecutive statements — each statement must open exactly where
	 * the previous one closed, which is what proves no month is missing, duplicated or
	 * misread across a multi-PDF set.
	 *
	 * Card statements print rows without a year, so the parser may have corrected one
	 * from the statement's own cycle. Those corrections are carried through per
	 * statement — a rewritten transaction date must be visible to whoever approves the
	 * import, not applied behind them.
	 *
	 * @param {Object} params
	 * @param {Array} params.parses - Parse payloads from parseBankStatementPdf, any order
	 * @returns {Object} { statements, allParsed, allTied, chainUnbroken, breakCount, totalTransactions, firstOpening, lastClosing, accountNumbersSeen }
	 */
	/**
	 * The distinct card numbers a run of statements mentions.
	 *
	 * A statement covering a card and its supplementary card reports both numbers in one string,
	 * and different statements separate them differently — "A / B" on one, "A; B" on another. Held
	 * whole, those read as four different accounts, which is how a normal supplementary card came
	 * to look like statements filed under the wrong client.
	 *
	 * @param {Object} params
	 * @param {Array} params.statements
	 * @returns {Array}
	 */
	listDistinctCardNumbers({ statements }) {
		const everyNumber = [];
		for (const statement of statements || []) {
			const reported = String(statement.accountNumberMasked || "").trim();
			if (!reported) continue;
			const separated = reported.split(/\s*[/;,]\s*/);
			for (const number of separated) {
				const cleaned = number.trim();
				if (cleaned) everyNumber.push(cleaned);
			}
		}
		return [...new Set(everyNumber)];
	},

	buildStatementRunReport({ parses, fiscalPeriod = null }) {
		const roundToCents = value => Math.round((Number(value) || 0) * 100) / 100;
		const ordered = [...(parses || [])].sort((parseA, parseB) => String(parseA.periodStart || "").localeCompare(String(parseB.periodStart || "")));

		let previousClosing = null;
		let previousPeriodEnd = null;
		const statements = ordered.map(parse => {
			const parsedOk = parse.status === "ok" || parse.status === "partial";
			const bankTieCheck = parse.integrity ? parse.integrity.opening_to_closing_check : null;
			const cardTieCheck = parse.integrity ? parse.integrity.balance_arithmetic_check : null;
			const tieCheck = bankTieCheck || cardTieCheck || null;
			const tieOk = !!(tieCheck && tieCheck.ok);
			const tieDelta = tieCheck ? tieCheck.delta : null;

			const yearCorrections = parse.yearCorrections || [];
			const yearsCorrectedCount = yearCorrections.length;

			const hasBothEnds = parsedOk && parse.openingBalance !== null && parse.openingBalance !== undefined && previousClosing !== null;
			const continuityDelta = hasBothEnds ? roundToCents(parse.openingBalance - previousClosing) : null;
			const continuityOk = previousClosing === null ? null : hasBothEnds && Math.abs(continuityDelta) < 0.01;

			const theMonthThatIsMissing = continuityOk === false
				? this.describeTheMonthThatIsMissing({
					afterPeriodEnd: previousPeriodEnd,
					afterClosingBalance: previousClosing,
					beforePeriodStart: parse.periodStart,
					beforeOpeningBalance: parse.openingBalance,
				})
				: null;

			if (parsedOk && parse.closingBalance !== null && parse.closingBalance !== undefined) previousClosing = parse.closingBalance;
			if (parsedOk && parse.periodEnd) previousPeriodEnd = parse.periodEnd;

			let outOfPeriodCount = 0;
			let isWhollyOutsidePeriod = false;
			if (fiscalPeriod && fiscalPeriod.periodStart && fiscalPeriod.periodEnd && parsedOk) {
				const { excludedBefore, excludedAfter } = this.filterTransactionsToPeriod({
					transactions: parse.transactions || [],
					periodStart: fiscalPeriod.periodStart,
					periodEnd: fiscalPeriod.periodEnd,
				});
				outOfPeriodCount = excludedBefore.length + excludedAfter.length;

				const endsBeforePeriod = !!parse.periodEnd && String(parse.periodEnd) < fiscalPeriod.periodStart;
				const startsAfterPeriod = !!parse.periodStart && String(parse.periodStart) > fiscalPeriod.periodEnd;
				isWhollyOutsidePeriod = endsBeforePeriod || startsAfterPeriod;
			}

			return {
				fileName: parse.fileName,
				statementType: parse.statementType || "bank",
				status: parse.status,
				errorDetail: parse.errorDetail || parse.error || null,
				parsedOk,
				fromRegistry: !!parse.fromRegistry,
				enteredByHand: !!parse.enteredByHand,
				periodStart: parse.periodStart,
				periodEnd: parse.periodEnd,
				openingBalance: parse.openingBalance,
				closingBalance: parse.closingBalance,
				accountNumberMasked: parse.accountNumberMasked,
				bankName: parse.bankName,
				transactionCount: (parse.transactions || []).length,
				tieOk,
				tieDelta,
				continuityOk,
				continuityDelta,
				theMonthThatIsMissing,
				outOfPeriodCount,
				isWhollyOutsidePeriod,
				yearsCorrectedCount,
				yearCorrections,
			};
		});

		const parsedStatements = statements.filter(statement => statement.parsedOk);
		const accountNumbersSeen = this.listDistinctCardNumbers({ statements: parsedStatements });
		const breakCount = statements.filter(statement => statement.continuityOk === false).length;

		const periodCoverage = this.buildStatementPeriodCoverage({ parsedStatements, fiscalPeriod });

		return {
			statements,
			allParsed: statements.length > 0 && parsedStatements.length === statements.length,
			allTied: parsedStatements.length > 0 && parsedStatements.every(statement => statement.tieOk),
			chainUnbroken: breakCount === 0,
			breakCount,
			totalTransactions: parsedStatements.reduce((total, statement) => total + statement.transactionCount, 0),
			totalOutOfPeriod: parsedStatements.reduce((total, statement) => total + statement.outOfPeriodCount, 0),
			firstOpening: parsedStatements.length ? parsedStatements[0].openingBalance : null,
			lastClosing: parsedStatements.length ? parsedStatements[parsedStatements.length - 1].closingBalance : null,
			accountNumbersSeen,
			periodCoverage,
		};
	},

	/**
	 * Compares the span of a run of parsed statements against the fiscal period and
	 * reports whether months are missing at either end — statements that start after
	 * the year begins or stop before it ends leave part of the year unbanked.
	 *
	 * @param {Object} params
	 * @param {Array} params.parsedStatements - report rows with periodStart/periodEnd
	 * @param {Object} [params.fiscalPeriod] - { periodStart, periodEnd }
	 * @returns {Object|null} { missingAtStart, missingAtEnd, statementsStart, statementsEnd } or null
	 */
	buildStatementPeriodCoverage({ parsedStatements, fiscalPeriod }) {
		if (!fiscalPeriod || !fiscalPeriod.periodStart || !fiscalPeriod.periodEnd) return null;

		const startDates = parsedStatements.map(statement => statement.periodStart).filter(Boolean).map(String);
		const endDates = parsedStatements.map(statement => statement.periodEnd).filter(Boolean).map(String);
		if (startDates.length === 0 || endDates.length === 0) return null;

		const statementsStart = startDates.sort()[0];
		const statementsEnd = endDates.sort()[endDates.length - 1];

		return {
			statementsStart,
			statementsEnd,
			missingAtStart: statementsStart > fiscalPeriod.periodStart,
			missingAtEnd: statementsEnd < fiscalPeriod.periodEnd,
		};
	},

	async importBankStatementPdf({ engagementId, clientId, file, bankAccountName, currency = null, postsToAccountId = null }) {
		try {
			let formData = new FormData();
			if (engagementId) formData.append("engagementId", String(engagementId));
			if (clientId) formData.append("clientId", String(clientId));
			if (bankAccountName) formData.append("bankAccountName", bankAccountName);
			if (currency) formData.append("currency", currency);
			if (postsToAccountId) formData.append("postsToAccountId", String(postsToAccountId));
			formData.append("files", file);
			let response = await authedFetch(`/tools/bookkeeping/import_bank_statement`, {
				method: "POST",
				body: formData,
			});
			let text = await response.text();
			let json;
			try { json = JSON.parse(text); }
			catch (parseErr) {
				return {
					success: false,
					error: response.status === 404
						? "API endpoint not found (HTTP 404) — restart the API server to register the new /import_bank_statement route"
						: `Server returned non-JSON response (HTTP ${response.status}): ${text.slice(0, 200)}`,
				};
			}
			return json;
		} catch (error) {
			console.error("importBankStatementPdf failed:", error);
			return { success: false, error: String(error) };
		}
	},

	async clearEngagementTransactions({ engagementId, clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.clearEngagementTransactions({}, { engagementId, clientId, wpfEngagementId }, userData.authToken);
			return { success: true, ...response.data };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { success: false, error: reason };
		}
	},

	/**
	 * Records that a preparer has chosen to work on books their statements do not support.
	 *
	 * The reason travels with it because this is a decision about financial records rather than a
	 * dismissed warning, and a year later somebody has to be able to read why the books were built
	 * on statements that did not add up.
	 *
	 * @param {Number} wpfEngagementId
	 * @param {String} reason
	 * @return {Promise<Object>} { recorded, reason }
	 */
	async acceptStatementsThatDoNotReconcile({ wpfEngagementId, reason }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.acceptUnreconciledStatements({ engagementId: wpfEngagementId, reason }, {}, userData.authToken);
			let data = response.data || {};
			return { recorded: !!data.recorded, reason: data.reason || null };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { recorded: false, reason: serverBody.error || "That could not be recorded just now." };
		}
	},

	/**
	 * Loads saved transactions and engagement detail for an engagement.
	 *
	 * `ok` says whether the books were actually read. It matters because a client with no books and a
	 * read that failed used to return the same empty list, and the bookkeeping tool opens its import
	 * wizard when it believes there is nothing on file. So a network blip presented as an empty client,
	 * the preparer was walked through the import again, and a second copy of the whole file was
	 * written — one engagement reached five copies of all 135 of its transactions that way.
	 *
	 * Absent and unknown are two different facts and callers have to be able to tell them apart.
	 *
	 * @param {Number} engagementId
	 * @param {Number} clientId
	 * @return {Promise<Object>} { ok, transactions, engagementDetail }
	 */
	async loadEngagementTransactions({ engagementId, clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementTransactions({}, { engagementId, clientId, wpfEngagementId }, userData.authToken);
			let data = response.data || {};
			return { ...data, ok: true, transactions: data.transactions || [], engagementDetail: data.engagementDetail || null };
		} catch (error) {
			return { ok: false, transactions: [], engagementDetail: null };
		}
	},

	/**
	 * Maps saved engagement transactions from the API into the row shape the
	 * bookkeeping table renders.
	 *
	 * The shape is a whitelist, so anything the server sends and this does not name is lost on
	 * the way in. That is how an approved transfer came back unapproved: the pairing was stored,
	 * returned, and dropped here, so reopening the file offered to approve transfers that were
	 * already recorded. Whatever ties a row to something else — the account, the bank account,
	 * the leg it mirrors — has to be carried, or a screen decides what to show from a row that
	 * no longer knows what it belongs to.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array}
	 */
	mapEngagementTransactionsForTable({ transactions }) {
		const tableRows = transactions.map(transaction => {
			const isFromGlReference = transaction.classification_method === "gl_reference";
			return {
				id: transaction.id != null ? transaction.id : null,
				date: transaction.date,
				description: transaction.description || "",
				amount: transaction.amount,
				amountSource: transaction.amountSource || null,
				amountAsRead: transaction.amountAsRead != null ? transaction.amountAsRead : null,
				postedAmount: transaction.postedAmount != null ? transaction.postedAmount : null,
				taxAmount: transaction.taxAmount != null ? transaction.taxAmount : null,
				taxNotSplitReason: transaction.taxNotSplitReason || null,
				canonical_code: transaction.canonical_code || null,
				canonical_label: transaction.canonical_label || null,
				classification: transaction.classification || "Unclassified",
				classification_label: transaction.classification_label || null,
				classification_method: transaction.classification_method || null,
				classification_status: transaction.classification_status || null,
				clientBankStatementId: transaction.clientBankStatementId || null,
				mirrors_transaction_id: transaction.mirrors_transaction_id || null,
				account_id: transaction.account_id || null,
				bank_account_id: transaction.bank_account_id || null,
				confidence: transaction.confidence,
				gifi_code: transaction.gifi_code || null,
				tax: transaction.tax || "",
				tax_method: transaction.tax_method || null,
				reviewed_by: transaction.reviewed_by || null,
				reviewed_at: transaction.reviewed_at || null,
				review_note: transaction.review_note || null,
				source_evidence: transaction.source_evidence || null,
				suspenseAccepted: !!transaction.suspenseAccepted,
				notATransfer: !!transaction.notATransfer,
				isArchived: !!transaction.isArchived,
				archiveReason: transaction.archiveReason || null,
				archiveNote: transaction.archiveNote || null,
				archivedAt: transaction.archivedAt || null,
				archivedBy: transaction.archivedBy || null,
				accountName: transaction.accountName || "",
				bankAccountNumber: transaction.bankAccountNumber || null,
				currency: transaction.currency || "CAD",
				exchange_rate: transaction.exchange_rate != null ? transaction.exchange_rate : 1,
				selected: false,
				glClassified: isFromGlReference,
				cwClassified: isFromGlReference,
				originalAmount: transaction.originalAmount != null ? transaction.originalAmount : null,
				cwClassification: isFromGlReference ? transaction.classification || null : null,
			};
		});
		return tableRows;
	},

	/**
	 * Everything this client's bookkeeping was built from: the statements on their
	 * record, the general ledger in force for this period, and the CaseWare export
	 * behind it. Each statement reports how many transactions it currently has in the
	 * books, so the tool can say what removing it would cost before anyone agrees.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} [params.wpfEngagementId]
	 * @return {Promise<Object>} { bankStatements, generalLedger, casewareFiles } or { error }
	 */
	async listImportedFiles({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.listImportedFiles({}, { clientId, wpfEngagementId }, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Opens the file an import was built from. A stored file nobody can read is only
	 * half an audit trail — a reviewer asking which general ledger produced a trial
	 * balance has to be able to open it, not just read its name.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.documentId
	 * @param {String} params.fileName
	 * @return {Promise<Object|null>} { error } when it could not be fetched
	 */
	async downloadImportedFile({ clientId, documentId, fileName }) {
		try {
			const response = await authedFetch(`/tools/bookkeeping/imported_file_download?clientId=${clientId}&documentId=${documentId}`, { method: "GET" });
			if (!response || !response.ok) return { error: REQUEST_FAILED_MESSAGE };

			const blob = await response.blob();
			UtilityService.downloadFile({ blob, fileName });
			return null;
		} catch (error) {
			return { error: error.message || REQUEST_FAILED_MESSAGE };
		}
	},

	/**
	 * What one statement was read as saying: its lines, and whether they reach the closing
	 * balance printed on it. The tie comes back with the lines because it is what says whether
	 * the reading can be trusted, and a caller showing one without the other shows half the truth.
	 *
	 * @param {Object} params
	 * @param {Number} params.statementId
	 * @return {Promise<Object>} { statement, lines, tie } or { error }
	 */
	async readStatementReading({ statementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.readStatementReading({}, { statementId }, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Corrects several lines on one statement in a single request. Each line is validated and
	 * saved on its own server-side, so one bad line among them doesn't block the rest — the
	 * response says which of them corrected and which didn't, and why.
	 *
	 * @param {Object} params
	 * @param {Number} params.statementId
	 * @param {Array<Object>} params.corrections - [{ lineId, date, description, amount }]
	 * @return {Promise<Object>} { results: [{ lineId, corrected, reason, booksWouldNeed }], lines, tie } or { error }
	 */
	async correctSeveralStatementLines({ statementId, corrections }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.correctSeveralStatementLines({ statementId, corrections }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Puts a corrected statement reading into the books. Kept apart from the correction itself
	 * because they are different decisions: one is about what a document says, the other changes
	 * the figures the financial statements are built from.
	 *
	 * @param {Object} params
	 * @param {Number} params.statementId
	 * @param {Number} params.lineId
	 * @param {Number} params.transactionId
	 * @return {Promise<Object>} { pushed, reason } or { error }
	 */
	async putStatementCorrectionInTheBooks({ statementId, lineId, transactionId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.putStatementCorrectionInTheBooks({ statementId, lineId, transactionId }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Puts a line the reader missed onto the statement's own record, and then into the books.
	 *
	 * Two things are wrong when a row is dropped, and fixing one leaves the other. The statement's
	 * reading no longer reaches the closing balance it prints, and the books are short a transaction.
	 * Adding it to the books alone — which is all this panel could do before — left the statement
	 * reporting a difference forever, so the file stayed held with nothing a preparer could do about
	 * it.
	 *
	 * The statement's record is written first because it is the evidence the books are judged
	 * against. If the second step fails, the books are short of a statement that now reads correctly,
	 * which is a difference the tool already reports and a preparer can already fix. The reverse
	 * order would leave the books holding a transaction no statement supports, which is the harder
	 * thing to notice.
	 *
	 * @param {Object} params
	 * @param {Number} params.statementId
	 * @param {Number} params.wpfEngagementId
	 * @param {String} params.date
	 * @param {String} params.description
	 * @param {Number} params.amount
	 * @return {Promise<Object>} { added, reason, reachedTheBooks, tie }
	 */
	async recordALineTheReaderMissed({ statementId, wpfEngagementId, date, description, amount }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.recordMissedStatementLine({ statementId, date, description, amount }, {}, userData.authToken);
			let onTheStatement = (response && response.data) || {};
			if (!onTheStatement.added) return { added: false, reason: onTheStatement.reason || REQUEST_FAILED_MESSAGE, reachedTheBooks: false, tie: null };

			let inTheBooks = await this.addLineReadOffTheStatement({
				statementId,
				wpfEngagementId,
				transactionDate: date,
				description,
				amount,
			});

			return {
				added: true,
				reason: inTheBooks && inTheBooks.added ? null : (inTheBooks && inTheBooks.reason) || null,
				reachedTheBooks: !!(inTheBooks && inTheBooks.added),
				tie: onTheStatement.tie || null,
			};
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { added: false, reason: serverBody.error || serverBody.message || COULD_NOT_REACH_THE_SERVER, reachedTheBooks: false, tie: null };
		}
	},

	/**
	 * Reads a statement file that is already on the client's record, so an unread statement
	 * can be retried without anyone finding and uploading it a second time. A reading that
	 * already succeeded is returned from what was stored, so a retry after a lost reply
	 * costs nothing and returns immediately.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.documentId
	 * @return {Promise<Object>} The statement, in the same shape a fresh upload returns, or { error }
	 */
	async rereadStatementFile({ clientId, documentId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.rereadStatementFile({ clientId, documentId }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Describes the statement that would close a break between two consecutive statements.
	 *
	 * A break means one statement does not open where the previous one closed, which is what a
	 * missing month looks like from the outside. Everything about the statement nobody has is
	 * already known: it runs from the day after the earlier one ended to the day before the later
	 * one began, it opens at the earlier closing balance, and it closes at the later opening
	 * balance. Handing those four figures to whoever fills the gap means the difference they have to
	 * account for is the break itself, so the entry is checked against the same arithmetic as any
	 * other statement rather than being taken on trust.
	 *
	 * A break can span more than one billing cycle. The whole gap is described as one period rather
	 * than guessing at cycle boundaries, and the dates stay editable.
	 *
	 * @param {Object} params
	 * @param {String} params.afterPeriodEnd - the earlier statement's period end
	 * @param {Number} params.afterClosingBalance - what the earlier statement closed at
	 * @param {String} params.beforePeriodStart - the later statement's period start
	 * @param {Number} params.beforeOpeningBalance - what the later statement opens at
	 * @return {Object|null} { periodStart, periodEnd, openingBalance, closingBalance, amount } or null
	 */
	describeTheMonthThatIsMissing({ afterPeriodEnd, afterClosingBalance, beforePeriodStart, beforeOpeningBalance }) {
		const endsAreKnown = afterClosingBalance !== null && afterClosingBalance !== undefined && beforeOpeningBalance !== null && beforeOpeningBalance !== undefined;
		if (!endsAreKnown) return null;

		const dayAfterTheEarlierOne = afterPeriodEnd ? moment(afterPeriodEnd, "YYYY-MM-DD").add(1, "day") : null;
		const dayBeforeTheLaterOne = beforePeriodStart ? moment(beforePeriodStart, "YYYY-MM-DD").subtract(1, "day") : null;

		return {
			periodStart: dayAfterTheEarlierOne && dayAfterTheEarlierOne.isValid() ? dayAfterTheEarlierOne.format("YYYY-MM-DD") : "",
			periodEnd: dayBeforeTheLaterOne && dayBeforeTheLaterOne.isValid() ? dayBeforeTheLaterOne.format("YYYY-MM-DD") : "",
			openingBalance: afterClosingBalance,
			closingBalance: beforeOpeningBalance,
			amount: Math.round((beforeOpeningBalance - afterClosingBalance) * 100) / 100,
		};
	},

	/**
	 * Which financial statement an account's figures end up on, written the way a preparer says it.
	 *
	 * The chart names an account but not where it lands, so nothing on screen told anyone whether
	 * a mapping was about the year's profit or about what the company owns and owes. That is the
	 * first thing a preparer needs when choosing between two accounts that read alike, and the
	 * taxonomy has always known it.
	 *
	 * Suspense sits on neither. It is named as what it is rather than pushed onto one of them,
	 * because an account that has to clear before the statements are finalised is not a balance
	 * sheet line a reviewer should get comfortable with.
	 *
	 * @param {Object} params
	 * @param {String} params.statementType - BALANCE_SHEET, INCOME_STATEMENT or SUSPENSE
	 * @return {String} wording for the reader, or "" when the taxonomy does not say
	 */
	whichStatementAnAccountSitsOn({ statementType }) {
		if (statementType === "BALANCE_SHEET") return "Balance sheet";
		if (statementType === "INCOME_STATEMENT") return "Profit & loss";
		if (statementType === "SUSPENSE") return "Must be cleared";
		return "";
	},

	/**
	 * Says how a hand-entered statement's lines got there, which is what the record shows a
	 * reviewer afterwards. Lines read off a file and then changed are no longer that file's
	 * figures, so any editing after a read makes it a typed statement rather than an imported one.
	 *
	 * @param {Object} params
	 * @param {Boolean} params.linesCameFromAFile
	 * @param {Boolean} params.linesWereChangedAfterwards
	 * @return {String} manual_entry | csv_import
	 */
	describeHowAStatementWasEntered({ linesCameFromAFile, linesWereChangedAfterwards }) {
		const stillTheFilesFigures = linesCameFromAFile && !linesWereChangedAfterwards;
		return stillTheFilesFigures ? "csv_import" : "manual_entry";
	},

	/**
	 * Files a statement whose figures a person entered, either typed in or read off a CSV.
	 * The same arithmetic is demanded of them as of a statement read from a PDF: the opening
	 * balance plus the lines must reach the closing balance.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.accountLabel
	 * @param {String} params.statementType - bank | credit_card
	 * @param {String} params.periodStart - YYYY-MM-DD
	 * @param {String} params.periodEnd - YYYY-MM-DD
	 * @param {Number} params.openingBalance
	 * @param {Number} params.closingBalance
	 * @param {Array} params.transactions - [{ date, description, amount }]
	 * @param {String} params.enteredFrom - manual_entry | csv_import
	 * @return {Promise<Object>} { statementId, transactionCount, tie } or { error }
	 */
	async recordStatementByHand({ clientId, accountLabel, statementType = "bank", bankName = null, periodStart, periodEnd, openingBalance, closingBalance, transactions, enteredFrom }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.recordStatementByHand(
				{ clientId, accountLabel, statementType, bankName, periodStart, periodEnd, openingBalance, closingBalance, transactions, enteredFrom },
				{},
				userData.authToken
			);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Takes an imported file off the client's record. Removing a bank statement also
	 * moves everything it put into the books to Archived, so the trial balance stops
	 * including it while a reviewer can still see what went and put it back.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.kind - bank_statement | gl_reference | caseware_reference
	 * @param {Number} [params.statementId]
	 * @param {Number} [params.documentId]
	 * @return {Promise<Object>} { removed, archivedTransactionCount } or { error }
	 */
	async removeImportedFile({ clientId, kind, statementId = null, documentId = null }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.removeImportedFile({ clientId, kind, statementId, documentId }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Writes onto each row which registered statement it came from, so removing that
	 * statement later can take exactly its transactions out of the books rather than
	 * guessing by date and amount — which would also take an identical transaction that
	 * arrived on a different statement.
	 *
	 * The rows carry the file's content hash from the moment they are read; the registry
	 * hands back the statement id it filed each hash under, and this joins the two.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - rows carrying statementFileHash
	 * @param {Array} params.registrationResults - [{ fileHash, statementId }] from the registry
	 * @returns {Array} the same rows, each with clientBankStatementId where it is known
	 */
	stampStatementIdOnTransactions({ transactions, registrationResults }) {
		const statementIdByFileHash = {};
		for (const result of registrationResults || []) {
			if (result.fileHash && result.statementId) statementIdByFileHash[result.fileHash] = result.statementId;
		}

		const stampedTransactions = (transactions || []).map(transaction => {
			const statementId = statementIdByFileHash[transaction.statementFileHash];
			if (!statementId) return transaction;
			return { ...transaction, clientBankStatementId: statementId };
		});
		return stampedTransactions;
	},

	/**
	 * Orders the rows inside a group by whichever column the reviewer clicked. Numbers
	 * are compared as numbers so -2,500 sits below 269 rather than beside "-2"
	 * alphabetically, and everything else as words, case-insensitively. Whether a value
	 * is a number is decided by the value, not by which column it came from: the tax
	 * column holds a code like "HST 13%" on this year's rows and a plain amount on the
	 * prior-year ledger, and both have to sort sensibly through the same function.
	 *
	 * Rows missing the value being sorted on collect at the end whichever way round the
	 * sort runs, rather than pretending to be empty strings and heading the list.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {String} params.column - date | description | tax | amount
	 * @param {Boolean} params.ascending
	 * @returns {Array} a new array; the input is left alone
	 */
	sortTransactionsForReview({ transactions, column, ascending }) {
		const rows = [...(transactions || [])];
		const direction = ascending ? 1 : -1;

		const isMissing = value => value === null || value === undefined || value === "";
		const asNumber = value => (typeof value === "number" ? value : Number(String(value).replace(/,/g, "")));

		const compareRows = (left, right) => {
			const leftValue = left[column];
			const rightValue = right[column];

			if (isMissing(leftValue) && isMissing(rightValue)) return 0;
			if (isMissing(leftValue)) return 1;
			if (isMissing(rightValue)) return -1;

			const leftNumber = asNumber(leftValue);
			const rightNumber = asNumber(rightValue);
			const bothAreNumbers = Number.isFinite(leftNumber) && Number.isFinite(rightNumber);
			if (bothAreNumbers) return (leftNumber - rightNumber) * direction;

			return String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: "base" }) * direction;
		};

		rows.sort(compareRows);
		return rows;
	},

	/**
	 * The issue thread hanging off one transaction — its status and every comment on
	 * it. A note is stored in two shapes depending on when it was written, so asking
	 * this way saves each caller from having to know both.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Object} { status, comments }
	 */
	readReviewIssue({ transaction }) {
		const issue = normalizeReviewIssue(transaction.review_note);
		return issue;
	},

	/**
	 * Turns the tax rates read out of a CaseWare .dbf ledger into the tax codes the
	 * table uses. The spreadsheet routes work this out for themselves while building
	 * the general ledger reference, but the .dbf route has its rates worked out on the
	 * server and keyed by account number, so they have to be matched back to the
	 * account names the classifications carry before anything can use them.
	 *
	 * @param {Object} params
	 * @param {Object} params.ratesByAccountNumber - { "5547": 0.13 }, a fraction per account
	 * @param {Array} params.accounts - [{ accountNumber, accountDescription }]
	 * @returns {Object} { "5547 - Auto Expenses": "Tax 13%" }
	 */
	buildAccountTaxCodesFromGeneralLedgerRates({ ratesByAccountNumber, accounts }) {
		const nameByAccountNumber = {};
		for (const account of accounts || []) {
			if (!account.accountNumber || !account.accountDescription) continue;
			nameByAccountNumber[String(account.accountNumber).trim()] = `${account.accountNumber} - ${account.accountDescription}`;
		}

		const accountTaxes = {};
		for (const [accountNumber, rate] of Object.entries(ratesByAccountNumber || {})) {
			const accountName = nameByAccountNumber[String(accountNumber).trim()];
			if (!accountName) continue;
			accountTaxes[accountName] = formatGeneralLedgerTaxCode(Number(rate) * 100);
		}
		return accountTaxes;
	},

	/**
	 * Names the stretch of time a set of rows covers, for a column heading. Reading the
	 * year off whichever row happens to come first — which is what this replaces — named
	 * a period running August 2025 to June 2026 "2025", and a prior-year ledger ending
	 * February 2025 "2025" as well: two different periods under the same heading, with
	 * nothing on screen to tell them apart.
	 *
	 * A fiscal year is named for the year it ends in, so the year end is used when it is
	 * known and the latest row otherwise. The span is given alongside it because a June
	 * year end is not the 2026 calendar year, and a reviewer comparing two columns has to
	 * see which months each one actually holds.
	 *
	 * @param {Object} params
	 * @param {Array} params.rows
	 * @param {String} [params.dateKey] - the field holding each row's date
	 * @param {String} [params.fiscalYearEnd] - the period's year end, when it is known
	 * @returns {Object} { yearName, span } — both "" when nothing is dated
	 */
	describeDatedPeriod({ rows, dateKey = "date", fiscalYearEnd = null }) {
		const dates = (rows || [])
			.map(row => String(row[dateKey] || ""))
			.filter(date => Number(date.slice(0, 4)) > 1900)
			.sort();

		if (dates.length === 0 && !fiscalYearEnd) return { yearName: "", span: "" };

		const earliest = dates[0];
		const latest = dates[dates.length - 1];
		const yearEndToName = fiscalYearEnd || latest;
		const yearName = yearEndToName ? String(Number(String(yearEndToName).slice(0, 4))) : "";

		if (dates.length === 0) return { yearName, span: "" };

		const asMonth = date => UtilityService.formatDate({ value: date, format: "MMM YYYY", fallback: "" });
		const firstMonth = asMonth(earliest);
		const lastMonth = asMonth(latest);
		const span = firstMonth === lastMonth ? firstMonth : `${firstMonth} – ${lastMonth}`;

		return { yearName, span };
	},

	/**
	 * A stable way to find the same row again in a list that has since been replaced.
	 * Classification runs hand back a whole new array every time one finishes, so a
	 * row held from an earlier render is no longer the same object — comparing objects
	 * would quietly match nothing and lose whatever the person just did. A saved row is
	 * identified by its id; one that has never been saved is identified by what was
	 * imported for it.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {String}
	 */
	getTransactionIdentity({ transaction }) {
		if (transaction.id != null) return `id:${transaction.id}`;
		const identity = `row:${transaction.date || ""}|${transaction.amount}|${transaction.description || ""}`;
		return identity;
	},

	/**
	 * Reduces a bank description to the merchant it names, so two rows for the same shop
	 * are recognised as the same question. A card line carries a reference number and a
	 * terminal that change every time — "Interac purchase - 8426 B001 COSTCO GAS W524"
	 * and "Interac purchase - 2434 B001 COSTCO GAS W524" are the same purchase at the
	 * same station, and asking about each separately gets one classified and the other
	 * left blank when a single call fails.
	 *
	 * Digits go, then the words that say how the money moved rather than who took it.
	 * Returns an empty string when nothing identifying is left — a bare "CHEQUE 2088"
	 * names no merchant — so callers fall back to the raw description and keep those rows
	 * as separate questions rather than collapsing every anonymous row onto one answer.
	 *
	 * This list is the frontend's own and is deliberately not shared with the backend's
	 * bank-line normalizer: the two serve different steps and both are replaced by the
	 * merchant extraction in W-926_MERCHANT_EXTRACTION_PLAN.md §8.1.
	 *
	 * @param {Object} params
	 * @param {String} params.description - the description as the bank wrote it
	 * @returns {String} the merchant words, lowercased and space-joined
	 */
	buildMerchantKey({ description }) {
		const withoutTrailingCount = String(description || "").replace(/\s*\(\d+\)\s*$/, "");
		const withoutDigits = withoutTrailingCount.replace(/\d+/g, " ");
		const words = withoutDigits.toLowerCase().split(/\W+/).filter(Boolean);
		const identifyingWords = words.filter(word => !MONEY_MOVEMENT_WORDS.includes(word) && word.length > 1);
		const merchantKey = identifyingWords.join(" ");
		return merchantKey;
	},

	/**
	 * The question a transaction is asking, for deciding whether two rows can share one
	 * answer. The merchant alone is not enough: the same shop can belong in two accounts
	 * depending on what was bought, and the size of the purchase is what separates them.
	 * In one real ledger APPLE STORE posts to Computer Equipment at $2,510.86 and to
	 * Office & General at $25.99 — one answer applied to both would capitalise a $26
	 * supply or expense a $2,510 asset.
	 *
	 * Rows are therefore grouped by merchant AND order of magnitude, so a fill-up shares
	 * an answer with the next fill-up but never with a capital purchase.
	 *
	 * @param {Object} params
	 * @param {String} params.description
	 * @param {Number} [params.amount]
	 * @returns {String} the key two rows must share to be answered together
	 */
	buildClassificationQuestionKey({ description, amount }) {
		const merchantKey = this.buildMerchantKey({ description }) || String(description || "");
		const size = Math.abs(Number(amount) || 0);
		if (!size) return `${merchantKey} @0`;

		const orderOfMagnitude = Math.floor(Math.log10(size));
		return `${merchantKey} @${orderOfMagnitude}`;
	},

	/**
	 * Says why a row carries the account it does when the imported general ledger
	 * decided it. The general ledger pass runs entirely in the browser, so unlike every
	 * other tier its sentence cannot come from the server — it is written here, once,
	 * rather than at the place that stamps the rows.
	 *
	 * The match is a similarity score between this description and a ledger line, not a
	 * posting the ledger actually made against this row, so the sentence says which line
	 * it resembled and how closely. Claiming the ledger "posts this description" reads as
	 * a fact checked against the source and would send a reviewer past the doubt.
	 *
	 * @param {Object} params
	 * @param {String} params.account - the account the matched ledger line posts to
	 * @param {String} [params.similarity] - how closely the descriptions matched, 0 to 1
	 * @param {String} [params.matchedLedgerDescription] - the ledger line it matched
	 * @returns {Object} evidence shaped like the server's
	 */
	describeGeneralLedgerClassification({ account, similarity = null, matchedLedgerDescription = null }) {
		const closeness = similarity ? ` (${Math.round(Number(similarity) * 100)}% match)` : "";
		const whatItMatched = matchedLedgerDescription
			? `"${matchedLedgerDescription}" in the imported general ledger${closeness}, which posts to ${account}`
			: `a line in the imported general ledger${closeness} that posts to ${account}`;

		return {
			reason: `This description matches ${whatItMatched}.`,
			sources: [],
			searched: false,
		};
	},

	/**
	 * The rows a classification run still has work to do on: everything without an
	 * account, ignoring rows archived out of the books.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array}
	 */
	getTransactionsNeedingClassification({ transactions }) {
		const needingClassification = this.listTransactionsStillNeedingAnAccount({ transactions });
		return needingClassification;
	},

	/**
	 * Adds a fresh import to books that already hold transactions. A row whose date,
	 * amount and description already appear in the books is not added again: importing
	 * a statement that overlaps one already loaded would otherwise double the overlap
	 * and overstate the accounts. Skipped rows are returned rather than dropped
	 * quietly, because a genuine same-day repeat can look identical to a duplicate and
	 * the preparer has to be able to see what was held back.
	 *
	 * Existing rows are never touched, so classifications and approvals already made
	 * survive the import untouched.
	 *
	 * A row that left the books because its statement was removed does not block an
	 * incoming one — that is what lets a corrected statement be imported in place of the
	 * one it replaces. Every other archived row still blocks, because a preparer who
	 * archived something as a duplicate meant it, and a later import must not quietly
	 * undo that.
	 *
	 * @param {Object} params
	 * @param {Array} params.existingTransactions - what is already in the books, archived rows included
	 * @param {Array} params.incomingTransactions - rows from the new import
	 * @returns {Object} { transactions, addedCount, skippedTransactions }
	 */
	mergeImportedTransactions({ existingTransactions, incomingTransactions }) {
		const existingRows = existingTransactions || [];
		const takenKeys = new Set();
		for (const transaction of existingRows) {
			const leftWithItsStatement = transaction.isArchived && transaction.archiveReason === ARCHIVE_REASONS.STATEMENT_REMOVED;
			if (leftWithItsStatement) continue;
			const key = buildDuplicateKey({ transaction });
			if (key) takenKeys.add(key);
		}

		const addedTransactions = [];
		const skippedTransactions = [];
		for (const transaction of incomingTransactions || []) {
			const key = buildDuplicateKey({ transaction });
			const alreadyInTheBooks = key && takenKeys.has(key);
			if (alreadyInTheBooks) {
				skippedTransactions.push(transaction);
				continue;
			}
			addedTransactions.push(transaction);
		}

		return {
			transactions: [...existingRows, ...addedTransactions],
			addedCount: addedTransactions.length,
			skippedTransactions,
		};
	},

	/**
	 * Widens a chosen set of rows to include the other leg of any settled transfer among them.
	 *
	 * A settled transfer is one movement of money written down on both sides. Taking one side out
	 * of the books and leaving the other records that movement once: the surviving leg still posts
	 * to the account at the far end, but the account it names has no matching entry. The row also
	 * goes quiet — it is not waiting for an account, so it is not on the Unclassified tab, and its
	 * pair can no longer be found, so the Transfers tab cannot offer to reverse it either.
	 *
	 * Both legs therefore move together in either direction: archiving one archives its partner,
	 * restoring one restores its partner. The books stay balanced by construction instead of by
	 * the preparer noticing they had to select both.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - every table row
	 * @param {Function} params.isChosen - (transaction, index) => boolean
	 * @returns {Function} (transaction, index) => boolean
	 */
	alsoChooseTheOtherLegOfEachTransfer({ transactions, isChosen }) {
		const rows = transactions || [];
		const idsOfTheOtherLegs = new Set();

		rows.forEach((transaction, index) => {
			if (!isChosen(transaction, index)) return;
			if (transaction.mirrors_transaction_id) idsOfTheOtherLegs.add(String(transaction.mirrors_transaction_id));
		});
		if (idsOfTheOtherLegs.size === 0) return isChosen;

		const alsoTheOtherLeg = (transaction, index) => {
			if (isChosen(transaction, index)) return true;
			if (transaction.id === undefined || transaction.id === null) return false;
			return idsOfTheOtherLegs.has(String(transaction.id));
		};
		return alsoTheOtherLeg;
	},

	/**
	 * Marks the chosen transactions as archived — removed from the books but kept on
	 * the engagement, with who removed them and why, so the decision can be traced and
	 * undone. Rows the caller did not choose come back untouched, except the other leg of a
	 * transfer, which cannot be left behind without recording one side of a movement.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - every table row
	 * @param {Function} params.isChosen - (transaction, index) => boolean
	 * @param {String} params.reason - one of ARCHIVE_REASONS
	 * @param {String} [params.note]
	 * @param {String} [params.archivedBy]
	 * @param {String} [params.archivedAt] - ISO timestamp; the caller supplies it so the mapper stays pure
	 * @returns {Object} { transactions, archivedCount }
	 */
	archiveTransactions({ transactions, isChosen, reason, note = "", archivedBy = "", archivedAt = null }) {
		const isChosenOrItsOtherLeg = this.alsoChooseTheOtherLegOfEachTransfer({ transactions, isChosen });
		let archivedCount = 0;
		let transferCounterpartsIncluded = 0;
		const updatedTransactions = (transactions || []).map((transaction, index) => {
			if (transaction.isArchived) return transaction;
			if (!isChosenOrItsOtherLeg(transaction, index)) return transaction;

			archivedCount += 1;
			if (!isChosen(transaction, index)) transferCounterpartsIncluded += 1;
			return {
				...transaction,
				selected: false,
				isArchived: true,
				archiveReason: reason,
				archiveNote: note || null,
				archivedBy: archivedBy || null,
				archivedAt: archivedAt,
			};
		});
		return { transactions: updatedTransactions, archivedCount, transferCounterpartsIncluded };
	},

	/**
	 * Puts archived transactions back into the books, clearing the archive trail so a
	 * restored row is indistinguishable from one that was never removed. The other leg of a
	 * transfer comes back with it, for the same reason it left with it.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - every table row
	 * @param {Function} params.isChosen - (transaction, index) => boolean
	 * @returns {Object} { transactions, restoredCount }
	 */
	restoreTransactions({ transactions, isChosen }) {
		const isChosenOrItsOtherLeg = this.alsoChooseTheOtherLegOfEachTransfer({ transactions, isChosen });
		let restoredCount = 0;
		let transferCounterpartsIncluded = 0;
		const updatedTransactions = (transactions || []).map((transaction, index) => {
			if (!transaction.isArchived) return transaction;
			if (!isChosenOrItsOtherLeg(transaction, index)) return transaction;

			restoredCount += 1;
			if (!isChosen(transaction, index)) transferCounterpartsIncluded += 1;
			return {
				...transaction,
				selected: false,
				isArchived: false,
				archiveReason: null,
				archiveNote: null,
				archivedBy: null,
				archivedAt: null,
			};
		});
		return { transactions: updatedTransactions, restoredCount, transferCounterpartsIncluded };
	},

	/**
	 * Points out rows that look like the same transaction imported twice — identical
	 * date, amount and description within the live books. Only a pointer: two genuine
	 * transactions can legitimately match (two $5 coffees on one day), so nothing is
	 * archived automatically and the preparer decides. Archived rows are ignored, so a
	 * resolved duplicate stops being flagged.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - table rows
	 * @returns {Object} { duplicateKeys: Set, duplicateCount } - keys are date|description|amount
	 */
	findLikelyDuplicateTransactions({ transactions }) {
		const rowsByKey = {};
		for (const transaction of transactions || []) {
			if (transaction.isArchived) continue;
			const key = buildDuplicateKey({ transaction });
			if (!key) continue;
			rowsByKey[key] = (rowsByKey[key] || 0) + 1;
		}

		const duplicateKeys = new Set();
		let duplicateCount = 0;
		for (const key of Object.keys(rowsByKey)) {
			if (rowsByKey[key] < 2) continue;
			duplicateKeys.add(key);
			duplicateCount += rowsByKey[key];
		}
		return { duplicateKeys, duplicateCount };
	},

	/**
	 * The identity used to spot a repeated transaction. Exposed so the table can ask
	 * whether one row is part of a flagged group without rebuilding the rule.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {String} "" when the row has too little to compare
	 */
	buildDuplicateKeyForTransaction({ transaction }) {
		const key = buildDuplicateKey({ transaction });
		return key;
	},

	/**
	 * Builds the audit-trail block for the review report describing what was archived
	 * out of the books and why, so a reviewer can see that transactions were removed
	 * rather than discovering a silent gap between the statements and the books.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - every table row, archived included
	 * @returns {Object|null} { archivedCount, byReason, archivedTransactions } or null when nothing is archived
	 */
	buildArchiveSummary({ transactions }) {
		const archivedRows = (transactions || []).filter(transaction => transaction.isArchived);
		if (archivedRows.length === 0) return null;

		const byReason = {};
		for (const row of archivedRows) {
			const reasonLabel = ARCHIVE_REASON_LABELS[row.archiveReason] || "Other";
			byReason[reasonLabel] = (byReason[reasonLabel] || 0) + 1;
		}

		const archivedTransactions = archivedRows.map(row => ({
			date: row.date || null,
			description: row.description || "",
			amount: row.amount,
			accountName: row.accountName || "",
			reason: ARCHIVE_REASON_LABELS[row.archiveReason] || "Other",
			note: row.archiveNote || "",
			archivedBy: row.archivedBy || "",
		}));

		return { archivedCount: archivedRows.length, byReason, archivedTransactions };
	},

	/**
	 * Maps table-shaped transactions down to the fields the save endpoint persists,
	 * including the account currency and exchange rate so a foreign-currency conversion
	 * survives a reload.
	 *
	 * Each row names itself. The server decides what to update by that id, so a payload without
	 * one reads as a file of entirely new rows and entirely deleted old ones — every save would
	 * replace the table, and anything referring to a transaction by id would be destroyed with it.
	 *
	 * The row also names the account it was put in. The preparer picked that account from this
	 * client's own chart, so the id is already known here; leaving it out asks the server to work
	 * back to it from the label it was displayed under, which only matches while the display string
	 * and the stored account name agree. When it fails the row is filed as unclassified and the
	 * label, the method and the confidence are cleared with it — so a lookup that missed reads as a
	 * preparer who never chose, and the classification disappears on the next reload.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Table rows
	 * @returns {Array}
	 */
	mapTableTransactionsForSave({ transactions }) {
		const savedTransactions = (transactions || []).map(transaction => ({
			id: transaction.id || null,
			date: transaction.date,
			description: transaction.description,
			amount: transaction.amount,
			amountSource: transaction.amountSource || null,
			amountAsRead: transaction.amountAsRead != null ? transaction.amountAsRead : null,
			accountName: transaction.accountName,
			currency: transaction.currency || "CAD",
			exchange_rate: transaction.exchange_rate != null ? transaction.exchange_rate : 1,
			originalAmount: transaction.originalAmount != null ? transaction.originalAmount : null,
			account_id: transaction.account_id || null,
			canonical_code: transaction.canonical_code || null,
			classification_label: transaction.classification_label || null,
			classification_method: transaction.classification_method || null,
			classification_status: transaction.classification_status || null,
			clientBankStatementId: transaction.clientBankStatementId || null,
			confidence: transaction.confidence != null ? transaction.confidence : null,
			tax: transaction.tax || "",
			tax_method: transaction.tax_method || null,
			reviewed_by: transaction.reviewed_by || null,
			reviewed_at: transaction.reviewed_at || null,
			review_note: transaction.review_note || null,
			source_evidence: transaction.source_evidence || null,
			suspenseAccepted: !!transaction.suspenseAccepted,
			notATransfer: !!transaction.notATransfer,
			isArchived: !!transaction.isArchived,
			archiveReason: transaction.archiveReason || null,
			archiveNote: transaction.archiveNote || null,
			archivedAt: transaction.archivedAt || null,
			archivedBy: transaction.archivedBy || null,
		}));
		return savedTransactions;
	},

	/**
	 * A record of what the server last told us each row contained.
	 *
	 * Kept in the payload shape rather than the table shape so the comparison later asks exactly
	 * the question that matters — would this save write anything different — and not a broader one
	 * about fields the endpoint never persists.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Table rows as the server last returned them
	 * @returns {Object} the saved contents of each row, keyed by id
	 */
	rememberWhatTheServerHas({ transactions }) {
		const remembered = {};
		for (const row of this.mapTableTransactionsForSave({ transactions })) {
			if (row.id) remembered[row.id] = JSON.stringify(row);
		}
		return remembered;
	},

	/**
	 * The rows a save actually needs to send, and the rows it needs to delete.
	 *
	 * Sending the whole file to record one classification costs about sixteen megabytes at a
	 * hundred thousand transactions, and the browser pays it on every keystroke-triggered save.
	 * Comparing each row against what the server last returned turns that into the handful of
	 * rows a preparer touched.
	 *
	 * A row the server never mentioned is new and is always sent. A row that has left the table
	 * is named for deletion, because the endpoint no longer infers removal from absence — that is
	 * what makes sending a subset safe. With nothing remembered, every row counts as changed and
	 * nothing is deleted, so a first save after loading still writes the whole file rather than
	 * quietly skipping it.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Table rows as they stand now
	 * @param {Object} params.serverHas - From rememberWhatTheServerHas
	 * @returns {Object} { transactions, removeTransactionIds }
	 */
	pickWhatChangedSinceTheServerSawIt({ transactions, serverHas }) {
		const remembered = serverHas || {};
		const rows = this.mapTableTransactionsForSave({ transactions });

		const stillInTheTable = new Set();
		const changed = [];
		for (const row of rows) {
			if (!row.id) {
				changed.push(row);
				continue;
			}
			stillInTheTable.add(String(row.id));
			if (remembered[row.id] !== JSON.stringify(row)) changed.push(row);
		}

		const removeTransactionIds = Object.keys(remembered)
			.filter(id => !stillInTheTable.has(id))
			.map(Number);

		return { transactions: changed, removeTransactionIds };
	},

	/**
	 * Gathers the transactions for a bookkeeping generation run from a source that names
	 * its accounts some other way than the statements screen: CSVs mapped to CaseWare bank
	 * accounts, or checked Plaid accounts. Statements attached on the statements screen go
	 * through collectStatementTransactionsByAccount instead, whatever route the wizard took.
	 * Returns the rows shaped for the bookkeeping table plus the number of CSV rows whose
	 * date or amount could not be parsed.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Object} params.statementColumnMapByFile - Confirmed column maps keyed by file key
	 * @param {Array} params.bankAccountsFromCw
	 * @param {Object} params.bankAccountStatementMap - Chosen CSV File per CaseWare account number
	 * @param {Array} params.connectedAccounts
	 * @param {String} params.startDate - "YYYY-MM-DD" start of the Plaid fetch window
	 * @param {String} params.endDate - "YYYY-MM-DD" end of the Plaid fetch window
	 * @returns {Promise<Object>} { transactions, invalidCount }
	 */
	async collectTransactionsForGeneration({ clientId, statementColumnMapByFile = {}, bankAccountsFromCw, bankAccountStatementMap, connectedAccounts, startDate, endDate }) {
		let rawTransactions = [];
		let plaidAccounts = [];
		let invalidCount = 0;

		if (bankAccountsFromCw.length > 0) {
			for (const account of bankAccountsFromCw) {
				const file = bankAccountStatementMap[account.accountNumber];
				if (!file) continue;
				const columnMap = statementColumnMapByFile[UtilityService.getFileKey(file)] || null;
				const extracted = await this.extractTransactionsFromStatementFile({ file, clientId, columnMap, bankAccountNumber: account.accountNumber, bankAccountDescription: account.description });
				rawTransactions.push(...extracted.transactions);
				invalidCount += extracted.invalidCount || 0;
			}
		} else {
			const checkedAccounts = connectedAccounts.filter(account => account.isChecked);
			for (const account of checkedAccounts) {
				const { accounts, transactions } = await PlaidService.fetchAccountsAndTransactions({ startDate, endDate, plaidAccountId: account.id, clientId });
				plaidAccounts.push(accounts[0]);
				rawTransactions.push(...transactions);
			}
		}

		const accountNameById = {};
		for (const account of plaidAccounts) {
			accountNameById[account.account_id] = account.name;
		}

		const transactions = rawTransactions.map(transaction => ({
			date: transaction.date,
			description: transaction.name || transaction.description || "",
			merchant: transaction.merchant_name,
			amount: transaction.amount,
			accountName: accountNameById[transaction.account_id] || transaction.bankAccountName || "",
			bankAccountNumber: transaction.bankAccountNumber || null,
			selected: false,
			classification: null,
			tax: "",
		}));

		return { transactions, invalidCount };
	},

	/**
	 * Gathers transactions for a generation run where each confirmed bank account has a
	 * statement uploaded against it — the one way statements enter the books, whether the
	 * file started from a general ledger or from the statements alone. CSV statements are
	 * parsed in the browser and tagged with the account name (with the file's confirmed sign
	 * convention applied); PDF statements are sent to the vision parser and saved to the
	 * engagement tagged to the chosen account, then read back. The account name on every row
	 * is what drives the split.
	 *
	 * A PDF import also carries the account's currency, so the bank account records it the
	 * moment it is created and a later statement against the same account converts the same
	 * way, and the chart account chosen for a new bank account, so the account is tied the
	 * moment it is created. Spreadsheet rows are saved later, and the save names the chosen
	 * chart account once per bank account rather than on every row.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Array} params.bankAccountUploads - [{ accountName, file, kind }] kind is "spreadsheet" or "pdf"
	 * @param {Object} params.statementSignByFile - Sign-convention confirmations keyed by file key
	 * @param {Object} params.statementColumnMapByFile - Confirmed column maps keyed by file key
	 * @param {Object} [params.currencyByAccount] - ISO code per account name
	 * @param {Object} [params.postsToAccountIdByBankAccount] - chart account id per account name, chosen on the statements screen
	 * @returns {Promise<Object>} { transactions, invalidCount, pdfImportResults }
	 */
	async collectStatementTransactionsByAccount({ clientId, wpfEngagementId, bankAccountUploads, statementSignByFile, statementColumnMapByFile = {}, currencyByAccount = {}, postsToAccountIdByBankAccount = {} }) {
		let statementTransactions = [];
		let invalidCount = 0;
		let pdfImportResults = [];
		let importedAnyPdf = false;

		for (const upload of bankAccountUploads) {
			if (!upload.file) continue;

			if (upload.kind === "pdf") {
				importedAnyPdf = true;
				const currency = currencyByAccount[upload.accountName] || null;
				const postsToAccountId = postsToAccountIdByBankAccount[upload.accountName] || null;
				const result = await this.importBankStatementPdf({ clientId, engagementId: wpfEngagementId, file: upload.file, bankAccountName: upload.accountName, currency, postsToAccountId });
				pdfImportResults.push({ name: upload.file.name, accountName: upload.accountName, ...result });
				continue;
			}

			const columnMap = statementColumnMapByFile[UtilityService.getFileKey(upload.file)] || null;
			const extracted = await this.extractTransactionsFromStatementFile({ file: upload.file, clientId, columnMap, bankAccountNumber: null, bankAccountDescription: upload.accountName });
			const sign = statementSignByFile[UtilityService.getFileKey(upload.file)];
			const isInverted = !!(sign && sign.inverted);
			const signedTransactions = this.applySignConvention({ transactions: extracted.transactions, inverted: isInverted });
			statementTransactions.push(...signedTransactions);
			invalidCount += extracted.invalidCount || 0;
		}

		const statementTableTransactions = statementTransactions.map(transaction => ({
			date: transaction.date,
			description: transaction.description || "",
			merchant: transaction.merchant,
			amount: transaction.amount,
			accountName: transaction.bankAccountName || "",
			bankAccountNumber: transaction.bankAccountNumber || null,
			selected: false,
			classification: null,
			tax: "",
		}));

		let pdfTableTransactions = [];
		if (importedAnyPdf) {
			const dbResult = await this.loadEngagementTransactions({ clientId, wpfEngagementId });
			const savedTransactions = dbResult && dbResult.transactions ? dbResult.transactions : [];
			pdfTableTransactions = this.mapEngagementTransactionsForTable({ transactions: savedTransactions });
		}

		const transactions = [...pdfTableTransactions, ...statementTableTransactions];
		return { transactions, invalidCount, pdfImportResults };
	},

	/**
	 * Converts foreign-currency transactions to CAD via the Bank of Canada rate for each
	 * transaction's date, on the API. Returns an index-aligned list of conversions.
	 *
	 * @param {Array} transactions - [{ date, amount }]
	 * @param {String} currency - ISO code (e.g. "USD")
	 * @returns {Promise<Array>} [{ cadAmount, rate, rateDate }] index-aligned to input, or []
	 */
	async convertTransactionsToCad({ transactions, currency }) {
		try {
			let userData = UserService.getUserData();
			let payload = transactions.map(transaction => ({ date: transaction.date, amount: transaction.amount }));
			let response = await Kichiri.tools.convertCurrency({ transactions: payload, currency }, {}, userData.authToken);
			let converted = response.data && Array.isArray(response.data.converted) ? response.data.converted : [];
			return converted;
		} catch (error) {
			console.log(error);
			return [];
		}
	},

	/**
	 * Guesses an account's currency from its name so a foreign account defaults to the
	 * right ISO code at import instead of silently staying CAD (which would make a
	 * cross-currency transfer read as a mismatched CAD pair and go undetected). Returns
	 * "CAD" when no currency hint is found; the guess is always user-overridable.
	 *
	 * @param {Object} params
	 * @param {String} params.accountName
	 * @returns {String} ISO currency code
	 */
	guessAccountCurrency({ accountName }) {
		const normalizedName = (accountName || "").toLowerCase();
		for (const entry of CURRENCY_NAME_HINTS) {
			const hasHint = entry.hints.some(hint => normalizedName.includes(hint));
			if (hasHint) return entry.code;
		}
		return "CAD";
	},

	/**
	 * Re-derives one bank account's currency after import: converts its transactions to
	 * CAD at the Bank of Canada rate when a foreign currency is chosen, or restores the
	 * native amounts when switched back to CAD. Lets a user fix a currency that was set
	 * wrong (or auto-defaulted wrong) without re-importing. Returns a new array.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {String} params.accountName
	 * @param {String} params.currency - ISO code
	 * @returns {Promise<Array>}
	 */
	/**
	 * Reports what one bank account's transactions currently look like, so the sign
	 * convention can be judged before it is changed: how many rows there are, and how
	 * many read as money in versus money out.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Every transaction in the engagement
	 * @param {String} params.accountName - The bank account to describe
	 * @returns {Object} { transactionCount, moneyInCount, moneyOutCount, netAmount }
	 */
	summarizeBankAccountSigns({ transactions, accountName }) {
		const accountTransactions = (transactions || []).filter(transaction => (transaction.accountName || transaction.bankAccountName) === accountName);
		const amounts = accountTransactions.map(transaction => Number(transaction.amount) || 0);
		const moneyInCount = amounts.filter(amount => amount > 0).length;
		const moneyOutCount = amounts.filter(amount => amount < 0).length;
		const netAmount = amounts.reduce((runningTotal, amount) => runningTotal + amount, 0);
		const roundedNetAmount = Math.round(netAmount * 100) / 100;
		return { transactionCount: accountTransactions.length, moneyInCount, moneyOutCount, netAmount: roundedNetAmount };
	},

	/**
	 * Flips the sign of every transaction on one bank account, for a statement that was
	 * imported with the wrong sign convention. The pre-conversion native amount is
	 * flipped alongside the CAD amount, so a later currency change re-converts from the
	 * corrected figure instead of resurrecting the original sign. Flipping a second time
	 * restores the original amounts exactly, which is what makes this safe to undo.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Every transaction in the engagement
	 * @param {String} params.accountName - The bank account whose signs are flipped
	 * @returns {Array}
	 */
	flipSignsForBankAccount({ transactions, accountName }) {
		const flippedTransactions = (transactions || []).map(transaction => {
			const belongsToAccount = (transaction.accountName || transaction.bankAccountName) === accountName;
			if (!belongsToAccount) return transaction;

			const flippedTransaction = { ...transaction, amount: -(Number(transaction.amount) || 0) };
			const hasNativeAmount = transaction.originalAmount !== null && transaction.originalAmount !== undefined;
			if (hasNativeAmount) flippedTransaction.originalAmount = -(Number(transaction.originalAmount) || 0);
			return flippedTransaction;
		});
		return flippedTransactions;
	},

	/**
	 * Lays a set of per-row changes onto the rows as they are now, matched by each row's stable
	 * identity, and says which rows were touched so a caller can save only those.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - rows as they stand now
	 * @param {Object} params.changeByIdentity - { [identity]: partial row }, as setAccountCurrency returns
	 * @returns {Object} { transactions, changedRows }
	 */
	applyRowChanges({ transactions, changeByIdentity }) {
		const changes = changeByIdentity || {};
		const changedRows = [];
		const updatedTransactions = (transactions || []).map(transaction => {
			const change = changes[this.getTransactionIdentity({ transaction })];
			if (!change) return transaction;
			const updatedRow = { ...transaction, ...change };
			changedRows.push(updatedRow);
			return updatedRow;
		});
		return { transactions: updatedTransactions, changedRows };
	},

	/**
	 * The change a currency choice makes to an account's rows, as a patch per row keyed by the
	 * row's stable identity rather than a rebuilt copy of the whole list.
	 *
	 * Converting a foreign account waits on an exchange-rate lookup, and the caller applies the
	 * result seconds later. Returning a whole new array built from the rows as they were when the
	 * lookup started would write that stale snapshot back over anything the preparer changed
	 * meanwhile. A patch keyed by identity lets the caller lay only the amount and currency onto the
	 * rows as they are now, touching nothing else.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {String} params.accountName
	 * @param {String} params.currency
	 * @returns {Promise<Object>} { [identity]: { amount, currency, exchange_rate, originalAmount, exchangeRateDate } }
	 */
	async setAccountCurrency({ transactions, accountName, currency }) {
		const accountTransactions = (transactions || []).filter(transaction => transaction.accountName === accountName);
		if (accountTransactions.length === 0) return {};

		const nativeAmountFor = transaction => (transaction.originalAmount != null ? Number(transaction.originalAmount) : Number(transaction.amount));
		const isForeign = !!currency && currency !== "CAD";
		const changeByIdentity = {};

		if (!isForeign) {
			for (const transaction of accountTransactions) {
				const nativeAmount = nativeAmountFor(transaction);
				changeByIdentity[this.getTransactionIdentity({ transaction })] = { amount: nativeAmount, currency: "CAD", exchange_rate: 1, originalAmount: null, exchangeRateDate: null };
			}
			return changeByIdentity;
		}

		const nativeForConversion = accountTransactions.map(transaction => ({ date: transaction.date, amount: nativeAmountFor(transaction) }));
		const conversions = await this.convertTransactionsToCad({ transactions: nativeForConversion, currency });

		accountTransactions.forEach((transaction, index) => {
			const conversion = conversions[index];
			if (!conversion) return;
			changeByIdentity[this.getTransactionIdentity({ transaction })] = {
				amount: conversion.cadAmount,
				originalAmount: nativeAmountFor(transaction),
				currency,
				exchange_rate: conversion.rate,
				exchangeRateDate: conversion.rateDate,
			};
		});
		return changeByIdentity;
	},

	/**
	 * Converts every transaction whose account is marked in a non-CAD currency into CAD,
	 * grouping by currency so each currency is fetched once. Converted rows carry the CAD
	 * amount, the original foreign amount, the currency code, and the exchange rate used;
	 * CAD accounts (and untouched rows) are returned unchanged.
	 *
	 * @param {Array} transactions - Table-shaped rows carrying accountName, date, amount
	 * @param {Object} currencyByAccount - { [accountName]: isoCode }
	 * @returns {Promise<Array>}
	 */
	async applyCurrencyConversions({ transactions, currencyByAccount }) {
		const currencyForAccount = currencyByAccount || {};
		const isForeignCurrency = code => !!code && code !== "CAD";

		const indicesByCurrency = {};
		transactions.forEach((transaction, index) => {
			const code = currencyForAccount[transaction.accountName];
			if (!isForeignCurrency(code)) return;
			if (!indicesByCurrency[code]) indicesByCurrency[code] = [];
			indicesByCurrency[code].push(index);
		});

		const currencyCodes = Object.keys(indicesByCurrency);
		if (currencyCodes.length === 0) return transactions;

		const convertedTransactions = [...transactions];
		for (const code of currencyCodes) {
			const indices = indicesByCurrency[code];
			const foreignTransactions = indices.map(index => ({ date: transactions[index].date, amount: transactions[index].amount }));
			const conversions = await this.convertTransactionsToCad({ transactions: foreignTransactions, currency: code });
			indices.forEach((originalIndex, position) => {
				const conversion = conversions[position];
				if (!conversion) return;
				convertedTransactions[originalIndex] = {
					...transactions[originalIndex],
					amount: conversion.cadAmount,
					originalAmount: transactions[originalIndex].amount,
					currency: code,
					exchange_rate: conversion.rate,
					exchangeRateDate: conversion.rateDate,
				};
			});
		}
		return convertedTransactions;
	},

	/**
	 * Checks generated transactions against the expected bookkeeping period (the day
	 * after the prior year end through the chosen number of months) and reports how
	 * many fall outside it, with readable period boundary labels for the warning toast.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {String} params.priorYearEnd - "YYYY-MM-DD"
	 * @param {Number} params.periodMonths
	 * @returns {Object|null} { outOfRangeCount, periodStartLabel, periodEndLabel }, or null when every transaction is in range or no check applies
	 */
	findTransactionsOutsidePeriod({ transactions, priorYearEnd, periodMonths }) {
		if (!priorYearEnd || transactions.length === 0) return null;

		const priorYearEndDate = UtilityService.parseLocalDate(priorYearEnd);
		const periodStart = new Date(priorYearEndDate);
		periodStart.setDate(periodStart.getDate() + 1);
		const periodEnd = new Date(priorYearEndDate);
		periodEnd.setMonth(periodEnd.getMonth() + periodMonths);

		const isOutsidePeriod = transaction => {
			const transactionDate = new Date(transaction.date);
			const isBeforePeriodStart = transactionDate < periodStart;
			const isAfterPeriodEnd = transactionDate > periodEnd;
			const isOutside = isBeforePeriodStart || isAfterPeriodEnd;
			return isOutside;
		};
		const outOfRangeCount = transactions.filter(isOutsidePeriod).length;
		if (outOfRangeCount === 0) return null;

		return {
			outOfRangeCount,
			periodStartLabel: UtilityService.formatShortDate(periodStart),
			periodEndLabel: UtilityService.formatShortDate(periodEnd),
		};
	},

	/**
	 * Pulls a "YYYY-MM-DD" date out of a CaseWare file name, e.g.
	 * "Client 2025-08-31am.dbf" gives "2025-08-31"; returns null when the name has no date.
	 *
	 * @param {Object} params
	 * @param {String} params.fileName
	 * @returns {String|null}
	 */
	extractIsoDateFromFileName({ fileName }) {
		const lastPossibleStart = fileName.length - ISO_DATE_LENGTH;
		for (let start = 0; start <= lastPossibleStart; start++) {
			const candidate = fileName.slice(start, start + ISO_DATE_LENGTH);
			if (isIsoDateText(candidate)) return candidate;
		}
		return null;
	},

	/**
	 * Strips the trailing year or "YYYY-MM-DD" date (and any parenthesized suffix) off
	 * a CaseWare client folder name, leaving just the entity name, e.g.
	 * "Acme Ltd - 2025-08-31 (copy)" gives "Acme Ltd".
	 *
	 * @param {Object} params
	 * @param {String} params.folderName
	 * @returns {String}
	 */
	stripTrailingYearFromFolderName({ folderName }) {
		const withoutTrailingYear = folderName.replace(/\s*-?\s*\d{4}(-\d{2}-\d{2})?\s*(\(.*\))?\s*$/, "");
		const entityName = withoutTrailingYear.trim();
		return entityName;
	},

	/**
	 * Saves transactions and engagement settings for an engagement
	 * @param {Number} engagementId
	 * @param {Number} clientId
	 * @param {Array} transactions
	 * @param {String} province
	 * @param {String} fiscalYearEnd
	 * @param {String} accountantName
	 * @param {String} firmName
	 * @param {Object} csrsChecklist
	 * @param {Array} generalLedgerEntries
	 * @return {Promise<Object>}
	 */
	async saveEngagementTransactions({ engagementId, clientId, wpfEngagementId, transactions, removeTransactionIds, province, provinceChosenByAPerson, fiscalYearEnd, accountantName, firmName, csrsChecklist, generalLedgerEntries, postsToAccountIdByBankAccount }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.saveEngagementTransactions({ engagementId, clientId, wpfEngagementId, transactions, removeTransactionIds, province, provinceChosenByAPerson, fiscalYearEnd, accountantName, firmName, csrsChecklist, generalLedgerEntries, postsToAccountIdByBankAccount }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || "the server could not be reached" };
		}
	},

	/**
	 * Loads the adjusting journal entries for a client's engagement
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async loadAdjustingEntries({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementAdjustingEntries({}, { clientId, wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Saves the adjusting journal entries for a client's engagement, or says why the server refused
	 * them — a line with no account it can place, or an entry that does not balance — so a refused
	 * save is never shown as saved.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Array} params.entries
	 * @return {Promise<Object>} { saved } or { error }
	 */
	async saveAdjustingEntries({ clientId, wpfEngagementId, entries }) {
		return await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/engagement_adjusting_entries", body: { clientId, wpfEngagementId, entries } });
	},

	async createChartOfAccount({ clientId, accountNumber, accountName, accountType, normalBalance, canonicalCode, gifiCode }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createChartOfAccount({ clientId, accountNumber, accountName, accountType, normalBalance, canonicalCode, gifiCode }, {}, userData.authToken);
			return { account: response.data.account };
		} catch (error) {
			if (error.response && error.response.status === 409) return { conflict: true, existing: error.response.data.existing };
			if (error.response) return { error: (error.response.data && error.response.data.message) || "Failed to create account" };
			return null;
		}
	},

	/**
	 * The step both ways of adding a chart account share: refuse an account with no name or no
	 * statement line, ask the server to create it, and word a failure. A number the chart already
	 * holds comes back as the server's conflict answer, for the caller to read its own way.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.accountName
	 * @param {String} [params.accountNumber]
	 * @param {String} params.canonicalCode
	 * @param {String} [params.gifiCode]
	 * @returns {Promise<Object>} { created, error } — created is the server's answer (an account, or a conflict with the existing one)
	 */
	async askTheServerToAddAnAccount({ clientId, accountName, accountNumber = null, canonicalCode, gifiCode = null }) {
		const cleanName = UtilityService.toTrimmedText(accountName);
		if (!cleanName || !canonicalCode) return { created: null, error: "An account needs a name and the statement line it reports on." };

		const created = await this.createChartOfAccount({ clientId, accountNumber: accountNumber || null, accountName: cleanName, canonicalCode, gifiCode });
		const couldNotBeAdded = !created || created.error;
		if (couldNotBeAdded) return { created: null, error: (created && created.error) || "The account could not be added to the chart." };
		return { created, error: null };
	},

	/**
	 * Adds an account to the client's chart, or says why it could not.
	 *
	 * Several screens add accounts, and each used to read the three shapes createChartOfAccount
	 * comes back in for itself. One reading here means the same failure is worded the same way
	 * wherever it happens.
	 *
	 * This is the strict reading: a number the chart already holds is refused, because the caller
	 * asked for a new account and got somebody else's. A caller who typed the number deliberately
	 * and means the account that has it wants addAccountToTheChartOrUseTheExistingOne instead.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.accountName
	 * @param {String} [params.accountNumber] - the number the client's own ledger gives it; assigned by the server when absent
	 * @param {String} params.canonicalCode - the statement line the account reports on
	 * @param {String} [params.gifiCode]
	 * @returns {Promise<Object>} { account, error } — one of the two is set
	 */
	async addAccountToTheChart({ clientId, accountName, accountNumber = null, canonicalCode, gifiCode = null }) {
		const { created, error } = await this.askTheServerToAddAnAccount({ clientId, accountName, accountNumber, canonicalCode, gifiCode });
		if (error) return { account: null, error };
		if (created.conflict) {
			const putAway = created.existing && created.existing.isRetired ? ", which was put away" : "";
			return { account: null, error: `Account number ${created.existing.accountNumber} is already on this chart as ${created.existing.accountName}${putAway}.` };
		}
		return { account: created.account, error: null };
	},

	/**
	 * Adds an account to the chart, or answers with the account that already holds that number.
	 *
	 * The tolerant reading, for a caller who typed the number: on a journal entry line or in the
	 * bulk pass that follows classification, a number the chart already holds means the account
	 * they were reaching for, and posting to it is the right outcome rather than an error. Which
	 * of the two happened is said out loud, so a caller that cares can tell.
	 *
	 * An account that was put away is the exception. It still holds its number, so it still comes
	 * back as the conflict, but nothing is meant to post to a retired account — so this refuses and
	 * says to bring it back first, rather than quietly booking the amount onto it.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.accountName
	 * @param {String} [params.accountNumber]
	 * @param {String} params.canonicalCode
	 * @param {String} [params.gifiCode]
	 * @returns {Promise<Object>} { account, usedExisting, error }
	 */
	async addAccountToTheChartOrUseTheExistingOne({ clientId, accountName, accountNumber = null, canonicalCode, gifiCode = null }) {
		const { created, error } = await this.askTheServerToAddAnAccount({ clientId, accountName, accountNumber, canonicalCode, gifiCode });
		if (error) return { account: null, usedExisting: false, error };
		if (created.conflict && created.existing && created.existing.isRetired) {
			return {
				account: null,
				usedExisting: false,
				error: `Account number ${created.existing.accountNumber} belongs to ${created.existing.accountName}, which was put away. Bring it back before posting to it.`,
			};
		}
		if (created.conflict) return { account: created.existing, usedExisting: true, error: null };
		return { account: created.account, usedExisting: false, error: null };
	},

	/**
	 * Loads the financial statement notes for a client's engagement
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async loadFsNotes({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementFsNotes({}, { clientId, wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Saves the financial statement notes for a client's engagement
	 * @param {Number} clientId
	 * @param {Array} notes
	 * @return {Promise<Object>}
	 */
	async saveFsNotes({ clientId, wpfEngagementId, notes }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.saveEngagementFsNotes({ clientId, wpfEngagementId, notes }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Builds a lookup of custom-note title keyed by note id, used to label custom notes
	 * in the preview and the downloaded PDF.
	 * @param {Array} customNotes
	 * @return {Object}
	 */
	buildCustomNoteTitleMap({ customNotes }) {
		let titleByNoteId = {};
		(customNotes || []).forEach(function (note) { titleByNoteId[note.id] = note.title || ""; });
		return titleByNoteId;
	},

	/**
	 * Builds the persisted financial-statement notes list from the edited note bodies and
	 * the user-added custom notes. Custom notes carry their own title and source "custom";
	 * standard notes use their template key as the title and source "manual". A custom note
	 * is included even when its body is still empty so the note itself survives a reload.
	 * @param {Object} noteTexts
	 * @param {Array} customNotes
	 * @return {Array}
	 */
	buildFsNotesToPersist({ noteTexts, customNotes }) {
		let customNoteById = {};
		(customNotes || []).forEach(function (note) { customNoteById[note.id] = note; });

		let noteKeysFromBodies = Object.keys(noteTexts || {});
		let noteKeysFromCustom = (customNotes || []).map(function (note) { return note.id; });
		let allNoteKeys = [...new Set([...noteKeysFromBodies, ...noteKeysFromCustom])];

		let notes = allNoteKeys.map(function (noteKey, index) {
			let customNote = customNoteById[noteKey];
			let isCustomNote = !!customNote;
			let body = (noteTexts && noteTexts[noteKey]) || "";
			return {
				noteKey: noteKey,
				title: isCustomNote ? (customNote.title || "") : noteKey,
				body: body,
				sortOrder: index,
				isIncluded: true,
				source: isCustomNote ? "custom" : "manual",
			};
		});
		return notes;
	},

	/**
	 * Shapes and saves the full financial-statement notes list (standard edits + custom notes).
	 * @param {Number} clientId
	 * @param {Number} wpfEngagementId
	 * @param {Object} noteTexts
	 * @param {Array} customNotes
	 * @return {Promise<Object>}
	 */
	async saveAllFsNotes({ clientId, wpfEngagementId, noteTexts, customNotes }) {
		let notes = this.buildFsNotesToPersist({ noteTexts, customNotes });
		return this.saveFsNotes({ clientId, wpfEngagementId, notes });
	},

	/**
	 * Records a sign-off on a client's engagement.
	 *
	 * A refused sign-off comes back with the server's reason instead of nothing, because every
	 * refusal — the books do not balance, a client request is still blocking, QuickBooks has changed
	 * since the books were pulled — is something the preparer has to act on. When the refusal is
	 * about QuickBooks, what moved is returned too, so the screen can show it and ask for a reason.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId - the engagement being signed
	 * @param {String} params.signoffLevel
	 * @param {String} params.designation
	 * @param {String} params.attestation
	 * @param {Object} params.retainedEarnings
	 * @param {Array} params.notesToFs
	 * @param {String} params.noticeToReader
	 * @param {String} [params.sourceDriftReason] - why the preparer is signing although QuickBooks has moved
	 * @return {Promise<Object>} the sign-off, or { error, sourceDrift }
	 */
	async signOffEngagement({ clientId, wpfEngagementId, signoffLevel, designation, attestation, retainedEarnings, notesToFs, noticeToReader, sourceDriftReason }) {
		const answer = await callRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/engagement_signoff", body: { clientId, wpfEngagementId, signoffLevel, designation, attestation, retainedEarnings, notesToFs, noticeToReader, sourceDriftReason } });
		if (answer.error) return { error: answer.error, sourceDrift: answer.source_drift || null };
		return answer;
	},

	/**
	 * Lists a client's compilation files across years (the roll-forward chain) —
	 * powers the "view different years' working papers" navigator.
	 * @param {Number} clientId
	 * @return {Promise<Object>} { years: [...] }
	 */
	async getEngagementYears({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementYears({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Loads a specific compilation file's frozen deliverable (locked statements),
	 * for viewing a prior year in the navigator.
	 * @param {Number} wpfEngagementId
	 * @return {Promise<Object>}
	 */
	async getEngagementDeliverable({ wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementDeliverable({}, { wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Records an imported trial balance against an engagement, replacing anything imported before.
	 *
	 * The server refuses an import that does not balance or that carries unmapped accounts, so a
	 * rejection here is a real answer about the file rather than a network problem.
	 *
	 * @param {Number} wpfEngagementId
	 * @param {Object} preview - the result of previewTrialBalanceImport
	 * @param {String} sourceFileName
	 * @return {Promise<Object>} { imported, replaced }
	 */
	async commitTrialBalanceImport({ wpfEngagementId, preview, sourceFileName }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.commitTrialBalanceImport(
			{
				wpfEngagementId,
				entries: preview.entries,
				unmapped: preview.unmapped,
				balances: preview.balances,
				difference: preview.difference,
				sourceFileName,
			},
			{},
			userData.authToken,
		);
		return response.data;
	},

	/**
	 * Removes the trial balance imported for an engagement, returning it to whatever it had before.
	 *
	 * @param {Number} wpfEngagementId
	 * @return {Promise<Object>} { removed }
	 */
	async clearTrialBalanceImport({ wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.clearTrialBalanceImport({ wpfEngagementId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || "the server could not be reached" };
		}
	},

	/**
	 * Calls a tools route whose refusals are sentences meant for the preparer; see
	 * apiFetch.callRouteThatExplainsItsRefusals, which this hands to.
	 *
	 * @param {Object} params
	 * @param {String} params.path - the route, e.g. "/tools/bookkeeping/qbo_trial_balance_commit"
	 * @param {Object} params.body
	 * @return {Promise<Object>} the route's answer, or { error, ...whatever else the refusal said }
	 */
	async callToolsRouteThatExplainsItsRefusals({ path, body }) {
		const answer = await callRouteThatExplainsItsRefusals({ path, body });
		return answer;
	},

	/**
	 * Every account whose balance has moved in QuickBooks since the engagement's books were pulled.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @return {Promise<Object>} { changes: { checkedAt, reachable, unreachableBecause, hasMoved, moved } } or { error }
	 */
	async findQuickBooksChangesSincePull({ clientId, wpfEngagementId }) {
		return await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_changes_since_pull", body: { clientId, wpfEngagementId } });
	},

	/**
	 * One adjusting entry laid out as it would be booked in QuickBooks, with the reasons it cannot be.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.adjustingEntryHeaderId
	 * @return {Promise<Object>} { journal } or { error }
	 */
	async describeAdjustingEntryToBook({ clientId, wpfEngagementId, adjustingEntryHeaderId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_journal_preview", body: { clientId, wpfEngagementId, adjustingEntryHeaderId } });
		return answer;
	},

	/**
	 * Books one adjusting entry in the client's QuickBooks company.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.adjustingEntryHeaderId
	 * @param {Object} params.chartAccountIdByLine - line number → chart account, for lines that named only a statement line
	 * @param {Array<String>} params.acknowledgedWarnings - the warning codes the preparer ticked
	 * @return {Promise<Object>} { booked } or { error }
	 */
	async bookAdjustingEntryInQuickBooks({ clientId, wpfEngagementId, adjustingEntryHeaderId, chartAccountIdByLine, acknowledgedWarnings }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_journal_book", body: { clientId, wpfEngagementId, adjustingEntryHeaderId, chartAccountIdByLine, acknowledgedWarnings } });
		return answer;
	},

	/**
	 * The journals that would move QuickBooks's Opening Balance Equity into an equity account.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @return {Promise<Object>} { reclassification } or { error }
	 */
	async describeOpeningBalanceEquityReclassification({ clientId, wpfEngagementId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_opening_balance_equity_preview", body: { clientId, wpfEngagementId } });
		return answer;
	},

	/**
	 * Books one Opening Balance Equity reclassification journal in QuickBooks.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {String} params.period - "prior" or "current"
	 * @param {String} params.targetQboAccountId
	 * @param {Array<String>} params.acknowledgedWarnings - the warning codes the preparer ticked
	 * @return {Promise<Object>} { booked } or { error }
	 */
	async bookOpeningBalanceEquityReclassification({ clientId, wpfEngagementId, period, targetQboAccountId, acknowledgedWarnings }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_opening_balance_equity_book", body: { clientId, wpfEngagementId, period, targetQboAccountId, acknowledgedWarnings } });
		return answer;
	},

	/**
	 * Deletes a journal this engagement booked from QuickBooks. The server refuses when QuickBooks
	 * holds a changed version of it, answering with { error, journalChangedInQuickBooks }; sending
	 * removeEvenIfChanged after the preparer has seen the change deletes it anyway. For a record that
	 * the client entered the journal, nothing is deleted; the server refuses while QuickBooks still
	 * holds the journal as recorded, answering { error, journalStillMatchesTheEntry }, and
	 * forgetEvenIfMatching forgets it anyway.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.journalId
	 * @param {Boolean} [params.removeEvenIfChanged]
	 * @param {Boolean} [params.forgetEvenIfMatching]
	 * @return {Promise<Object>} { removal } or { error, journalChangedInQuickBooks, journalStillMatchesTheEntry }
	 */
	async removeJournalFromQuickBooks({ clientId, wpfEngagementId, journalId, removeEvenIfChanged, forgetEvenIfMatching }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_journal_remove", body: { clientId, wpfEngagementId, journalId, removeEvenIfChanged: removeEvenIfChanged === true, forgetEvenIfMatching: forgetEvenIfMatching === true } });
		return answer;
	},

	/**
	 * What removing a booked journal did to its adjusting entry, in a sentence for the preparer.
	 *
	 * @param {Object} params
	 * @param {Object} params.removal - from removeJournalFromQuickBooks
	 * @param {String} params.qboJournalEntryId
	 * @return {String}
	 */
	describeJournalRemoval({ removal, qboJournalEntryId }) {
		if (removal.leftInQuickBooks) return `Journal ${qboJournalEntryId} is no longer treated as this entry. It was left in QuickBooks — it is the client's — and the adjusting entry is applied by the tool again.`;
		const removed = removal.alreadyDeletedInQuickBooks ? `Journal ${qboJournalEntryId} had already been deleted in QuickBooks; it is recorded as removed.` : `Journal ${qboJournalEntryId} removed from QuickBooks.`;
		if (removal.adjustingEntryReappliedNow) return `${removed} Its adjusting entry is applied by the tool again.`;
		if (removal.adjustingEntryReappliesOnTheNextPull) return `${removed} Its adjusting entry stays retired until the next pull from QuickBooks is saved, because the books saved here still carry the journal; the save will apply it again.`;
		return removed;
	},

	/**
	 * Lays an adjusting entry out against the journal the client says they entered for it in
	 * QuickBooks, found by the journal number QuickBooks shows, so the preparer can see the lines
	 * match before recording it.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.adjustingEntryHeaderId
	 * @param {String} params.qboJournalReference - QuickBooks's journal number, or its id
	 * @return {Promise<Object>} { plan } or { error }
	 */
	async describeClientEntryToRecord({ clientId, wpfEngagementId, adjustingEntryHeaderId, qboJournalReference }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_journal_client_entry_preview", body: { clientId, wpfEngagementId, adjustingEntryHeaderId, qboJournalReference } });
		return answer;
	},

	/**
	 * Records that the client entered an adjusting entry in QuickBooks themselves. Only a journal whose
	 * lines match the entry is recorded; the server refuses the rest with the difference.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.adjustingEntryHeaderId
	 * @param {String} params.qboJournalReference
	 * @return {Promise<Object>} { recorded } or { error }
	 */
	async recordClientEntryInQuickBooks({ clientId, wpfEngagementId, adjustingEntryHeaderId, qboJournalReference }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_journal_client_entry_record", body: { clientId, wpfEngagementId, adjustingEntryHeaderId, qboJournalReference } });
		return answer;
	},

	/**
	 * Which lookup found the client's journal, said when it matters: a number typed that no journal
	 * carries but that is a journal's id finds that journal, which may not be the one the preparer
	 * meant.
	 *
	 * @param {Object} params
	 * @param {Object} params.journalInQuickBooks - from describeClientEntryToRecord
	 * @param {String} params.typed
	 * @return {String|null}
	 */
	describeHowTheJournalWasFound({ journalInQuickBooks, typed }) {
		if (journalInQuickBooks.foundBy !== "id") return null;
		const said = `No journal is numbered ${typed}; this is the journal whose id is ${typed}. Check it is the one the client entered.`;
		return said;
	},

	/**
	 * What recording the client's journal will do to the adjusting entry, said before the preparer
	 * records it: retired at once when the saved books were pulled after the client entered it, or
	 * still applied by the tool until the next pull is saved.
	 *
	 * @param {Object} params
	 * @param {Object} params.plan - from describeClientEntryToRecord
	 * @return {String}
	 */
	describeWhatRecordingTheClientEntryDoes({ plan }) {
		const pulled = UtilityService.formatDateTime({ value: plan.pulledAt });
		if (plan.booksAlreadyCarryIt) return `The books saved here were pulled ${pulled}, after this journal was entered, so they already carry it: recording retires the adjusting entry at once and the tool stops applying it on top.`;
		return `The books saved here were pulled ${pulled}, before this journal was entered, so the tool keeps applying the adjusting entry until the next pull from QuickBooks is saved; that save carries the journal and retires the entry.`;
	},

	/**
	 * What recording did, in a sentence for the preparer.
	 *
	 * @param {Object} params
	 * @param {Object} params.recorded - from recordClientEntryInQuickBooks
	 * @param {Number} params.entryNumber
	 * @return {String}
	 */
	describeClientEntryRecording({ recorded, entryNumber }) {
		const recordedAs = `Adjusting entry ${entryNumber} is recorded as entered by the client in QuickBooks as journal ${recorded.reference}.`;
		const whatHappens = recorded.retiredNow ? "The saved books already carry it, so QuickBooks carries the entry from now on." : "The tool keeps applying it until the next pull from QuickBooks is saved.";
		const sentence = `${recordedAs} ${whatHappens}`;
		return sentence;
	},

	/**
	 * Every journal this engagement has booked into QuickBooks, removed ones included.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @return {Promise<Object>} { journals } or { error }
	 */
	async listJournalsBookedInQuickBooks({ clientId, wpfEngagementId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_journals", body: { clientId, wpfEngagementId } });
		return answer;
	},

	/**
	 * An adjusting entry's booking plan as the confirmation shows it: one row per line with the
	 * QuickBooks account it posts to, or the accounts to choose from when the line named only a
	 * statement line, and whether everything needed to book is in place.
	 *
	 * @param {Object} params
	 * @param {Object} params.journal - from describeAdjustingEntryToBook
	 * @param {Object} params.choices - line number → chosen chart account id
	 * @return {Object} { lines, allChosen, chartAccountIdByLine }
	 */
	describeAdjustingEntryForConfirmation({ journal, choices }) {
		const lines = journal.lines.map(line => {
			const onlyCandidate = line.candidates.length === 1 ? line.candidates[0] : null;
			const chosen = choices[line.lineNumber] || (onlyCandidate ? onlyCandidate.chartAccountId : null);
			const isLinked = !!line.quickBooksAccount;
			const nothingToChoose = !isLinked && line.candidates.length === 0;
			return {
				key: line.lineNumber,
				isLinked,
				quickBooksName: this.describeChartAccount({ account: line.quickBooksAccount }),
				placeholder: nothingToChoose ? "No QuickBooks account to post to — link the chart account first" : null,
				chartName: line.chartAccount ? this.describeChartAccount({ account: line.chartAccount }) : line.statementLine,
				candidates: line.candidates.map(candidate => ({ value: candidate.chartAccountId, label: this.describeChartAccount({ account: candidate.quickBooksAccount }) })),
				chosen,
				debit: line.side === "DEBIT" ? line.amount : 0,
				credit: line.side === "CREDIT" ? line.amount : 0,
			};
		});
		const chartAccountIdByLine = {};
		for (const line of lines) if (!line.isLinked && line.chosen) chartAccountIdByLine[line.key] = line.chosen;
		const allChosen = lines.every(line => line.isLinked || line.chosen);
		return { lines, allChosen, chartAccountIdByLine };
	},

	/**
	 * One Opening Balance Equity reclassification journal as the confirmation shows it.
	 *
	 * The sides come from the server with the journal — which side Opening Balance Equity is debited
	 * or credited on is the server's accounting statement, not something the screen works out again.
	 *
	 * @param {Object} params
	 * @param {Object} params.reclassification - from describeOpeningBalanceEquityReclassification
	 * @param {Object} params.journal - one of reclassification.journals: { amount, openingBalanceEquitySide, targetSide }
	 * @param {String|null} params.targetQboAccountId - the equity account chosen
	 * @return {Object} { lines, totals }
	 */
	describeOpeningBalanceEquityJournalForConfirmation({ reclassification, journal, targetQboAccountId }) {
		const target = reclassification.equityAccounts.find(account => account.qboAccountId === targetQboAccountId) || null;
		const amountOn = side => (side === "DEBIT" ? { debit: journal.amount, credit: 0 } : { debit: 0, credit: journal.amount });
		const lines = [
			{
				key: "obe",
				isLinked: true,
				quickBooksName: this.describeChartAccount({ account: reclassification.openingBalanceEquity }),
				placeholder: null,
				chartName: "Opening Balance Equity — not printed on the statements",
				candidates: [],
				...amountOn(journal.openingBalanceEquitySide),
			},
			{
				key: "target",
				isLinked: !!target,
				quickBooksName: this.describeChartAccount({ account: target }),
				placeholder: "Choose the equity account above",
				chartName: "Where the balance belongs",
				candidates: [],
				...amountOn(journal.targetSide),
			},
		];
		return { lines, totals: { debits: journal.amount, credits: journal.amount } };
	},

	/**
	 * The booking warnings that belong to one Opening Balance Equity journal, matched on the period
	 * the server keyed them by — the same key the booking route takes.
	 *
	 * @param {Object} params
	 * @param {Object} params.reclassification - from describeOpeningBalanceEquityReclassification
	 * @param {Object} params.journal - one of reclassification.journals
	 * @return {Array<Object>}
	 */
	listWarningsForOpeningBalanceEquityJournal({ reclassification, journal }) {
		const warnings = (reclassification.warnings || []).filter(warning => warning.period === journal.period);
		return warnings;
	},

	/**
	 * The codes of the warnings the preparer has ticked, as the booking and save routes take them.
	 *
	 * @param {Object} params
	 * @param {Object} params.acknowledged - warning code → true when ticked
	 * @return {Array<String>}
	 */
	listAcknowledgedWarningCodes({ acknowledged }) {
		const codes = Object.keys(acknowledged || {}).filter(code => acknowledged[code]);
		return codes;
	},

	/**
	 * Whether every warning shown has been ticked, so the consequential button can be enabled.
	 *
	 * @param {Object} params
	 * @param {Array<Object>} params.warnings - [{ code }]
	 * @param {Object} params.acknowledged - warning code → true when ticked
	 * @return {Boolean}
	 */
	everyWarningIsAcknowledged({ warnings, acknowledged }) {
		const every = (warnings || []).every(warning => !!(acknowledged && acknowledged[warning.code]));
		return every;
	},

	/**
	 * Why an adjusting entry tied to a QuickBooks journal cannot be edited, and what frees it — which
	 * differs for a journal booked from here and one the client entered themselves.
	 *
	 * @param {Object} params
	 * @param {String} params.purpose - the journal's purpose
	 * @return {String}
	 */
	describeWhyABookedEntryCannotBeEdited({ purpose }) {
		const isTheClientsJournal = purpose === this.JOURNAL_PURPOSES.ENTERED_BY_CLIENT;
		const reason = isTheClientsJournal
			? "Recorded as entered by the client in QuickBooks — mark it as not the client's entry, or have the client change their journal, to change it"
			: "Booked in QuickBooks — remove it from QuickBooks to change it";
		return reason;
	},

	/**
	 * Where a booked journal stands, in the words every screen uses for it. "Booked" and "carried by
	 * QuickBooks" are two states of one journal: until a pull taken after the booking is saved, the
	 * tool still applies the entry on top of the figures; after it, QuickBooks carries it and the tool
	 * does not. A journal removed from QuickBooks while the saved figures still carry it is a third
	 * state, which lasts until the next pull is saved.
	 *
	 * A journal the client entered themselves, recorded here so it settles the same way, reads as
	 * the client's, because nothing was sent to QuickBooks for it.
	 *
	 * @param {Object} params
	 * @param {String} params.qboJournalEntryId
	 * @param {String|null} params.entryStatus - the adjusting entry's status: "accepted" or "in_quickbooks"
	 * @param {String|null} [params.removedAt]
	 * @param {String} [params.purpose] - "adjusting_entry", "opening_balance_equity" or "entered_by_client"
	 * @return {String}
	 */
	describeBookedJournalState({ qboJournalEntryId, entryStatus, removedAt, purpose }) {
		const enteredByTheClient = purpose === this.JOURNAL_PURPOSES.ENTERED_BY_CLIENT;
		if (removedAt) return `Removed from QuickBooks (was journal ${qboJournalEntryId}) — still in the saved figures until the next pull is saved`;
		if (enteredByTheClient && entryStatus === "in_quickbooks") return `Entered by the client in QuickBooks as journal ${qboJournalEntryId}`;
		if (enteredByTheClient) return `Entered by the client in QuickBooks as journal ${qboJournalEntryId} — applied by the tool until the next pull is saved`;
		if (entryStatus === "in_quickbooks") return `Carried by QuickBooks as journal ${qboJournalEntryId}`;
		return `Booked in QuickBooks as journal ${qboJournalEntryId} — applied by the tool until the next pull is saved`;
	},

	/**
	 * What saving a pull did to the journals booked from the file, in a sentence, or null when it
	 * touched none.
	 *
	 * @param {Object} params
	 * @param {Object} [params.journalsSettled] - { retired: [entry numbers], foundDeleted: [QuickBooks ids] }
	 * @return {String|null}
	 */
	describeJournalsSettledByAPull({ journalsSettled }) {
		if (!journalsSettled) return null;
		const sentences = [];
		if (journalsSettled.retired.length > 0) {
			sentences.push(`QuickBooks now carries adjusting ${journalsSettled.retired.length === 1 ? "entry" : "entries"} ${journalsSettled.retired.join(", ")}, so the tool no longer applies ${journalsSettled.retired.length === 1 ? "it" : "them"} on top.`);
		}
		if (journalsSettled.foundDeleted.length > 0) {
			sentences.push(`Journal ${journalsSettled.foundDeleted.join(", ")} booked from this file was deleted in QuickBooks, so its adjusting entry is applied by the tool again.`);
		}
		const sentence = sentences.length > 0 ? sentences.join(" ") : null;
		return sentence;
	},

	/**
	 * The starting choice for each QuickBooks account that still needs linking, taken from what the
	 * server offered: the proposed existing chart account when there is one, otherwise a new chart
	 * account on the suggested statement line. These are starting points the preparer reviews; none
	 * is sent until they press Link.
	 *
	 * An account already on the chart but without a statement line starts on that chart account, so
	 * only its statement line has to be chosen. A choice says which of two things it is — an existing
	 * chart account, by id, or a new chart account to be made — as two fields, so no reader has to ask
	 * what kind of value the id holds.
	 *
	 * @param {Object} params
	 * @param {Array<Object>} params.accountsNeedingLinking - from the pull's preview
	 * @return {Object} QuickBooks account id to { chartAccountId, createsANewChartAccount, canonicalCode }
	 */
	proposeQuickBooksLinkChoices({ accountsNeedingLinking }) {
		const choices = {};
		for (const account of accountsNeedingLinking || []) {
			const proposedChartAccountId = account.proposedChartAccount ? account.proposedChartAccount.chartAccountId : null;
			const existingChartAccountId = account.linkedChartAccountWithoutAMapping || proposedChartAccountId || null;
			choices[account.qboAccountId] = {
				chartAccountId: existingChartAccountId,
				createsANewChartAccount: !existingChartAccountId,
				canonicalCode: account.suggestedCanonicalCode || null,
			};
		}
		return choices;
	},

	/**
	 * The one thing standing between the preparer and saving a QuickBooks pull, in the order they
	 * have to do things: link the accounts, then anything the server still refuses, then choose
	 * where last year's profit closes, then confirm the warnings. Null when nothing does.
	 *
	 * @param {Object} params
	 * @param {Object} params.preview - the pull's preview
	 * @param {Boolean} params.closeAccountIsMissing
	 * @param {Number} params.unacknowledgedCount
	 * @return {String|null}
	 */
	describeNextQuickBooksStep({ preview, closeAccountIsMissing, unacknowledgedCount }) {
		const accountsToLink = preview.accountsNeedingLinking.length;
		if (preview.refusals.length > 0) return "Not ready: QuickBooks's figures have a problem — see above.";
		if (accountsToLink > 0) return `Next: link the ${UtilityService.describeCount({ count: accountsToLink, singular: "QuickBooks account" })} in step 1.`;
		if (preview.stepsRemaining.length > 0) return `Next: ${preview.stepsRemaining[0]}`;
		if (closeAccountIsMissing) return "Next: choose where last year's profit closes.";
		if (unacknowledgedCount > 0) return "Next: confirm the points above.";
		return null;
	},

	/**
	 * "1 balance read" or "8 balances read", for a file whose books are an imported trial balance.
	 *
	 * @param {Object} params
	 * @param {Object} params.importedTrialBalance - { count, sourceFileName, importedAt }
	 * @return {String}
	 */
	describeCountOfBalances({ importedTrialBalance }) {
		const balances = UtilityService.describeCount({ count: importedTrialBalance.count, singular: "balance" });
		const described = `${balances} read`;
		return described;
	},

	/**
	 * What saving a pull will do to the journals already booked in QuickBooks, said before the save:
	 * the adjusting entries it retires because QuickBooks now carries them, and the booked journals it
	 * found deleted in QuickBooks, whose entries it applies again.
	 *
	 * @param {Object} params
	 * @param {Object} [params.journalSettlement] - { entriesToRetire: [{ entryNumber, qboJournalEntryId }], journalsFoundDeleted: [{ entryNumber, qboJournalEntryId }] }
	 * @return {String|null}
	 */
	describeJournalSettlementPreview({ journalSettlement }) {
		if (!journalSettlement) return null;
		const sentences = [];
		const retiring = (journalSettlement.entriesToRetire || []).filter(item => item.entryNumber);
		const deleted = (journalSettlement.journalsFoundDeleted || []).filter(item => item.entryNumber);
		if (retiring.length > 0) {
			const numbers = retiring.map(item => item.entryNumber).filter(Boolean).join(", ");
			sentences.push(`Saving retires adjusting ${retiring.length === 1 ? "entry" : "entries"} ${numbers}: QuickBooks now carries ${retiring.length === 1 ? "it" : "them"}, so the tool stops applying ${retiring.length === 1 ? "it" : "them"} on top.`);
		}
		if (deleted.length > 0) {
			const ids = deleted.map(item => item.qboJournalEntryId).join(", ");
			sentences.push(`Journal ${ids} booked from this file ${deleted.length === 1 ? "is" : "are"} gone from QuickBooks, so saving applies ${deleted.length === 1 ? "its" : "their"} adjusting ${deleted.length === 1 ? "entry" : "entries"} by the tool again.`);
		}
		const sentence = sentences.length > 0 ? sentences.join(" ") : null;
		return sentence;
	},

	/**
	 * What a file's books are when they were read from a trial balance, in a phrase: how many
	 * balances, and the file or QuickBooks company they came from, as recorded when they were saved —
	 * "8 balances read from QuickBooks Online — Ottawa River Inc — pulled Oct 3, 2026".
	 *
	 * @param {Object} params
	 * @param {Object} params.importedTrialBalance - { count, sourceFileName, importedAt }
	 * @return {String}
	 */
	describeTheBooksSource({ importedTrialBalance }) {
		const balances = this.describeCountOfBalances({ importedTrialBalance });
		const source = importedTrialBalance.sourceFileName || "a trial balance";
		const described = `${balances} from ${source}`;
		return described;
	},

	/**
	 * One line saying what a file's books are when they were read from a trial balance, and what that
	 * means for the file: the source, and that there is nothing to classify.
	 *
	 * @param {Object} params
	 * @param {Object} params.importedTrialBalance - { count, sourceFileName, importedAt }
	 * @return {String}
	 */
	describeWhereTheBooksCameFrom({ importedTrialBalance }) {
		const source = this.describeTheBooksSource({ importedTrialBalance });
		const described = `${source}. There are no transactions to classify; the statements are built straight from these balances.`;
		return described;
	},

	/**
	 * Says where the starting choice for a QuickBooks account came from, so the preparer knows how
	 * much to trust it: an existing chart account with the same number or name, the statement line
	 * QuickBooks's own account type implies, that type read together with the account's name, a
	 * pick by the AI from the lines that type allows, or a match on the account's name alone.
	 *
	 * @param {Object} params
	 * @param {Object} params.account - one of the pull's accountsNeedingLinking
	 * @return {String|null}
	 */
	describeQuickBooksLinkSuggestion({ account }) {
		if (account.proposedChartAccount) return `Proposed: ${account.proposedChartAccount.because}`;
		if (account.suggestionNote) return `Suggested from the account's QuickBooks type. ${account.suggestionNote}`;
		if (account.suggestionMethod === "quickbooks_account_type") return "Suggested from the account's QuickBooks type";
		if (account.suggestionMethod === "quickbooks_account_type_and_name") return "Suggested from the account's QuickBooks type and its name";
		if (account.suggestionMethod === "ai_within_quickbooks_type") return "Picked by AI from the lines the account's QuickBooks type allows — check it";
		if (account.suggestedCanonicalCode) return "Suggested from the account's name — check it";
		return null;
	},

	/**
	 * The links to send for the accounts whose choice is complete: an existing chart account, or a
	 * new chart account with its statement line. A new account without a statement line, or an
	 * account with no choice yet, is left out rather than sent half-made.
	 *
	 * @param {Object} params
	 * @param {Array<Object>} params.accountsNeedingLinking
	 * @param {Object} params.choices - QuickBooks account id to { chartAccountId, createsANewChartAccount, canonicalCode }
	 * @return {Array<Object>} [{ qboAccountId, chartAccountId, canonicalCode }]
	 */
	describeQuickBooksLinks({ accountsNeedingLinking, choices }) {
		const links = [];
		for (const account of accountsNeedingLinking || []) {
			const choice = choices[account.qboAccountId] || {};
			const isNewChartAccount = choice.createsANewChartAccount === true;
			const existingNeedsALine = !isNewChartAccount && account.linkedChartAccountWithoutAMapping === choice.chartAccountId;
			const needsAStatementLine = isNewChartAccount || existingNeedsALine;
			if (!isNewChartAccount && !choice.chartAccountId) continue;
			if (needsAStatementLine && !choice.canonicalCode) continue;
			const link = isNewChartAccount
				? { qboAccountId: account.qboAccountId, canonicalCode: choice.canonicalCode }
				: { qboAccountId: account.qboAccountId, chartAccountId: choice.chartAccountId, canonicalCode: existingNeedsALine ? choice.canonicalCode : undefined };
			links.push(link);
		}
		return links;
	},

	/**
	 * The rows of the "entries to book in QuickBooks" download: one row per line of each accepted
	 * adjusting entry, dated at the year end, naming the QuickBooks account through its stored link.
	 * A line whose chart account is not linked says so in the QuickBooks column instead of naming a
	 * guess, and carries the chart account so the bookkeeper can find it.
	 *
	 * @param {Object} params
	 * @param {Object} params.entriesToBook - from listEntriesToBookInQuickBooks
	 * @return {Object} { headers, rows } for UtilityService.buildCsv
	 */
	describeEntriesToBookAsCsvRows({ entriesToBook }) {
		const headers = ["Journal no.", "Date", "QuickBooks account", "Chart account", "Debit", "Credit", "Memo"];
		const rows = [];
		for (const entry of entriesToBook.entries || []) {
			for (const line of entry.lines) {
				const quickBooksName = this.describeChartAccount({ account: line.quickBooksAccount }) || "Not linked to QuickBooks — choose the account";
				const chartName = this.describeChartAccount({ account: line.accountLabel });
				rows.push([entry.entryNumber, entriesToBook.periodEnd, quickBooksName, chartName, line.debit || "", line.credit || "", entry.description || ""]);
			}
		}
		return { headers, rows };
	},

	/**
	 * Pulls the engagement's current and prior year from the client's QuickBooks company. The
	 * server keeps QuickBooks's answer before reading it and returns the preview of what it would
	 * put in the books; nothing is saved to the books.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @return {Promise<Object>} the preview, or { error }
	 */
	async pullQuickBooksTrialBalance({ clientId, wpfEngagementId }) {
		const preview = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_trial_balance_pull", body: { clientId, wpfEngagementId } });
		return preview;
	},

	/**
	 * What makes up each statement line asked about: its opening balance, movements, adjusting
	 * entries and closing, and every source behind it — QuickBooks accounts, trial balance rows,
	 * bank transactions and adjusting entries.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Array<String>} params.canonicalCodes
	 * @return {Promise<Object>} { booksCameFromQuickBooks, lines } or { error }
	 */
	async describeStatementLines({ clientId, wpfEngagementId, canonicalCodes }) {
		const detail = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/statement_line_detail", body: { clientId, wpfEngagementId, canonicalCodes } });
		return detail;
	},

	/**
	 * QuickBooks's sales tax setup for a file whose books came from QuickBooks: its tax codes with
	 * their rates, and the accounts that carry a default tax code there.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @return {Promise<Object>} { taxSetup } or { error }
	 */
	async describeQuickBooksTaxSetup({ clientId, wpfEngagementId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_tax_setup", body: { clientId, wpfEngagementId } });
		return answer;
	},

	/**
	 * A QuickBooks tax code's rates as one line: "HST ON 13%", "GST 5% + PST 7%", or "None".
	 *
	 * @param {Object} params
	 * @param {Array<Object>} params.rates - [{ name, rateValue }]
	 * @return {String}
	 */
	describeQuickBooksTaxRates({ rates }) {
		if (!rates || rates.length === 0) return "None";
		const described = rates.map(rate => `${rate.name} ${rate.rateValue}%`).join(" + ");
		return described;
	},

	/**
	 * Pulls QuickBooks's own ledger entries for one account, for the books' year or the year before.
	 * The server keeps QuickBooks's answer on the client's record before reading it.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {String} params.qboAccountId
	 * @param {String} params.period - "current" or "prior"
	 * @return {Promise<Object>} { ledger } or { error }
	 */
	async pullQuickBooksAccountLedger({ clientId, wpfEngagementId, qboAccountId, period }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_account_ledger", body: { clientId, wpfEngagementId, qboAccountId, period } });
		return answer;
	},

	/**
	 * Every attachment QuickBooks is holding on one ledger entry's transaction.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.transactionType - the entry's transactionType, from pullQuickBooksAccountLedger
	 * @param {String} params.transactionId - the entry's transactionId, from pullQuickBooksAccountLedger
	 * @return {Promise<Object>} { checked, attachments: [{ id, fileName, contentType, sizeBytes }] } or { error }
	 */
	async describeAttachmentsForLedgerEntry({ clientId, transactionType, transactionId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_ledger_entry_attachments", body: { clientId, transactionType, transactionId } });
		return answer;
	},

	/**
	 * Opens one attachment found by describeAttachmentsForLedgerEntry in a new tab for preview,
	 * rather than forcing it into a download, since a preparer checking a receipt wants to read it
	 * and come back, not manage a downloaded copy.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.attachableId
	 * @param {String} params.fileName
	 * @return {Promise<Object|null>} { error } when it could not be fetched
	 */
	async previewLedgerEntryAttachment({ clientId, attachableId, fileName }) {
		try {
			const response = await authedFetch(`/tools/bookkeeping/qbo_ledger_entry_attachment_download?clientId=${clientId}&attachableId=${attachableId}`, { method: "GET" });
			if (!response || !response.ok) return { error: REQUEST_FAILED_MESSAGE };

			const blob = await response.blob();
			UtilityService.previewFile({ blob, fileName });
			return null;
		} catch (error) {
			return { error: error.message || REQUEST_FAILED_MESSAGE };
		}
	},

	/**
	 * The preview of a kept QuickBooks pull — the latest one for the engagement when no pull is
	 * named, so returning to the screen shows the pull already made.
	 *
	 * @param {Number} clientId
	 * @param {Number} wpfEngagementId
	 * @param {Number} [documentId]
	 * @return {Promise<Object>} { preview } (null when nothing was pulled yet), or { error }
	 */
	async previewQuickBooksTrialBalance({ clientId, wpfEngagementId, documentId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_trial_balance_preview", body: { clientId, wpfEngagementId, documentId } });
		return answer;
	},

	/**
	 * Records which chart account each QuickBooks account is, then returns the pull's preview read
	 * again with those links.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.documentId
	 * @param {Array<Object>} params.links - [{ qboAccountId, chartAccountId }] or [{ qboAccountId, canonicalCode }]
	 * @return {Promise<Object>} { linked, created, preview }, or { error }
	 */
	async linkQuickBooksAccounts({ clientId, documentId, links }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_trial_balance_link", body: { clientId, documentId, links } });
		return answer;
	},

	/**
	 * Removes a chart account's link to its QuickBooks account, then returns the pull's preview read
	 * again.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.chartAccountId
	 * @param {Number} params.documentId
	 * @return {Promise<Object>} { unlinked, preview }, or { error }
	 */
	async unlinkQuickBooksAccount({ clientId, chartAccountId, documentId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_trial_balance_unlink", body: { clientId, chartAccountId, documentId } });
		return answer;
	},

	/**
	 * Makes a kept QuickBooks pull the engagement's books. The server rebuilds every figure from the
	 * kept pull; only the pull and the warnings the preparer accepted are sent.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.documentId
	 * @param {Array<String>} params.acknowledgedWarnings
	 * @param {Number} [params.earningsCloseAccountId] - the equity account last year's profit closes into
	 * @return {Promise<Object>} { imported, replaced, priorYearFiguresSaved, state, importedTrialBalance }, or { error }
	 */
	async commitQuickBooksTrialBalance({ clientId, wpfEngagementId, documentId, acknowledgedWarnings, earningsCloseAccountId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_trial_balance_commit", body: { clientId, wpfEngagementId, documentId, acknowledgedWarnings, earningsCloseAccountId } });
		return answer;
	},

	/**
	 * The engagement's accepted adjusting entries laid out for entering in QuickBooks.
	 *
	 * @param {Number} clientId
	 * @param {Number} wpfEngagementId
	 * @return {Promise<Object>} { periodEnd, entries, linesWithoutAQuickBooksAccount, canPostJournalsToQuickBooks }, or { error }
	 */
	async listEntriesToBookInQuickBooks({ clientId, wpfEngagementId }) {
		const answer = await this.callToolsRouteThatExplainsItsRefusals({ path: "/tools/bookkeeping/qbo_entries_to_book", body: { clientId, wpfEngagementId } });
		return answer;
	},

	/**
	 * Reports what an imported trial balance would produce, without writing anything.
	 *
	 * Returns the canonical entries it would create, the accounts it could not map, and whether the
	 * import balances — so the preparer can fix the mapping before any of it reaches the books.
	 *
	 * @param {Number} clientId
	 * @param {Array} grid - rows of cells, from readStatementGrid
	 * @param {Number} [headerRowIndex] - the preparer's correction, when they have made one
	 * @param {Object} [columns] - the preparer's column assignment, when they have made one
	 * @return {Promise<Object>} { entries, unmapped, difference, balances, columns, detected, rowsRead }
	 */
	async previewTrialBalanceImport({ clientId, grid, headerRowIndex, columns }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.previewTrialBalanceImport(
			{ clientId, grid, headerRowIndex, columns },
			{},
			userData.authToken,
		);
		return response.data;
	},

	/**
	 * The client's balance sheet accounts, as options for choosing where a rounding difference goes.
	 *
	 * A canonical code's first segment is its rollup major, so the balance sheet accounts are the
	 * ASSET, LIABILITY and EQUITY ones. This only narrows what the picker offers — the server checks
	 * the account's statement_type before storing it, so an income statement account cannot be saved
	 * even if it somehow reached the request.
	 *
	 * @param {Array} clientChart - rows from getClientCoA, which arrive camelCased
	 * @return {Array} [{ value: canonicalCode, label }]
	 */
	listBalanceSheetAccountOptions({ clientChart }) {
		const BALANCE_SHEET_MAJORS = ["ASSET.", "LIABILITY.", "EQUITY."];
		const rows = clientChart || [];
		const balanceSheetRows = rows.filter(row =>
			row && row.canonicalCode && BALANCE_SHEET_MAJORS.some(major => String(row.canonicalCode).startsWith(major)));

		const seen = new Set();
		const options = [];
		for (const row of balanceSheetRows) {
			if (seen.has(row.canonicalCode)) continue;
			seen.add(row.canonicalCode);
			options.push({ value: row.canonicalCode, label: this.describeChartAccount({ account: row }) });
		}
		return options;
	},

	/**
	 * Records how this engagement's statements should be presented — which account carries a
	 * whole-dollar rounding difference, the name the entity reports under, and whether the owner's
	 * name in brackets is dropped from it. Only the keys passed are changed.
	 *
	 * @param {Number} clientId
	 * @param {Number} wpfEngagementId
	 * @param {Object} settings
	 * @return {Promise<Object>} the stored presentation settings
	 */
	/**
	 * Turns the presentation settings stored on an engagement into the values the settings screen
	 * shows, filling in the default for anything never chosen.
	 *
	 * The defaults live here rather than in the screen because they decide what the statements do when
	 * nothing was chosen — one signatory, titled Director, expenses in their natural order — and a
	 * screen that disagreed with the server about a default would show a preparer a setting they do
	 * not have.
	 *
	 * @param {Object} settings - as stored on the engagement, possibly empty
	 * @return {Object} every setting the Statements tab displays
	 */
	describeStoredPresentationSettings({ settings }) {
		let stored = settings || {};
		return {
			roundingCanonicalCode: stored.roundingCanonicalCode || null,
			statementEntityName: stored.statementEntityName || "",
			dropOwnerNameBrackets: !!stored.dropOwnerNameBrackets,
			accountCaptions: stored.accountCaptions || {},
			signatoryCount: stored.signatoryCount || 1,
			signatoryTitle: stored.signatoryTitle || "Director",
			jurisdiction: stored.jurisdiction || "",
			incorporationDate: stored.incorporationDate || "",
			principalActivities: stored.principalActivities || "",
			compilationReportCity: stored.compilationReportCity || "",
			compilationReportDate: stored.compilationReportDate || "",
			sectionSortOrder: stored.sectionSortOrder || {},
			statementOrder: stored.statementOrder || {},
		};
	},

	/**
	 * The order a section's lines are in after one of them is moved a place up or down.
	 *
	 * Line order is stored as a list of canonical codes rather than positions, so an account that
	 * appears for the first time next year lands beside its neighbours instead of at a numbered slot
	 * that no longer means anything. The whole section is returned, not just the line that moved,
	 * because a partial list would leave the rest to be re-derived and that is where order drifts.
	 *
	 * @param {Object} params
	 * @param {Array<Object>} params.accounts - the section's lines, in the order shown
	 * @param {String} params.canonicalCode - the line being moved
	 * @param {String} params.direction - "up" or "down"
	 * @return {Array<String>} every canonical code in the section, in the new order
	 */
	moveAccountWithinSection({ accounts, canonicalCode, direction }) {
		let codes = (accounts || []).map(account => account.canonical_code);
		let from = codes.indexOf(canonicalCode);
		if (from === -1) return codes;

		let to = direction === "up" ? from - 1 : from + 1;
		if (to < 0 || to >= codes.length) return codes;

		let reordered = [...codes];
		reordered[from] = codes[to];
		reordered[to] = codes[from];
		return reordered;
	},

	/**
	 * What to save when the compilation report is switched between the standard wording and the
	 * firm's own.
	 *
	 * Turning custom wording on must not resend the paragraphs currently on screen. Coming back from
	 * Default those are the standard ones, so resending them overwrites whatever the firm had
	 * written with the standard text and the edits are gone before anyone sees the screen. The
	 * wording is only seeded when the firm has genuinely never written any.
	 *
	 * Turning it off changes nothing but the choice, so the firm's wording is still there to come
	 * back to.
	 *
	 * @param {Boolean} custom - whether the firm's own wording is being asked for
	 * @param {Object} report - as the compilation payload describes it, carrying both wordings
	 * @return {Object} the settings to save
	 */
	describeWordingModeChange({ custom, report }) {
		if (!custom) return { useCustomCompilationReport: false };

		const wordingAlreadyWritten = (report && report.ownParagraphs) || [];
		if (wordingAlreadyWritten.length > 0) return { useCustomCompilationReport: true };

		const startingFrom = (report && report.standardParagraphs) || (report && report.paragraphs) || [];
		return { useCustomCompilationReport: true, compilationReportParagraphs: startingFrom };
	},

	/**
	 * Reads what the year-end package is expected to contain
	 * @param {Number} clientId
	 * @param {Number} [wpfEngagementId]
	 * @return {Promise<Object>}
	 */
	async loadPackageChecklist({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getPackageChecklist({}, { clientId, wpfEngagementId }, userData.authToken);
			return response.data || { items: [] };
		} catch (error) {
			return { items: [] };
		}
	},

	/**
	 * Records a decision about one package checklist item, or adds one by hand
	 * @param {Number} clientId
	 * @param {Number} [wpfEngagementId]
	 * @param {String} [itemKey] - omitted when adding an item by hand, which the server names
	 * @param {String} [status]
	 * @param {String} [dismissedReason]
	 * @param {String} [title]
	 * @param {String} [periodLabel]
	 * @return {Promise<Object>}
	 */
	async recordPackageItemDecision({ clientId, wpfEngagementId, itemKey, status, dismissedReason, title, periodLabel }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.recordPackageItemDecision(
			{ clientId, wpfEngagementId, itemKey, status, dismissedReason, title, periodLabel },
			{},
			userData.authToken,
		);
		return response.data;
	},

	/**
	 * Removes a package checklist item that was added by hand
	 * @param {Number} clientId
	 * @param {Number} [wpfEngagementId]
	 * @param {String} itemKey
	 * @return {Promise<Object>}
	 */
	async removeManualPackageItem({ clientId, wpfEngagementId, itemKey }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.removeManualPackageItem({ clientId, wpfEngagementId, itemKey }, {}, userData.authToken);
		return response.data;
	},

	/**
	 * Uploads a document that satisfies a package checklist item
	 * @param {Number} clientId
	 * @param {Number} [wpfEngagementId]
	 * @param {String} itemKey
	 * @param {String} [formCode]
	 * @param {File} file
	 * @return {Promise<Object>}
	 */
	async uploadPackageDocument({ clientId, wpfEngagementId, itemKey, formCode, file }) {
		let formData = new FormData();
		formData.append("files", file);
		formData.append("clientId", clientId);
		if (wpfEngagementId) formData.append("wpfEngagementId", wpfEngagementId);
		formData.append("itemKey", itemKey);
		if (formCode) formData.append("formCode", formCode);
		let response = await authedFetch(`/tools/bookkeeping/package_document`, {
			method: "POST",
			body: formData,
		});
		let result = await response.json();
		return result;
	},

	/**
	 * Reads the presentation choices stored on a client's engagement
	 * @param {Number} clientId
	 * @param {Number} [wpfEngagementId]
	 * @return {Promise<Object>}
	 */
	async getEngagementPresentationSettings({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementPresentationSettings({}, { clientId, wpfEngagementId }, userData.authToken);
			return response.data || {};
		} catch (error) {
			return {};
		}
	},

	async setEngagementPresentationSettings({ clientId, wpfEngagementId, settings }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.setEngagementPresentationSettings(
			{ clientId, wpfEngagementId, settings },
			{},
			userData.authToken,
		);
		return response.data;
	},

	/**
	 * Rolls a finalized engagement forward into the next period. Creates the
	 * successor engagement (linked via prior_engagement_id) with settings and
	 * working papers carried forward; openings + comparatives derive from the
	 * prior signed deliverable.
	 * @param {Number} clientId
	 * @param {Number} [priorEngagementId]
	 * @param {Boolean} [force] - roll forward again even if already rolled forward
	 * @return {Promise<Object>}
	 */
	async rollForward({ clientId, priorEngagementId, force }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.rollForward({ clientId, priorEngagementId, force }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Loads the year-end package for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async loadYearEndPackage({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getYearEndPackage({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Builds the year-end package for a client
	 * @param {Number} clientId
	 * @param {Array} attachedDocumentIds
	 * @param {Number} targetFolderId
	 * @param {Boolean} force
	 * @param {String} lawyerName
	 * @param {String} lawyerFirm
	 * @param {String} lawyerEmail
	 * @param {String} lawyerAddress
	 * @param {Array} excludedDocTypes
	 * @return {Promise<Object>}
	 */
	async buildYearEndPackage({ clientId, attachedDocumentIds, targetFolderId, force, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocTypes }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createYearEndPackage({ clientId, attachedDocumentIds, targetFolderId, force, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocTypes }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			if (error.response) return { error: error.response.data || { message: "Build failed" } };
			return { error: { message: error.message || "Network error" } };
		}
	},

	/**
	 * Builds and downloads the year-end package as a zip file
	 * @param {Number} clientId
	 * @param {Array} attachedDocumentIds
	 * @param {Number} targetFolderId
	 * @param {Boolean} force
	 * @param {String} lawyerName
	 * @param {String} lawyerFirm
	 * @param {String} lawyerEmail
	 * @param {String} lawyerAddress
	 * @param {Array} excludedDocTypes
	 * @return {Promise<String>}
	 */
	async downloadYearEndPackageZip({ clientId, attachedDocumentIds, targetFolderId, force, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocTypes }) {
		let response = await authedFetch(`/tools/bookkeeping/year_end_package?download=zip`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ clientId, attachedDocumentIds, targetFolderId, force, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocTypes }),
		});
		if (response.status >= 400) {
			let body = await response.json().catch(function () { return null; });
			throw new Error(body && body.message ? body.message : "Download failed");
		}
		let blob = await response.blob();
		let disposition = response.headers.get("content-disposition") || "";
		let nameMatch = /filename="?([^"]+)"?/i.exec(disposition);
		let filename = nameMatch ? nameMatch[1] : "Year-End Package.zip";
		let url = window.URL.createObjectURL(blob);
		let a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
		return filename;
	},


	/**
	 * Fetches the client document requests for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getClientRequests({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getClientRequests({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) { return { request: null, items: [] }; }
	},

	/**
	 * Adds items to a client's document request
	 * @param {Number} clientId
	 * @param {Array} items
	 * @return {Promise<Object>}
	 */
	async addClientRequestItems({ clientId, items }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createClientRequests({ clientId, items }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Updates items on a client's document request
	 * @param {Number} requestId
	 * @param {Array} items
	 * @return {Promise<Object>}
	 */
	async updateClientRequestItems({ requestId, items }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateClientRequests({ requestId, items }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Fetches the fixed asset register for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async fetchFixedAssetRegister({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.fixedAssetRegister({ client_id: clientId }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Deletes a single item from a client's document request
	 * @param {Number} itemId
	 * @return {Promise<Object>}
	 */
	async deleteClientRequestItem({ itemId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.deleteClientRequests({ itemId }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Sends a client's document request to a contact email
	 * @param {Number} requestId
	 * @param {String} contactEmail
	 * @return {Promise<Object>}
	 */
	async sendClientRequests({ requestId, contactEmail }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createClientRequestsSend({ requestId, contactEmail }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},



	/**
	 * Turns account drill-down transactions into a debit/credit working-paper
	 * breakdown with the tax portion on each row. The amount posted to the account
	 * is the opposite of the bank movement (invertSign true) for bank-classified
	 * transactions, and the amount itself for general ledger entries. A positive
	 * posting is a debit, a negative posting a credit. Rows that come from the books
	 * already say their tax — the server posted it, so the amount here is what reached
	 * the account and the tax sits beside it, and the two add up to the account's line
	 * on the trial balance. A general ledger row carries its tax as a dollar amount;
	 * anything else has it derived from the tax code on the tax-inclusive amount.
	 *
	 * @param {Array} transactions
	 * @param {Boolean} invertSign - True for bank transactions, false for GL entries
	 * @param {Boolean} taxIsAmount - True when the tax field already holds a dollar amount
	 * @returns {Object} { rows: [{ ...transaction, debit, credit, taxAmount }], totalDebit, totalCredit, totalTax }
	 */
	buildAccountDrilldownRows({ transactions, invertSign, taxIsAmount }) {
		let totalDebit = 0;
		let totalCredit = 0;
		let totalTax = 0;

		const rows = (transactions || []).map(transaction => {
			const amount = Number(transaction.amount) || 0;
			const postedAmount = invertSign ? -amount : amount;
			const debit = postedAmount > 0 ? postedAmount : 0;
			const credit = postedAmount < 0 ? -postedAmount : 0;
			const theBooksSaidTheTax = typeof transaction.taxAmount === "number";
			let taxAmount;
			if (theBooksSaidTheTax) taxAmount = transaction.taxAmount;
			else if (taxIsAmount) taxAmount = Number(transaction.tax) || 0;
			else taxAmount = this.calculateTaxAmount({ amount, tax: transaction.tax, hasTax: true });

			totalDebit += debit;
			totalCredit += credit;
			totalTax += taxAmount;

			return { ...transaction, debit, credit, taxAmount };
		});

		return { rows, totalDebit, totalCredit, totalTax };
	},


	/**
	 * Fetches the lead sheets for a client's engagement
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getLeadSheets({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getLeadSheets({}, { clientId, wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) { return { leadSheets: [] }; }
	},

	/**
	 * Updates the notes and sign-off on a lead sheet
	 * @param {Number} clientId
	 * @param {String} wpRef
	 * @param {String} notes
	 * @param {Object} signoff
	 * @return {Promise<Object>}
	 */
	async updateLeadSheet({ clientId, wpRef, notes, signoff }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateLeadSheets({ clientId, wpRef, notes, signoff }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},


	/**
	 * Fetches the documents attached to a client's lead sheets
	 * @param {Number} clientId
	 * @param {String} wpRef
	 * @return {Promise<Object>}
	 */
	async getLeadSheetDocuments({ clientId, wpRef }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getLeadSheetDocuments({}, { clientId, wpRef }, userData.authToken);
			return response.data;
		} catch (error) { return { documents: [], tags: [] }; }
	},

	/**
	 * Uploads a document to a client's lead sheet
	 * @param {Number} clientId
	 * @param {String} wpRef
	 * @param {String} evidenceRequirementCode
	 * @param {String} canonicalCode
	 * @param {Number} expectedBalance
	 * @param {File} file
	 * @return {Promise<Object>}
	 */
	async uploadLeadSheetDocument({ clientId, wpRef, evidenceRequirementCode, canonicalCode, expectedBalance, file }) {
		try {
			let formData = new FormData();
			formData.append("files", file);
			formData.append("clientId", clientId);
			formData.append("wpRef", wpRef);
			formData.append("evidenceRequirementCode", evidenceRequirementCode);
			if (canonicalCode) formData.append("canonicalCode", canonicalCode);
			if (expectedBalance != null) formData.append("expectedBalance", String(expectedBalance));
			let response = await authedFetch(`/tools/bookkeeping/lead_sheet_documents`, {
				method: "POST",
				body: formData,
			});
			return await response.json();
		} catch (error) { return null; }
	},

	/**
	 * Fetches the canonical chart of accounts taxonomy
	 * @return {Promise<Array>}
	 */
	async getCanonicalTaxonomy() {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getCanonicalAccounts({}, {}, userData.authToken);
			let data = response.data;
			return data.accounts || [];
		} catch (error) { return []; }
	},

	/**
	 * Toggles whether a bank statement is excluded from reconciliation
	 * @param {Number} verificationId
	 * @param {Boolean} excluded
	 * @return {Promise<Object>}
	 */
	/**
	 * The client's chart as options a picker can offer, labelled the same way describeChartAccount
	 * labels the same account everywhere else
	 * @param {Array} clientChart - rows from getClientCoA, which arrive camelCased
	 * @return {Array}
	 */
	listChartAccountOptions({ clientChart }) {
		const rows = (clientChart || []).filter(row => row && row.id);
		const options = rows.map(row => ({ value: row.id, label: this.describeChartAccount({ account: row }) }));
		return options;
	},

	/**
	 * Records pairs of statement lines as payments between the client's own accounts.
	 *
	 * The whole batch goes in one request. Sent one at a time, each pair paid the full round trip
	 * to the server and back for work that takes milliseconds.
	 *
	 * @param {Object} params
	 * @param {Array} params.pairs  [{ outgoingTransactionId, incomingTransactionId }]
	 * @return {Promise<Object>} { results } — one verdict per pair, in the order sent
	 */
	/**
	 * Records which equity account an imported prior year's profit closes into.
	 *
	 * @param {Object} params
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.accountId
	 * @return {Promise<Object>} { ok, reason, account } — account is null when the designation is cleared
	 */
	/**
	 * What the prior year's profit is, which equity account it closes into, and what else it could.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} [params.wpfEngagementId]
	 * @return {Promise<Object>} { earningsClose } or { error }
	 */
	/**
	 * The statements whose lines do not account for the movement they print.
	 *
	 * A statement states its own opening and closing balance, so it says how much moved. When the
	 * lines that reached the books fall short of that, money is missing from the books and stays
	 * missing for the rest of the engagement.
	 *
	 * Three different things can be wrong and each arrives with its own `kind`: the statement belongs
	 * to no account in these books, what was read off it does not reach the closing balance it prints,
	 * or the books hold less than its lines say. They are fixed in different places, so a caller that
	 * flattens them into "something is wrong" has to send the preparer looking.
	 *
	 * `answered` says whether the question was actually asked. A failed request and a file with
	 * nothing wrong both return an empty list, and this is read to decide whether to stop a preparer
	 * working. Treating a network blip as "nothing wrong" would let the books be built on statements
	 * nobody checked, which is the thing being prevented; treating it as "everything is wrong" would
	 * stop a firm working over a dropped connection. The caller has to tell the two apart and say so
	 * on screen.
	 *
	 * @param {Object} params
	 * @param {Number} params.wpfEngagementId
	 * @return {Promise<Object>} { answered, statements, acceptance, error }
	 */
	async fetchStatementsThatDoNotReconcile({ wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getStatementsThatDoNotReconcile({}, { engagementId: wpfEngagementId }, userData.authToken);
			let data = response.data || {};
			return { answered: true, statements: data.statements || [], acceptance: data.acceptance || null };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { answered: false, statements: [], acceptance: null, error: reason };
		}
	},

	/**
	 * Asks what flipping an account's signs would do to the statements on file: which of them stop
	 * supporting the books, which start to, and how much would be left unaccounted for afterwards.
	 *
	 * `answered` says whether the question was actually asked. A failed request returns no preview,
	 * and the caller must warn rather than read that as "nothing would change", because flipping
	 * reverses every line and the books are only safe to flip when the statements say so.
	 *
	 * @param {Object} params
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.bankAccountId - the engagement's bank account whose signs would be flipped
	 * @return {Promise<Object>} { answered, preview, error }
	 */
	async fetchWhatFlippingWouldDo({ wpfEngagementId, bankAccountId }) {
		if (!wpfEngagementId || !bankAccountId) return { answered: false, preview: null, error: "an engagement and a bank account are needed" };
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.previewFlipSigns({}, { engagementId: wpfEngagementId, bankAccountId }, userData.authToken);
			let preview = response.data || null;
			return { answered: true, preview, error: null };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { answered: false, preview: null, error: reason };
		}
	},

	/**
	 * Turns what flipping an account's signs would do into the words the confirmation shows.
	 *
	 * It warns whenever a statement that supports the books now would stop supporting them, and
	 * whenever the check could not be made: an unanswered request or a statement that could not be
	 * compared is not evidence that flipping is safe. It stays quiet only when the statements were
	 * read and none of them would stop. `startReconciling` is carried out so the caller can say how
	 * many statements came into line once the flip is done.
	 *
	 * @param {Object} params
	 * @param {Object} params.preview - { statementsOnFile, reconcileNow, stopReconciling, startReconciling, unaccountedForAfterFlip, couldNotCompare }
	 * @param {Boolean} params.answered - whether the preview request was answered
	 * @param {String} params.accountName
	 * @return {Object} { mustWarn, title, lines, note, startReconciling }
	 */
	describeWhatFlippingWouldDo({ preview, answered, accountName }) {
		const title = `Flip signs on ${accountName}?`;
		const note = "Flip only if the statements themselves were read the wrong way round. To change the signs in a download only, use “Flip signs in the download” instead.";

		const couldNotCheck = !answered || !preview || preview.couldNotCompare === true;
		if (couldNotCheck) {
			const lines = ["The tool could not check this account's statements, so it cannot say whether they support the books as they stand."];
			return { mustWarn: true, title, lines, note, startReconciling: 0 };
		}

		const startReconciling = preview.startReconciling || 0;
		const statementsThatWouldStop = preview.stopReconciling || [];
		const wouldAnyStop = statementsThatWouldStop.length > 0;
		if (!wouldAnyStop) {
			return { mustWarn: false, title, lines: [], note, startReconciling };
		}

		const statementsOnFile = preview.statementsOnFile || 0;
		const reconcileNow = preview.reconcileNow || 0;
		const stopCount = statementsThatWouldStop.length;
		const statementsOnFileText = UtilityService.describeCount({ count: statementsOnFile, singular: "statement" });
		const isOneStatementOnFile = statementsOnFile === 1;
		const readVerb = isOneStatementOnFile ? "reads" : "read";
		const supportVerb = reconcileNow === 1 ? "supports" : "support";
		const whoSupportsTheBooks = isOneStatementOnFile ? "it supports the books." : `${reconcileNow} of them ${supportVerb} the books.`;
		const stopWord = stopCount === 1 ? "statement" : "statements";
		const unaccountedForText = `$${UtilityService.formatCurrency(preview.unaccountedForAfterFlip)}`;
		const howTheBooksReadNow = `The ${statementsOnFileText} on file for ${accountName} ${readVerb} the same way as the books do now: ${whoSupportsTheBooks}`;
		const whatFlippingDoes = `Flipping reverses every line against its statement: ${stopCount} ${stopWord} will stop supporting the books, leaving ${unaccountedForText} unaccounted for, and the bank balance and everything built from it will be wrong by up to that amount.`;
		const howToFlipBack = "To undo it, flip again straight away. After Statements is next used or these books are opened again, the tool may hold them until the statements are put right or someone chooses “Work on these books anyway”, and flipping back then needs that step first.";
		const lines = [howTheBooksReadNow, whatFlippingDoes, howToFlipBack];
		return { mustWarn: true, title, lines, note, startReconciling };
	},

	/**
	 * Records a line that is on the statement but never made it into the books.
	 *
	 * @param {Object} params
	 * @param {Number} params.statementId
	 * @param {Number} params.wpfEngagementId
	 * @param {String} params.transactionDate
	 * @param {String} params.description
	 * @param {Number} params.amount - signed the way the statement reads it
	 * @return {Promise<Object>} { added, reason, transactionId }
	 */
	async addLineReadOffTheStatement({ statementId, wpfEngagementId, transactionDate, description, amount }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.addLineReadOffTheStatement(
				{ statementId, engagementId: wpfEngagementId, transactionDate, description, amount },
				{},
				userData.authToken
			);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { added: false, reason };
		}
	},

	async fetchEarningsClose({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEarningsClose({}, { clientId, wpfEngagementId }, userData.authToken);
			return { earningsClose: response.data.earningsClose };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	async designateEarningsCloseAccount({ wpfEngagementId, accountId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateEarningsClose({ wpfEngagementId, accountId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { ok: false, reason };
		}
	},

	async recordTransferPairs({ pairs }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.recordTransferPairs({ pairs }, {}, userData.authToken);
		return response.data;
	},

	/**
	 * Undoes a transfer match so both legs post again
	 * @param {Number} transactionId  the leg that was recorded as the mirror
	 * @return {Promise<Object>}
	 */
	async releaseTransferPair({ transactionId }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.releaseTransferPair({ transactionId }, {}, userData.authToken);
		return response.data;
	},

	/**
	 * Lists each imported bank or card account with the chart account it posts to.
	 *
	 * The engagement is named rather than left to the server to guess. These links decide which
	 * chart account the second side of every transaction posts to, and a client has one
	 * engagement per year — answering for the wrong year would offer the wrong accounts to retie.
	 *
	 * A failure comes back as a reason, never as an empty list. An empty list is a real answer —
	 * this client has imported nothing yet — and returning it for a failed request tells the
	 * screen a client with accounts has none.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} [params.wpfEngagementId]
	 * @return {Promise<Object>} { bankAccounts } or { error }
	 */
	async fetchBankAccountLinks({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getBankAccountLinks({}, { clientId, wpfEngagementId }, userData.authToken);
			return { bankAccounts: response.data.bankAccounts || [] };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * The account names read off a set of statements, each once. A statement whose account could
	 * not be read has no label and is left out rather than listed as a blank.
	 *
	 * @param {Object} params
	 * @param {Array} params.statements - statements as the shelf lists them, each with accountLabel
	 * @returns {Array} account labels, each once, in the order first seen
	 */
	listAccountLabelsOnStatements({ statements }) {
		const everyAccountNamed = (statements || []).map(statement => statement.accountLabel).filter(Boolean);
		const accountLabels = [...new Set(everyAccountNamed)];
		return accountLabels;
	},

	/**
	 * The one key by which an account is recognised across the shelf and the statements screen:
	 * the chart account it is tied to when it has one, and its name otherwise. Every place that
	 * asks "is this statement for that account" compares these keys, so there is one answer.
	 *
	 * @param {Object} params
	 * @param {Number} [params.accountId] - the chart account, when tied
	 * @param {String} params.name
	 * @returns {String}
	 */
	keyOfAnAccount({ accountId, name }) {
		const key = accountId ? "account:" + accountId : "label:" + name;
		return key;
	},

	/**
	 * Which account a statement belongs to, as the shelf understands it.
	 *
	 * A statement carries the chart account it was tied to when it was read, and the label read
	 * off the page. The tie is the identity when there is one, and the label when there is not.
	 * A statement with neither has no account, and says so, rather than being filed under the
	 * bank's name: an institution is not an account, and a Scotia chequing account and a Scotia
	 * card would otherwise land in one section.
	 *
	 * @param {Object} params
	 * @param {Object} params.statement - a statement as the shelf lists it
	 * @returns {Object} { key, name, isAccountKnown }
	 */
	whichAccountAStatementBelongsTo({ statement }) {
		const isAccountKnown = !!(statement.accountId || statement.accountLabel);
		const name = statement.accountLabel || (statement.accountId ? "Account " + statement.accountId : "Account not read");
		const key = this.keyOfAnAccount({ accountId: statement.accountId, name });
		return { key, name, isAccountKnown };
	},

	/**
	 * The first day and the last day a set of statements covers between them.
	 *
	 * @param {Object} params
	 * @param {Array} params.statements
	 * @returns {Object} { periodStart, periodEnd }, either null when no statement carries a date
	 */
	spanCoveredByStatements({ statements }) {
		const starts = (statements || []).map(statement => statement.periodStart).filter(Boolean);
		const ends = (statements || []).map(statement => statement.periodEnd).filter(Boolean);
		const sortedStarts = starts.map(String).sort();
		const sortedEnds = ends.map(String).sort();
		const periodStart = sortedStarts.length > 0 ? sortedStarts[0] : null;
		const periodEnd = sortedEnds.length > 0 ? sortedEnds[sortedEnds.length - 1] : null;
		return { periodStart, periodEnd };
	},

	/**
	 * Says how well an account is covered, the way both the shelf and the statements screen
	 * show it beside the account name: "12 statements · Jun 30, 2025 – Jun 30, 2026".
	 *
	 * @param {Object} params
	 * @param {Number} params.count
	 * @param {String} [params.periodStart]
	 * @param {String} [params.periodEnd]
	 * @returns {String}
	 */
	describeStatementCoverage({ count, periodStart, periodEnd }) {
		const statements = UtilityService.describeCount({ count, singular: "statement" });
		const range = UtilityService.describeDateRange({ start: periodStart, end: periodEnd });
		const coverage = `${statements} · ${range}`;
		return coverage;
	},

	/**
	 * Groups the statements on the shelf into one section per bank account, in the order the
	 * shelf lists them: accounts by name, then any statement whose account could not be read in a
	 * final section of its own, so it is still seen rather than filed under a guess. Within a
	 * section statements run in date order.
	 *
	 * @param {Object} params
	 * @param {Array} params.statements - statements as the shelf lists them
	 * @returns {Array} [{ key, name, isAccountKnown, statements, count, periodStart, periodEnd }]
	 */
	listStatementSectionsByAccount({ statements }) {
		const sectionByKey = {};
		const keysInOrder = [];

		for (const statement of statements || []) {
			const account = this.whichAccountAStatementBelongsTo({ statement });
			if (!sectionByKey[account.key]) {
				sectionByKey[account.key] = { key: account.key, name: account.name, isAccountKnown: account.isAccountKnown, statements: [] };
				keysInOrder.push(account.key);
			}
			sectionByKey[account.key].statements.push(statement);
		}

		const byDateThenId = (left, right) => {
			const byDate = String(left.periodStart || "").localeCompare(String(right.periodStart || ""));
			if (byDate !== 0) return byDate;
			return left.id - right.id;
		};
		const sections = keysInOrder.map(key => {
			const section = sectionByKey[key];
			const statementsInOrder = [...section.statements].sort(byDateThenId);
			const span = this.spanCoveredByStatements({ statements: statementsInOrder });
			return { ...section, statements: statementsInOrder, count: statementsInOrder.length, periodStart: span.periodStart, periodEnd: span.periodEnd };
		});

		const knownAccounts = sections.filter(section => section.isAccountKnown).sort((left, right) => left.name.localeCompare(right.name));
		const accountsNotRead = sections.filter(section => !section.isAccountKnown);
		const sectionsInOrder = [...knownAccounts, ...accountsNotRead];
		return sectionsInOrder;
	},

	/**
	 * How many live transactions each bank account holds, so a screen about to change an account
	 * can say how much the change touches before anyone agrees to it. Archived rows are not
	 * counted, since they are out of the books and stay out.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - rows shaped for the table
	 * @returns {Object} live row count by account name
	 */
	countTransactionsByBankAccount({ transactions }) {
		const countByAccount = {};
		for (const transaction of transactions || []) {
			if (transaction.isArchived || !transaction.accountName) continue;
			countByAccount[transaction.accountName] = (countByAccount[transaction.accountName] || 0) + 1;
		}
		return countByAccount;
	},

	/**
	 * Splits imported rows by the bank account they were attached to, so each account's rows
	 * can be filed on the shelf as their own statement, named for that account. Rows with no
	 * account are grouped under an empty name.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - rows shaped for the table, each with accountName
	 * @returns {Object} rows keyed by account name
	 */
	groupTransactionsByBankAccount({ transactions }) {
		const rowsByAccount = {};
		for (const transaction of transactions || []) {
			const accountName = transaction.accountName || "";
			if (!rowsByAccount[accountName]) rowsByAccount[accountName] = [];
			rowsByAccount[accountName].push(transaction);
		}
		return rowsByAccount;
	},

	/**
	 * The bank accounts a file already knows, ready for the statements screen to list.
	 *
	 * An account on the file is one with a bank account row on this engagement. It carries a
	 * chart tie and a currency, or visibly lacks them, and it cannot be renamed from the
	 * statements screen, because a new name would become a second account at save. A statement
	 * on the shelf for this period can name an account with no row on this file yet — read but
	 * never generated — so those names are offered as well, still free to change.
	 *
	 * @param {Object} params
	 * @param {Array} params.bankAccountLinks - [{ id, accountName, accountId, postsTo, currency }]
	 * @param {Array} params.shelfAccountLabels - account names read off this period's statements on the shelf
	 * @returns {Array} [{ id, name, accountId, postsTo, currency, isOnTheFile }]
	 */
	listBankAccountsKnownToTheFile({ bankAccountLinks, shelfAccountLabels }) {
		const accountsOnTheFile = (bankAccountLinks || []).map(link => ({
			id: link.id,
			name: link.accountName,
			accountId: link.accountId || null,
			postsTo: link.postsTo || null,
			canonicalCode: link.canonicalCode || null,
			currency: link.currency || null,
			isOnTheFile: true,
		}));

		const namesOnTheFile = new Set(accountsOnTheFile.map(account => account.name));
		const labelsNotOnTheFile = (shelfAccountLabels || []).filter(label => label && !namesOnTheFile.has(label));
		const uniqueLabels = [...new Set(labelsNotOnTheFile)];
		const accountsOnlyOnTheShelf = uniqueLabels.map(name => ({ id: null, name, accountId: null, postsTo: null, canonicalCode: null, currency: null, isOnTheFile: false }));

		const knownAccounts = [...accountsOnTheFile, ...accountsOnlyOnTheShelf];
		return knownAccounts;
	},

	/**
	 * The bank accounts the Settings screen lists: every account on the file, whether or not it
	 * has rows in the table, and every account the rows name that is not on the file yet.
	 *
	 * The file's own list is the record; a list scraped from the rows misses an account with no
	 * rows and cannot say which row belongs to which saved account. The rows contribute one thing
	 * the record may not hold yet, the currency the rows were imported in.
	 *
	 * @param {Object} params
	 * @param {Array} params.bankAccountLinks - the file's bank accounts as the server lists them
	 * @param {Array} params.transactions - table rows, each with accountName and currency
	 * @returns {Array} [{ id, accountName, accountId, postsTo, canonicalCode, currency, isOnTheFile }] by name
	 */
	listBankAccountsForSettings({ bankAccountLinks, transactions }) {
		const currencyByAccountName = {};
		for (const transaction of transactions || []) {
			const accountName = transaction.accountName || transaction.bankAccountName;
			if (!accountName || currencyByAccountName[accountName] !== undefined) continue;
			currencyByAccountName[accountName] = transaction.currency || "CAD";
		}

		const knownAccounts = this.listBankAccountsKnownToTheFile({ bankAccountLinks, shelfAccountLabels: Object.keys(currencyByAccountName) });
		const accounts = knownAccounts.map(account => ({
			id: account.id,
			accountName: account.name,
			accountId: account.accountId,
			postsTo: account.postsTo,
			canonicalCode: account.canonicalCode,
			currency: account.currency || currencyByAccountName[account.name] || "CAD",
			isOnTheFile: account.isOnTheFile,
		}));
		return accounts.sort((a, b) => a.accountName.localeCompare(b.accountName));
	},

	/**
	 * How many statements are already on the shelf for each account the statements screen lists,
	 * and the span they cover, so a preparer sees which accounts are already covered before
	 * attaching more. A statement and an account are the same account when they share the one
	 * key the shelf recognises accounts by.
	 *
	 * @param {Object} params
	 * @param {Array} params.statements - statements as the shelf lists them
	 * @param {Array} params.accounts - [{ name, accountId }] the accounts being listed
	 * @returns {Object} by account name: { count, periodStart, periodEnd }
	 */
	summarizeStatementsOnTheShelfByAccount({ statements, accounts }) {
		const summaryByAccountName = {};

		for (const account of accounts || []) {
			const accountKey = this.keyOfAnAccount({ accountId: account.accountId, name: account.name });
			const statementsForAccount = (statements || []).filter(statement => this.whichAccountAStatementBelongsTo({ statement }).key === accountKey);
			if (statementsForAccount.length === 0) continue;

			const span = this.spanCoveredByStatements({ statements: statementsForAccount });
			summaryByAccountName[account.name] = { count: statementsForAccount.length, periodStart: span.periodStart, periodEnd: span.periodEnd };
		}

		return summaryByAccountName;
	},

	/**
	 * The names on a client's chart of accounts, for adding a bank account that has no statement
	 * on the file yet. A name picked from the chart ties to its chart account by that same name
	 * when the account is saved, which is what makes the statement's second side land somewhere
	 * real.
	 *
	 * @param {Object} params
	 * @param {Array} params.chartAccounts - rows as the chart endpoint returns them
	 * @returns {Array} account names, each once
	 */
	listChartAccountNames({ chartAccounts }) {
		const names = (chartAccounts || []).map(account => account.accountName).filter(Boolean);
		const uniqueNames = [...new Set(names)];
		return uniqueNames;
	},

	/**
	 * Points an imported bank or card account at a different account on the client's chart
	 * @param {Number} bankAccountId
	 * @param {Number} accountId
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async setBankAccountChartAccount({ bankAccountId, accountId, clientId }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.updateBankAccountLink({ bankAccountId, accountId, clientId }, {}, userData.authToken);
		return response.data;
	},

	/**
	 * Records which currency an imported bank or card account is held in. Used when a preparer
	 * changes an account's currency deliberately, so the account carries the new fact even when
	 * it has no transactions yet for a save to derive it from.
	 *
	 * @param {Object} params
	 * @param {Number} params.bankAccountId
	 * @param {String} params.currency - ISO code
	 * @param {Number} params.clientId
	 * @return {Promise<Object>} { recorded, reason }
	 */
	async setBankAccountCurrency({ bankAccountId, currency, clientId }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.updateBankAccountLink({ bankAccountId, currency, clientId }, {}, userData.authToken);
		return response.data;
	},

	/**
	 * Ties an imported bank or card account to a chart account and says how it went, in words a
	 * screen can show as they are. Both the statements screen and the table's bank account list
	 * offer this, so the outcome is worded once here rather than once per screen.
	 *
	 * @param {Object} params
	 * @param {Number} params.bankAccountId
	 * @param {Number} params.accountId
	 * @param {Number} params.clientId
	 * @return {Promise<Object>} { tied, message }
	 */
	async tieBankAccountToChart({ bankAccountId, accountId, clientId }) {
		if (!bankAccountId) return { tied: false, message: "That bank account has not been saved yet — import its statement first." };

		const result = await this.setBankAccountChartAccount({ bankAccountId, accountId, clientId });
		const tied = !!(result && result.tied);
		const message = tied ? "This account now posts to the account you chose." : (result && result.reason) || "That account could not be changed.";
		return { tied, message };
	},

	async toggleStatementExclusion({ verificationId, excluded }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateLeadSheetDocuments({ verificationId, toggleExclude: excluded }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Applies a reconciliation action to a single line item
	 * @param {Number} verificationId
	 * @param {String} side
	 * @param {Number} index
	 * @param {String} action
	 * @param {Object} payload
	 * @return {Promise<Object>}
	 */
	async reconciliationAction({ verificationId, side, index, action, payload }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createReconciliationAction({ verificationId, side, index, action, payload }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || "the server could not be reached" };
		}
	},

	/**
	 * Applies a reconciliation action to multiple line items
	 * @param {Number} verificationId
	 * @param {String} side
	 * @param {Array} indexes
	 * @param {String} action
	 * @param {Object} payload
	 * @return {Promise<Object>}
	 */
	async reconciliationBulkAction({ verificationId, side, indexes, action, payload }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateReconciliationAction({ verificationId, side, indexes, action, payload }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || serverBody.message || error.message || "the server could not be reached" };
		}
	},

	/**
	 * Updates the reviewed flag and tag on a lead sheet document
	 * @param {Number} documentId
	 * @param {Boolean} reviewed
	 * @param {String} tag
	 * @return {Promise<Object>}
	 */
	async updateLeadSheetDocument({ documentId, reviewed, tag }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateLeadSheetDocuments({ documentId, reviewed, tag }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Deletes a lead sheet document
	 * @param {Number} documentId
	 * @return {Promise<Object>}
	 */
	async deleteLeadSheetDocument({ documentId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.deleteLeadSheetDocuments({ documentId }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},


	/**
	 * Fetches the fixed assets and totals for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getFixedAssets({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getFixedAssets({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) { return { assets: [], totals: { cost: 0, accumulatedDepreciation: 0, nbv: 0 } }; }
	},

	/**
	 * Adds a fixed asset to a client's register
	 * @param {Number} clientId
	 * @param {Object} asset
	 * @return {Promise<Object>}
	 */
	async addFixedAsset({ clientId, asset }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createFixedAssets({ clientId, asset }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Updates a fixed asset in a client's register
	 * @param {Number} clientId
	 * @param {Number} assetId
	 * @param {Object} updates
	 * @return {Promise<Object>}
	 */
	async updateFixedAsset({ clientId, assetId, updates }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateFixedAssets({ clientId, assetId, updates }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Deletes a fixed asset from a client's register
	 * @param {Number} clientId
	 * @param {Number} assetId
	 * @return {Promise<Object>}
	 */
	async deleteFixedAsset({ clientId, assetId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.deleteFixedAssets({ clientId, assetId }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Fetches the available engagement templates
	 * @return {Promise<Array>}
	 */
	async getEngagementTemplates() {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementTemplates({}, {}, userData.authToken);
			let data = response.data;
			return data.templates || [];
		} catch (error) {
			return [];
		}
	},

	/**
	 * Reruns the engagement cross-checks and returns the updated results together with the
	 * refreshed statements as the server presents them.
	 *
	 * The presentation — the rounded statements, the rows they are drawn as, the approval block —
	 * travels with the rerun because the table replaces its copy with whatever comes back. Dropping
	 * it here left the preview without a balance sheet or income statement after every rerun,
	 * including the one that runs the moment a compilation finishes.
	 *
	 * @param {Number} clientId
	 * @param {Number} wpfEngagementId
	 * @param {Object} previousResults
	 * @param {Object} approvalContext
	 * @return {Promise<Object>} { crossCheckResults, crossCheckFacts, rerunTrace, afs, roundedPresentation, statementRows, approvalBlock }
	 */
	async rerunCrossChecks({ clientId, wpfEngagementId, previousResults, approvalContext }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.rerunCrossChecks({ clientId: clientId, wpfEngagementId: wpfEngagementId, previousResults: previousResults, approvalContext: approvalContext }, {}, userData.authToken);
			let data = response.data;
			return {
				crossCheckResults: data.crossCheckResults || [],
				crossCheckFacts: data.crossCheckFacts || null,
				rerunTrace: data.rerunTrace || null,
				afs: data.afs || null,
				roundedPresentation: data.roundedPresentation || null,
				statementRows: data.statementRows || null,
				approvalBlock: data.approvalBlock || null,
			};
		} catch (error) {
			return { crossCheckResults: [], crossCheckFacts: null, rerunTrace: null, afs: null, roundedPresentation: null, statementRows: null, approvalBlock: null };
		}
	},

	/**
	 * Updates a single key in the engagement context
	 * @param {Number} clientId
	 * @param {String} key
	 * @param {*} value
	 * @return {Promise<Object>}
	 */
	async updateEngagementContext({ clientId, key, value }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateEngagementContext({ clientId: clientId, key: key, value: value }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Fetches the client's chart of accounts mapping
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getClientCoA({ clientId, includeRetired = false }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getCoaMapping({}, { clientId, includeRetired: includeRetired ? "true" : undefined }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Puts a chart account away, or says what still needs it.
	 *
	 * An account is never deleted: a booked transaction names the account it was posted to, and
	 * that is the record of where the money went. Retiring stops the account being offered and
	 * can be undone, so what comes back is either a retirement or the list of things that would
	 * have been left pointing at nothing.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.accountId
	 * @returns {Promise<Object>} { retired, stillNeededBy, error }
	 */
	async retireChartAccount({ clientId, accountId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.retireChartAccount({ clientId, accountId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { retired: false, error: serverBody.error || serverBody.message || error.message || "the server could not be reached" };
		}
	},

	/**
	 * Lists every industry a preparer can choose for a client.
	 * @return {Promise<Array>} [{ id, name }]
	 */
	async listIndustries() {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.listIndustries({}, {}, userData.authToken);
			let data = response.data || {};
			return data.industries || [];
		} catch (error) {
			return [];
		}
	},

	/**
	 * Brings a retired chart account back into use.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.accountId
	 * @returns {Promise<Object>} { restored, reason, error }
	 */
	async restoreChartAccount({ clientId, accountId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.restoreChartAccount({ clientId, accountId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { restored: false, error: serverBody.error || serverBody.message || error.message || "the server could not be reached" };
		}
	},

	/**
	 * Adds a new industry a preparer can select, so it doesn't need a deploy first.
	 * @param {Object} params
	 * @param {String} params.name
	 * @return {Promise<Object>} { industry, alreadyExisted } or { error }
	 */
	async addIndustry({ name }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.addIndustry({ name }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			return { error: serverBody.error || "That industry could not be added." };
		}
	},

	/**
	 * How to tell a preparer what still needs an account they tried to put away.
	 *
	 * Each kind brings its own plural. They are noun phrases whose noun is not always the last
	 * word, so adding an s to the end of one produces "3 statement filed against its".
	 *
	 * @param {Object} params
	 * @param {Array} params.stillNeededBy - [{ singular, plural, count }] from the retire attempt
	 * @returns {String}
	 */
	describeWhatStillNeedsTheAccount({ stillNeededBy }) {
		const parts = (stillNeededBy || []).map(one => UtilityService.describeCount({ count: one.count, singular: one.singular, plural: one.plural }));
		if (parts.length === 0) return "";
		if (parts.length === 1) return parts[0];
		return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
	},

	/**
	 * Suggests canonical mappings for a client's chart of accounts
	 * @param {Number} clientId
	 * @param {Array} accounts
	 * @return {Promise<Object>}
	 */
	async mapCoaSuggest({ clientId, accounts }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.mapCoaSuggest({ clientId, accounts }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Saves the chart of accounts mappings for a client
	 * @param {Number} clientId
	 * @param {Object} mappings
	 * @return {Promise<Object>}
	 */
	async mapCoaSave({ clientId, mappings }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.mapCoaSave({ clientId, mappings }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return { error: error.message || "The chart of accounts could not be saved." };
		}
	},

	/**
	 * Materializes a WpfEngagement for a client (no transaction side effects) so
	 * opening balances can be entered before the bank import. Returns
	 * { wpfEngagementId, priorEngagementId, isBootstrap, fiscalYearEnd }.
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async ensureEngagement({ clientId, fiscalYearEnd = undefined, periodStart = undefined, periodEnd = undefined, periodType = undefined, periodReason = undefined, province = undefined }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.ensureEngagement({ clientId, fiscalYearEnd, periodStart, periodEnd, periodType, periodReason, province }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Derives a fiscal period from its end date and how many months it runs. Twelve
	 * months is a full fiscal year ending on that date; a shorter length is an
	 * interim period; a custom start covers a first year, a stub year from a changed
	 * year-end, and a final period on wind-up. No period may exceed 53 weeks
	 * (ITA 249(1)), and the length decides the period type the books are kept under.
	 *
	 * @param {Object} params
	 * @param {String} params.fiscalYearEnd - "YYYY-MM-DD"
	 * @param {Number|String} [params.periodLengthMonths] - 12, 6, 3, 1, or CUSTOM_PERIOD_LENGTH
	 * @param {String} [params.periodStartOverride] - "YYYY-MM-DD", required when the length is custom
	 * @returns {Object} { periodStart, periodEnd, periodMonths, daysInPeriod, isFullYear, isShortTaxYear, periodType, periodReason } or { error }
	 */
	derivePeriodFromYearEnd({ fiscalYearEnd, periodLengthMonths = 12, periodStartOverride = null }) {
		const yearEnd = moment(fiscalYearEnd, "YYYY-MM-DD", true);
		if (!yearEnd.isValid()) return { error: "Pick the period end date." };

		const isCustomLength = periodLengthMonths === CUSTOM_PERIOD_LENGTH;
		let periodStart;

		if (isCustomLength) {
			const chosenStart = moment(periodStartOverride, "YYYY-MM-DD", true);
			if (!chosenStart.isValid()) return { error: "Pick the first day of the period." };
			if (!chosenStart.isBefore(yearEnd)) return { error: "The period must start before it ends." };
			periodStart = chosenStart;
		} else if (periodLengthMonths === 12) {
			periodStart = this.deriveFullYearStart({ fiscalYearEnd: yearEnd.format("YYYY-MM-DD") });
		} else {
			periodStart = this.deriveStartFromMonthsBack({ fiscalYearEnd: yearEnd.format("YYYY-MM-DD"), monthsBack: periodLengthMonths });
		}

		const daysInPeriod = yearEnd.diff(periodStart, "days") + 1;
		if (daysInPeriod > LONGEST_LEGAL_TAX_YEAR_IN_DAYS) {
			return { error: "A fiscal period cannot be longer than 53 weeks (371 days)." };
		}

		const periodMonths = Math.max(1, Math.round(yearEnd.diff(periodStart, "months", true)));
		const isChosenInterim = !isCustomLength && periodLengthMonths !== 12;
		const isFullYear = !isChosenInterim && daysInPeriod >= SHORTEST_PERIOD_TREATED_AS_A_FULL_YEAR_IN_DAYS;

		return {
			periodStart: periodStart.format("YYYY-MM-DD"),
			periodEnd: yearEnd.format("YYYY-MM-DD"),
			periodMonths,
			daysInPeriod,
			isFullYear,
			isShortTaxYear: !isFullYear && !isChosenInterim,
			periodType: derivePeriodTypeFromLength({ isChosenInterim, daysInPeriod }),
			periodReason: isFullYear ? PERIOD_BASIS.FULL_YEAR : isChosenInterim ? PERIOD_BASIS.INTERIM : PERIOD_BASIS.YEAR_END_CHANGE,
		};
	},

	/**
	 * Works out which period-length radio a stored fiscal period corresponds to, so
	 * reopening the settings shows the choice that produced it rather than resetting
	 * to twelve months. A period whose dates no standard length reproduces is custom.
	 *
	 * @param {Object} params
	 * @param {Object} params.fiscalPeriod - { periodStart, periodEnd }
	 * @returns {Number|String} a month count, or CUSTOM_PERIOD_LENGTH
	 */
	matchPeriodLengthOption({ fiscalPeriod }) {
		if (!fiscalPeriod || !fiscalPeriod.periodStart || !fiscalPeriod.periodEnd) return 12;

		const standardLengths = PERIOD_LENGTH_OPTIONS.map(option => option.value).filter(value => value !== CUSTOM_PERIOD_LENGTH);
		for (const lengthMonths of standardLengths) {
			const derived = this.derivePeriodFromYearEnd({ fiscalYearEnd: fiscalPeriod.periodEnd, periodLengthMonths: lengthMonths });
			if (!derived.error && derived.periodStart === fiscalPeriod.periodStart) return lengthMonths;
		}
		return CUSTOM_PERIOD_LENGTH;
	},

	/**
	 * The first day of a period that runs a whole number of months back from its end
	 * date — a 3-month period ending Jun 30 starts Apr 1.
	 *
	 * @param {Object} params
	 * @param {String} params.fiscalYearEnd - "YYYY-MM-DD"
	 * @param {Number} params.monthsBack
	 * @returns {Object} a moment for the first day of the period
	 */
	deriveStartFromMonthsBack({ fiscalYearEnd, monthsBack }) {
		const yearEnd = moment(fiscalYearEnd, "YYYY-MM-DD", true);
		const isMonthEnd = yearEnd.clone().endOf("month").isSame(yearEnd, "day");
		if (isMonthEnd) {
			const startOfPeriodMonth = yearEnd.clone().subtract(monthsBack - 1, "months").startOf("month");
			return startOfPeriodMonth;
		}
		const dayAfterPriorPeriodEnd = yearEnd.clone().subtract(monthsBack, "months").add(1, "day").startOf("day");
		return dayAfterPriorPeriodEnd;
	},

	/**
	 * The first day of a full year ending on the given year-end: the day after the
	 * previous year-end. A month-end year-end is treated as following a month-end,
	 * so an end-of-February year always starts March 1 and the leap day belongs to
	 * exactly one fiscal year.
	 *
	 * @param {Object} params
	 * @param {String} params.fiscalYearEnd - "YYYY-MM-DD"
	 * @returns {Object} a moment for the first day of the period
	 */
	deriveFullYearStart({ fiscalYearEnd }) {
		const yearEnd = moment(fiscalYearEnd, "YYYY-MM-DD", true);
		const isMonthEndYearEnd = yearEnd.clone().endOf("month").isSame(yearEnd, "day");
		if (isMonthEndYearEnd) {
			const priorYearEndAtMonthEnd = yearEnd.clone().subtract(1, "year").endOf("month").startOf("day");
			return priorYearEndAtMonthEnd.add(1, "day");
		}
		return yearEnd.clone().subtract(1, "year").add(1, "day").startOf("day");
	},

	/**
	 * Splits transactions into those inside the fiscal period and those outside it —
	 * the hard cutoff. Rows after the year-end belong to the next fiscal year and rows
	 * before the period started belong to the prior one; neither may enter these books.
	 * Excluded rows are returned, never discarded, so they can be reported and kept on
	 * file for the neighbouring period.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - [{ date: "YYYY-MM-DD", ... }]
	 * @param {String} params.periodStart - "YYYY-MM-DD"
	 * @param {String} params.periodEnd - "YYYY-MM-DD"
	 * @returns {Object} { kept, excludedBefore, excludedAfter }
	 */
	filterTransactionsToPeriod({ transactions, periodStart, periodEnd }) {
		const kept = [];
		const excludedBefore = [];
		const excludedAfter = [];
		const undated = [];

		for (const transaction of transactions || []) {
			const transactionDate = String(transaction.date || "");
			if (!transactionDate) {
				undated.push(transaction);
				kept.push(transaction);
				continue;
			}
			if (periodStart && transactionDate < periodStart) {
				excludedBefore.push(transaction);
				continue;
			}
			if (periodEnd && transactionDate > periodEnd) {
				excludedAfter.push(transaction);
				continue;
			}
			kept.push(transaction);
		}

		return { kept, excludedBefore, excludedAfter, undated };
	},

	/**
	 * Builds the audit-trail block for the review report describing which imported
	 * transactions were held out of the books by the fiscal-period cutoff, so a
	 * reviewer can trace every excluded row (date, description, amount, account)
	 * instead of trusting a bare count.
	 *
	 * @param {Object} params
	 * @param {Object} [params.fiscalPeriod] - { periodStart, periodEnd }
	 * @param {Object} [params.periodExclusions] - { excludedBefore, excludedAfter }
	 * @returns {Object|null} { periodStart, periodEnd, excludedBeforeCount, excludedAfterCount, excludedTransactions } or null
	 */
	buildPeriodExclusionSummary({ fiscalPeriod, periodExclusions }) {
		if (!periodExclusions) return null;

		const excludedBefore = periodExclusions.excludedBefore || [];
		const excludedAfter = periodExclusions.excludedAfter || [];
		if (excludedBefore.length === 0 && excludedAfter.length === 0) return null;

		const describeExcludedRow = (transaction, sideOfPeriod) => ({
			date: transaction.date || null,
			description: transaction.description || "",
			amount: transaction.amount,
			accountName: transaction.accountName || "",
			sideOfPeriod,
		});

		const beforeRows = excludedBefore.map(transaction => describeExcludedRow(transaction, "before_period"));
		const afterRows = excludedAfter.map(transaction => describeExcludedRow(transaction, "after_period"));

		return {
			periodStart: (fiscalPeriod && fiscalPeriod.periodStart) || periodExclusions.periodStart || null,
			periodEnd: (fiscalPeriod && fiscalPeriod.periodEnd) || periodExclusions.periodEnd || null,
			excludedBeforeCount: excludedBefore.length,
			excludedAfterCount: excludedAfter.length,
			excludedTransactions: [...beforeRows, ...afterRows],
		};
	},

	/**
	 * Turns the verification modal's parses into the rows the statement registry
	 * expects, so verified statements are filed on the client's record at import.
	 * Only parses that actually produced data (ok/partial) and carry a file hash
	 * are registrable; a statement the registry already served back is skipped —
	 * re-filing it would only restate what is already on file.
	 *
	 * @param {Object} params
	 * @param {Array} params.parsesWithAccounts - [{ parse, accountLabel }]
	 * @returns {Array} [{ fileHash, fileName, accountLabel, parse }]
	 */
	buildStatementRegistrationRows({ parsesWithAccounts }) {
		const registrableParses = (parsesWithAccounts || []).filter(({ parse }) => {
			const producedData = parse.status === "ok" || parse.status === "partial";
			return producedData && parse.fileHash && !parse.fromRegistry;
		});

		const rows = registrableParses.map(({ parse, accountLabel }) => {
			const bankTieCheck = parse.integrity ? parse.integrity.opening_to_closing_check : null;
			const cardTieCheck = parse.integrity ? parse.integrity.balance_arithmetic_check : null;
			const tieCheck = bankTieCheck || cardTieCheck || null;
			const integrityPass = !!(tieCheck && tieCheck.ok);
			const integrityDelta = tieCheck && tieCheck.delta !== undefined ? tieCheck.delta : null;

			return {
				fileHash: parse.fileHash,
				fileName: parse.fileName,
				accountLabel: accountLabel || null,
				parse: {
					statementType: parse.statementType || "bank",
					bankName: parse.bankName || null,
					accountNumberMasked: parse.accountNumberMasked || null,
					periodStart: parse.periodStart || null,
					periodEnd: parse.periodEnd || null,
					openingBalance: parse.openingBalance,
					closingBalance: parse.closingBalance,
					integrityPass,
					integrityDelta,
					parseStatus: parse.status || "ok",
					source: parse.source || "pdf_import",
					transactions: (parse.transactions || []).map(transaction => ({
						date: transaction.date || null,
						description: transaction.description || "",
						amount: transaction.amount,
					})),
				},
			};
		});
		return rows;
	},

	/**
	 * Builds a registry row for transactions that did NOT come from a parsed PDF —
	 * a CSV or spreadsheet import, or a bank feed. Without this, rows the fiscal
	 * cutoff holds out of these books exist nowhere the next period can reach, and
	 * a statement that straddles the year-end loses its post-cutoff weeks entirely.
	 *
	 * These carry no printed balances to tie to, so they are filed with the source
	 * that produced them and no integrity claim, rather than a claim that was never
	 * checked. The identity hash is derived from the client, source and content so
	 * re-importing the same file does not duplicate it.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.source - e.g. "csv_import", "spreadsheet_import", "bank_feed"
	 * @param {String} params.sourceName - the file name or feed name, shown to the user
	 * @param {String} [params.accountLabel]
	 * @param {Array} params.transactions - [{ date, description, amount }]
	 * @returns {Object|null} a registration row, or null when there is nothing to file
	 */
	/**
	 * Names an import for the statement registry's file_name column, which holds
	 * 255 characters. A long list of file names is summarised rather than truncated
	 * mid-name, so the label always reads as a whole thought.
	 *
	 * @param {Object} params
	 * @param {Array<String>} [params.fileNames]
	 * @returns {String}
	 */
	buildImportSourceLabel({ fileNames }) {
		const names = (fileNames || []).filter(Boolean);
		if (names.length === 0) return "Imported transactions";

		const joinedNames = names.join(", ");
		if (joinedNames.length <= REGISTRY_FILE_NAME_LIMIT) return joinedNames;

		const summary = `${names[0]} and ${names.length - 1} more file${names.length - 1 === 1 ? "" : "s"}`;
		if (summary.length <= REGISTRY_FILE_NAME_LIMIT) return summary;
		return `${names.length} imported files`;
	},

	buildNonStatementRegistrationRow({ clientId, source, sourceName, accountLabel = null, transactions }) {
		const datedTransactions = (transactions || []).filter(transaction => !!transaction.date);
		if (datedTransactions.length === 0) return null;

		const contentFingerprint = datedTransactions
			.map(transaction => [transaction.date, transaction.description || "", Number(transaction.amount) || 0].join("|"))
			.join("~");
		const fileHash = UtilityService.buildStableHash({ text: `${clientId}|${source}|${sourceName}|${contentFingerprint}` });

		const sortedDates = datedTransactions.map(transaction => String(transaction.date)).sort();

		return {
			fileHash,
			fileName: sourceName,
			accountLabel,
			parse: {
				statementType: "bank",
				bankName: null,
				accountNumberMasked: null,
				periodStart: sortedDates[0],
				periodEnd: sortedDates[sortedDates.length - 1],
				openingBalance: null,
				closingBalance: null,
				integrityPass: false,
				integrityDelta: null,
				parseStatus: "unverified",
				source,
				transactions: datedTransactions.map(transaction => ({
					date: transaction.date,
					description: transaction.description || "",
					amount: transaction.amount,
				})),
			},
		};
	},

	/**
	 * The statements a registration put back on the shelf, with how many transactions came back.
	 *
	 * Removing a statement archives its transactions and re-importing the same file restores them
	 * on the server. The browser still holds its own copy of those rows as archived, and the next
	 * save would write that stale copy back over the restore while the freshly parsed rows are
	 * refused as duplicates. So a registration that restored anything has to be noticed, and the
	 * books re-read from the server before they are merged and saved.
	 *
	 * @param {Object} params
	 * @param {Array} params.registrationResults - as registerStatements reports them
	 * @returns {Array} [{ statementId, transactionCount }]
	 */
	listStatementsPutBackOnTheShelf({ registrationResults }) {
		const restored = (registrationResults || []).filter(result => result && result.restored && result.statementId);
		const putBack = restored.map(result => ({ statementId: result.statementId, transactionCount: Number(result.transactionCount) || 0 }));
		return putBack;
	},

	/**
	 * Files verified statements in the per-client registry so they persist across
	 * fiscal years and are never re-parsed (or re-paid-for) again.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} [params.wpfEngagementId] - lets each statement's bank-account identity resolve the same way the transactions themselves do
	 * @param {Array} params.statements - rows from buildStatementRegistrationRows
	 * @return {Promise<Object>} { results } or { error }
	 */
	async registerStatements({ clientId, wpfEngagementId, statements }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.registerStatements({ clientId, wpfEngagementId, statements }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Reads the client's statement registry — the statement shelf (no view), a
	 * date window of transactions (view "window"), or post-year-end subsequent
	 * activity (view "subsequent").
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} [params.view] - "window" | "subsequent" | undefined for the shelf
	 * @param {String} [params.windowStart]
	 * @param {String} [params.windowEnd]
	 * @param {String} [params.periodEnd]
	 * @param {Number} [params.windowDays]
	 * @return {Promise<Object>} view payload or { error }
	 */
	async readStatementRegistry({ clientId, view = undefined, windowStart = undefined, windowEnd = undefined, periodEnd = undefined, windowDays = undefined }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.readStatementRegistry({ clientId, view, windowStart, windowEnd, periodEnd, windowDays }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Pulls the fiscal period's slice of transactions from statements already on
	 * file for this client, leaving out statements that were imported in this
	 * session (their transactions are already in the books being generated).
	 * This is how a statement that straddled LAST year's cutoff contributes its
	 * early-days rows to THIS year without being uploaded again.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.periodStart - "YYYY-MM-DD"
	 * @param {String} params.periodEnd - "YYYY-MM-DD"
	 * @param {Array<String>} [params.sessionFileHashes] - hashes of statements imported this session
	 * @param {Array<Object>} [params.sessionTransactions] - this session's rows, so a registry row that matches one exactly (same date, description, amount) is dropped instead of duplicated
	 * @return {Promise<Object>} { transactions, statementsUsed, duplicateCount } or { error }; a row is taken at most once even when two filed sources both contain it, so an overlapping re-import can never enter the books twice
	 */
	async collectRegistryTransactionsForPeriod({ clientId, periodStart, periodEnd, sessionFileHashes = [], sessionTransactions = [] }) {
		const window = await this.readStatementRegistry({ clientId, view: "window", windowStart: periodStart, windowEnd: periodEnd });
		if (!window || window.error) return { error: (window && window.error) || "the statement registry could not be read" };

		const sessionHashes = new Set(sessionFileHashes);
		const statementsNotInSession = (window.statements || []).filter(statement => !sessionHashes.has(statement.fileHash));
		const statementIdsNotInSession = new Set(statementsNotInSession.map(statement => statement.id));

		const describeRowForMatching = row => [row.date || "", row.description || "", Number(row.amount) || 0].join("|");
		const sessionRowKeys = new Set(sessionTransactions.map(describeRowForMatching));

		let duplicateCount = 0;
		const transactions = [];
		for (const transaction of window.transactions || []) {
			if (!statementIdsNotInSession.has(transaction.statementId)) continue;

			const registryRow = {
				date: transaction.date,
				description: transaction.description,
				merchant: "",
				amount: transaction.amount,
				accountName: transaction.accountLabel || "Bank",
				bankAccountNumber: null,
				selected: false,
				classification: null,
				tax: "",
				fromRegistry: true,
				registryStatementId: transaction.statementId,
			};
			const rowKey = describeRowForMatching(registryRow);
			const isAlreadyInSession = sessionRowKeys.has(rowKey);
			if (isAlreadyInSession) {
				duplicateCount += 1;
				continue;
			}
			transactions.push(registryRow);
		}

		const usedStatementIds = new Set(transactions.map(transaction => transaction.registryStatementId));
		const statementsUsed = statementsNotInSession.filter(statement => usedStatementIds.has(statement.id));
		return { transactions, statementsUsed, duplicateCount };
	},

	/**
	 * Saves a manual / imported prior-year trial balance (opening balances +
	 * comparatives) for a bootstrap engagement. Server rejects an unbalanced TB.
	 * @param {Number} wpfEngagementId
	 * @param {String} source - manual | csv_tb | excel_tb | qbo_tb
	 * @param {Array} lines - [{ canonical_code, accountNumber, accountDescription, debit, credit }]
	 * @return {Promise<Object>}
	 */
	async saveOpeningBalances({ wpfEngagementId, source, lines }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.saveOpeningBalances({ wpfEngagementId, source, lines }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Loads saved prior-year figures for an engagement (split into debit/credit).
	 * @param {Number} wpfEngagementId
	 * @return {Promise<Object>}
	 */
	async getOpeningBalances({ wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getOpeningBalances({}, { wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Determines tax codes for a batch of transactions
	 * @param {String} province
	 * @param {Boolean} isGstRegistered
	 * @param {Array} transactions
	 * @return {Promise<Array>}
	 */
	async batchTaxCodes({ province, isGstRegistered, transactions, wpfEngagementId, industry }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.batchTaxCodes({ province, isGstRegistered, transactions, wpfEngagementId, industry }, {}, userData.authToken);
			let json = response.data;
			return json && json.results ? json.results : [];
		} catch (error) {
			return [];
		}
	},

	/**
	 * Retrieves tax rate for an account from the classification API.
	 *
	 * @param {String} account
	 * @param {String} province
	 * @param {String} industry
	 * @param {String} accountClass
	 * @returns {Promise<Object>} Tax rate data
	 */
	async getTaxRates({ clientId, account, province, industry, accountClass }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.classifyAccountsTax(
				{},
				{
					clientId,
					account,
					province,
					industry,
					...(accountClass ? { accountClass } : {}),
				},
				userData.authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Infers the client's industry from the entity name via the classification API.
	 *
	 * @param {String} entityName
	 * @returns {Promise<Object>} Inferred industry result, or null on error
	 */
	async inferIndustry({ entityName }) {

		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.inferIndustry({ entityName }, {}, userData.authToken);

			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Asks the API's AI which general ledger account names are real-world bank or credit
	 * card accounts, so the wizard can pre-select them for statement upload.
	 *
	 * @param {Number} clientId
	 * @param {Array<String>} accountNames - Account names from the imported general ledger
	 * @returns {Promise<Array<String>|null>} The account names the AI judged to be bank accounts, or null when the AI call failed
	 */
	async identifyLikelyBankAccounts({ clientId, accountNames }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.identifyBankAccountsFromGl({ clientId, accountNames }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			let bankAccountNames = response.data && Array.isArray(response.data.bankAccountNames) ? response.data.bankAccountNames : [];
			return { bankAccountNames };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Uploads CaseWare reference files for transaction classification.
	 *
	 * @param {String} clientId
	 * @param {Array} files
	 * @param {Array} transactions
	 * @returns {Promise<Object>} Server response with trial balance and classifications
	 */
	async uploadCasewareReference({ clientId, files, transactions, wpfEngagementId = null }) {

		try {
			await ClientService.fetchClient({ clientId });

			let formData = new FormData();

			for (let file of files) {
				formData.append("files", file);
			}

			if (transactions && transactions.length > 0) {
				formData.append("transactions", JSON.stringify(transactions));
			}

			if (wpfEngagementId) formData.append("wpfEngagementId", String(wpfEngagementId));

			let response = await authedFetch(`/tools/bookkeeping/upload_caseware_reference?clientId=${clientId}`, {
				method: "POST",
				body: formData,
				headers: {
					Accept: "application/json",
				},
			});

			return await response.json();
		} catch (error) {
			console.log(error);
		}

		return null;
	},


	/**
	 * Extracts transactions from one bank statement file, CSV or Excel. When the caller
	 * has a confirmed column map it is used as-is; otherwise the columns are detected from
	 * the file so a statement still imports without anyone mapping it by hand.
	 *
	 * @param {Object} params
	 * @param {File} params.file - The .csv or .xlsx statement
	 * @param {Number} [params.clientId] - Required only for Excel, which is read on the API
	 * @param {Object} [params.columnMap] - Confirmed { date, description, amount, moneyOut, moneyIn }
	 * @param {String} [params.bankAccountNumber] - Account number from CaseWare
	 * @param {String} [params.bankAccountDescription] - Account description from CaseWare
	 * @returns {Promise<Object>} { transactions, invalidCount } or { error } when the file could not be read
	 */
	async extractTransactionsFromStatementFile({ file, clientId = null, columnMap = null, bankAccountNumber = null, bankAccountDescription = null }) {
		try {
			const readResult = await this.readStatementGrid({ clientId, file });
			if (readResult.error) return { transactions: [], invalidCount: 0, error: readResult.error };

			const { hasHeadings, headers, dataRows } = this.detectStatementHeadings({ grid: readResult.grid });
			const confirmedOrGuessedColumnMap = columnMap || this.guessStatementColumnMap({ headers, dataRows, hasHeadings });
			const bankAccountName = bankAccountDescription || UtilityService.getFileNameWithoutExtension(file.name);

			const extracted = this.extractTransactionsFromStatementGrid({
				dataRows,
				columnMap: confirmedOrGuessedColumnMap,
				bankAccountName,
				bankAccountNumber,
			});
			return extracted;
		} catch (error) {
			console.log(error);
			return { transactions: [], invalidCount: 0, error: COULD_NOT_READ_STATEMENT };
		}
	},

	/**
	 * Reads a statement and returns its first deposit (positive amount) and first payment
	 * (negative amount) so the user can confirm the file's sign convention. Parses through
	 * the same path as the real import, with the same column map, so the preview matches.
	 *
	 * @param {Object} params
	 * @param {File} params.file - The .csv or .xlsx statement
	 * @param {Number} [params.clientId] - Required only for Excel, which is read on the API
	 * @param {Object} [params.columnMap] - Confirmed column map, when the user has set one
	 * @returns {Promise<Object>} { deposit, payment } — each { description, amount } or null
	 */
	async sampleSignConvention({ file, clientId = null, columnMap = null }) {
		let result = await this.extractTransactionsFromStatementFile({ file, clientId, columnMap });
		let transactions = result && result.transactions ? result.transactions : [];

		let firstDeposit = transactions.find(transaction => hasNonZeroAmount(transaction) && transaction.amount > 0);
		let firstPayment = transactions.find(transaction => hasNonZeroAmount(transaction) && transaction.amount < 0);

		return { deposit: toSignSample(firstDeposit), payment: toSignSample(firstPayment) };
	},

	/**
	 * Flips every transaction amount when the file's convention is inverted (positive
	 * amounts represent money out); otherwise returns the transactions unchanged.
	 *
	 * @param {Array} transactions
	 * @param {Boolean} inverted
	 * @returns {Array}
	 */
	applySignConvention({ transactions, inverted }) {
		if (!inverted) return transactions;
		const flippedTransactions = transactions.map(flipTransactionAmount);
		return flippedTransactions;
	},

	/**
	 * Builds a general ledger reference from mapped CSV rows: the {account, description}
	 * pairs the NLP matcher trains on, the full per-row entries the reviewer can browse,
	 * the distinct account list, and — when the tax and debit/credit columns are mapped —
	 * a default tax code per account. That tax rate is reverse-engineered straight from the
	 * general ledger (each account's tax amount as a percentage of its net amount), so the
	 * general ledger, not the province, is the source of the prior-year tax rates.
	 *
	 * Entry amounts use the same cash-flow convention as the bank-statement transactions
	 * (money in positive): credits (revenue) are positive and debits (expenses) are
	 * negative, so the prior-year figures line up sign-for-sign with the current year.
	 *
	 * @param {Array<Array<string>>} rows - CSV data rows (header row already removed)
	 * @param {number} accountColumnIndex
	 * @param {number} descriptionColumnIndex
	 * @param {number} [taxColumnIndex=-1]
	 * @param {number} [debitColumnIndex=-1]
	 * @param {number} [creditColumnIndex=-1]
	 * @param {number} [dateColumnIndex=-1]
	 * @param {number} [accountNumberColumnIndex=-1] - when mapped, the account number comes
	 *   straight from this column; otherwise it falls back to the leading digits of the account name
	 * @returns {Object} { generalLedgerReference: [{ account, description }], generalLedgerEntries: [{ date, account, accountNumber, description, amount, tax }], accountList: string[], accountTaxes: { [account]: taxCode } }
	 */
	buildGeneralLedgerReference({ rows, accountColumnIndex, descriptionColumnIndex, taxColumnIndex = -1, debitColumnIndex = -1, creditColumnIndex = -1, dateColumnIndex = -1, accountNumberColumnIndex = -1 }) {
		let generalLedgerReference = [];
		let generalLedgerEntries = [];
		let seenPairs = new Set();
		let accountList = [];
		let seenAccounts = new Set();
		let accountTotals = {};

		for (let row of rows) {
			let account = UtilityService.toTrimmedText(row[accountColumnIndex]);
			let description = UtilityService.toTrimmedText(row[descriptionColumnIndex]);
			if (!account) continue;

			if (!seenAccounts.has(account)) {
				seenAccounts.add(account);
				accountList.push(account);
			}

			let debitAmount = debitColumnIndex >= 0 ? UtilityService.parseAmount(row[debitColumnIndex]) : 0;
			let creditAmount = creditColumnIndex >= 0 ? UtilityService.parseAmount(row[creditColumnIndex]) : 0;
			let cashFlowAmount = Math.abs(creditAmount) - Math.abs(debitAmount);
			let taxValue = taxColumnIndex >= 0 ? UtilityService.parseAmount(row[taxColumnIndex]) : 0;
			let entryDate = dateColumnIndex >= 0 ? UtilityService.toTrimmedText(row[dateColumnIndex]) : "";
			let mappedAccountNumber = accountNumberColumnIndex >= 0 ? UtilityService.toTrimmedText(row[accountNumberColumnIndex]) : "";
			let accountNumber = mappedAccountNumber || UtilityService.getLeadingNumber(account);

			if (taxColumnIndex >= 0) {
				let netAmount = Math.abs(debitAmount) || Math.abs(creditAmount);
				if (!accountTotals[account]) accountTotals[account] = { net: 0, tax: 0 };
				accountTotals[account].net += netAmount;
				accountTotals[account].tax += Math.abs(taxValue);
			}

			generalLedgerEntries.push({ date: entryDate, account, accountNumber, description, amount: cashFlowAmount, tax: Math.abs(taxValue) });

			if (!description) continue;
			let pairKey = account + "||" + description;
			if (seenPairs.has(pairKey)) continue;
			seenPairs.add(pairKey);
			generalLedgerReference.push({ account, description });
		}

		const accountTaxes = deriveAccountTaxes({ accountTotals });
		return { generalLedgerReference, generalLedgerEntries, accountList, accountTaxes };
	},

	/**
	 * Rebuilds the matcher's view of a general ledger from the entries stored on the engagement,
	 * so statements added after the ledger was imported are matched against it the same way the
	 * first import was. The ledger file itself is not kept, but every entry is, and the matcher
	 * only ever looked at each entry's account and wording.
	 *
	 * @param {Object} params
	 * @param {Array} params.entries - [{ account, description }] as the engagement stores them
	 * @returns {Object} { generalLedgerReference: [{ account, description }], accountList: string[] }
	 */
	buildGeneralLedgerReferenceFromEntries({ entries }) {
		const generalLedgerReference = [];
		const accountList = [];
		const seenPairs = new Set();
		const seenAccounts = new Set();

		for (const entry of entries || []) {
			const account = UtilityService.toTrimmedText(entry.account);
			const description = UtilityService.toTrimmedText(entry.description);
			if (!account) continue;

			if (!seenAccounts.has(account)) {
				seenAccounts.add(account);
				accountList.push(account);
			}

			const pairKey = account + "|" + description;
			if (seenPairs.has(pairKey)) continue;
			seenPairs.add(pairKey);
			generalLedgerReference.push({ account, description });
		}

		return { generalLedgerReference, accountList };
	},

	/**
	 * Categorizes transactions by fuzzy-matching their descriptions against a general
	 * ledger reference, reusing the CaseWare NLP matcher on the API.
	 *
	 * @param {String} clientId
	 * @param {Array} generalLedgerReference - [{ account, description }]
	 * @param {Array} transactions - [{ description }]
	 * @returns {Promise<Array>} categorized transactions (index-aligned to input), or []
	 */
	async categorizeTransactionsFromGeneralLedger({ clientId, generalLedgerReference, transactions }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.categorizeFromGl({ clientId, generalLedgerReference, transactions }, {}, userData.authToken);
			let json = response.data;
			let result = json && json.data ? json.data : json;
			return result && result.categorizedTransactions ? result.categorizedTransactions : [];
		} catch (error) {
			console.log(error);
			return [];
		}
	},

	/**
	 * Builds a custom tax option like "Landscaping 7.5%" from a name and rate, enforcing
	 * the constraints the rest of the tax flow depends on: the rate must be the first
	 * number in the string (so the name can't contain digits) and the whole code must fit
	 * the 20-character tax_code column.
	 *
	 * @param {String} name
	 * @param {String|Number} ratePercent
	 * @returns {Object} { option } on success, { error } with a user-facing message otherwise
	 */
	buildCustomTaxRateOption({ name, ratePercent }) {
		const cleanName = UtilityService.toTrimmedText(name);
		const rate = parseFloat(ratePercent);
		const nameCharacters = [...cleanName];
		const nameContainsDigit = nameCharacters.some(isDigitCharacter);

		if (!cleanName) return { error: "Give the rate a name." };
		if (nameContainsDigit) return { error: "The name can't contain numbers — the rate is read from the first number in the code." };
		if (isNaN(rate) || rate < 0 || rate > 100) return { error: "Enter a rate between 0 and 100." };

		const option = `${cleanName} ${rate}%`;
		if (option.length > 20) return { error: "Keep the name shorter — tax codes are limited to 20 characters." };

		return { option };
	},

	/**
	 * Splits a custom tax option like "Landscaping 7.5%" back into its name and rate,
	 * for loading into the edit inputs.
	 *
	 * @param {String} option
	 * @returns {Object} { name, ratePercent }
	 */
	parseCustomTaxRateOption({ option }) {
		const rawOption = UtilityService.toTrimmedText(option);
		const words = rawOption.split(" ");
		const lastWord = words[words.length - 1] || "";
		const lastWordIsTheRate = lastWord.endsWith("%");
		const wordsWithoutRate = words.slice(0, -1);

		const ratePercent = lastWordIsTheRate ? lastWord.slice(0, -1) : "";
		const name = lastWordIsTheRate ? wordsWithoutRate.join(" ") : words.join(" ");
		return { name, ratePercent };
	},

	/**
	 * Applies one edit to every row a modal edit follows to, in a single pass.
	 *
	 * Changing an account and changing a tax from the classify modal are the same act — edit the
	 * row you opened, and every row you selected alongside it takes the same change — so they run
	 * through one transform rather than a copy each. The copies had already drifted: the account
	 * edit cascaded to the whole selection and the tax edit reached only the clicked row, which is
	 * the bug this removes. What differs between the two is the fields being set, which the caller
	 * supplies as buildPatchForRow; everything else — which rows, the clicked row that the modal
	 * still shows — is the same and lives here.
	 *
	 * Rows are matched by their stable identity, not by object reference. A save reloads the table
	 * and rebuilds every row as a new object, so a modal that stays open holds a row the array no
	 * longer contains; matching by reference then finds nothing and a second edit silently does
	 * nothing. The identity survives the reload, so a person can keep changing the same selection.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - the whole table
	 * @param {Set} params.identitiesToChange - the identities this edit follows to
	 * @param {Function} params.buildPatchForRow - row => the fields to merge onto that row
	 * @param {String} params.clickedIdentity - the identity of the row the person actually edited
	 * @returns {Object} { updatedTransactions, updatedClickedTransaction }
	 */
	applyPatchToRows({ transactions, identitiesToChange, buildPatchForRow, clickedIdentity }) {
		let updatedClickedTransaction = null;
		const updatedTransactions = transactions.map(row => {
			const rowIdentity = this.getTransactionIdentity({ transaction: row });
			if (!identitiesToChange.has(rowIdentity)) return row;
			const changed = { ...row, ...buildPatchForRow(row) };
			if (rowIdentity === clickedIdentity) updatedClickedTransaction = changed;
			return changed;
		});
		return { updatedTransactions, updatedClickedTransaction };
	},

	/**
	 * Replaces one tax code with another on every transaction that carries it;
	 * other transactions are untouched.
	 *
	 * @param {Array} transactions
	 * @param {String} oldTaxCode
	 * @param {String} newTaxCode
	 * @returns {Array}
	 */
	replaceTaxCodeOnTransactions({ transactions, oldTaxCode, newTaxCode }) {
		const updatedTransactions = transactions.map(transaction => (transaction.tax === oldTaxCode ? { ...transaction, tax: newTaxCode } : transaction));
		return updatedTransactions;
	},

	/**
	 * Reads the client's saved custom tax rates.
	 *
	 * @param {String} clientId
	 * @returns {Array<String>} custom tax options like "Landscaping 7.5%"
	 */
	getCustomTaxRates({ clientId }) {
		const storageKey = `bookkeeping_custom_tax_rates_${clientId}`;
		const storedRates = localStorage.getItem(storageKey) || "[]";
		const customTaxRates = JSON.parse(storedRates);
		return customTaxRates;
	},

	/**
	 * Persists the client's custom tax rates.
	 *
	 * @param {String} clientId
	 * @param {Array<String>} customTaxRates
	 */
	saveCustomTaxRates({ clientId, customTaxRates }) {
		const storageKey = `bookkeeping_custom_tax_rates_${clientId}`;
		const serializedRates = JSON.stringify(customTaxRates);
		localStorage.setItem(storageKey, serializedRates);
	},

	/**
	 * Reads the preparer's saved column layout for a client's transaction table: the
	 * widths they dragged and the columns they hid. A display preference only — it never
	 * changes what is imported, classified or reported.
	 *
	 * @param {Object} params
	 * @param {String|Number} params.clientId
	 * @returns {Object} { columnWidths, hiddenColumns }
	 */
	getTableColumnPreferences({ clientId }) {
		const storageKey = `bookkeeping_table_columns_${clientId}`;
		const storedPreferences = localStorage.getItem(storageKey);
		if (!storedPreferences) return { columnWidths: {}, hiddenColumns: [] };

		try {
			const preferences = JSON.parse(storedPreferences);
			const columnWidths = preferences.columnWidths || {};
			const hiddenColumns = preferences.hiddenColumns || [];
			return { columnWidths, hiddenColumns };
		} catch (error) {
			return { columnWidths: {}, hiddenColumns: [] };
		}
	},

	/**
	 * Saves the preparer's column layout for a client's transaction table.
	 *
	 * @param {Object} params
	 * @param {String|Number} params.clientId
	 * @param {Object} params.columnWidths - { [columnKey]: widthInPixels }
	 * @param {Array} params.hiddenColumns - Column keys the preparer has hidden
	 * @returns {void}
	 */
	saveTableColumnPreferences({ clientId, columnWidths, hiddenColumns }) {
		const storageKey = `bookkeeping_table_columns_${clientId}`;
		const serializedPreferences = JSON.stringify({ columnWidths, hiddenColumns });
		localStorage.setItem(storageKey, serializedPreferences);
	},

	/**
	 * Builds the CSS grid track list for the transaction table from the columns the
	 * preparer can currently see, so the header, every data row and the totals row stay
	 * aligned. Hidden columns contribute no track — their cells are hidden in CSS, which
	 * keeps the track count and the visible cell count in step.
	 *
	 * @param {Object} params
	 * @param {Array} params.columns - [{ key, width }] in display order
	 * @param {Object} params.columnWidths - Preparer overrides, { [columnKey]: widthInPixels }
	 * @param {Array} params.hiddenColumns - Column keys to leave out
	 * @param {Number} params.leadingWidth - Width of the leading checkbox column
	 * @returns {String} e.g. "36px 100px 240px 108px minmax(0, 1fr)"
	 */
	buildTableGridTemplate({ columns, columnWidths, hiddenColumns, leadingWidth }) {
		const visibleColumns = columns.filter(column => !hiddenColumns.includes(column.key));
		const widths = visibleColumns.map(column => {
			const overriddenWidth = columnWidths[column.key];
			const width = overriddenWidth || column.width;
			return `${width}px`;
		});
		const trailingSpacer = "minmax(0, 1fr)";
		const tracks = [`${leadingWidth}px`, ...widths, trailingSpacer];
		return tracks.join(" ");
	},

	/**
	 * Reads the client's keyword→tax rules. A per-client convenience the accountant
	 * records: any transaction whose description or merchant contains the keyword gets
	 * the rule's tax. The result is always reviewable and overridable per transaction.
	 *
	 * @param {String} clientId
	 * @returns {Array<Object>} rules like { keyword: "usa", tax: "Exempt" }
	 */
	getTaxRules({ clientId }) {
		const storageKey = `bookkeeping_tax_rules_${clientId}`;
		const storedRules = localStorage.getItem(storageKey) || "[]";
		const taxRules = JSON.parse(storedRules);
		return taxRules;
	},

	/**
	 * Persists the client's keyword→tax rules.
	 *
	 * @param {String} clientId
	 * @param {Array<Object>} taxRules - each { keyword, tax }
	 */
	saveTaxRules({ clientId, taxRules }) {
		const storageKey = `bookkeeping_tax_rules_${clientId}`;
		const serializedRules = JSON.stringify(taxRules);
		localStorage.setItem(storageKey, serializedRules);
	},

	/**
	 * True when `phrase` appears as whole word(s) in `text`. Compares token sequences,
	 * so "usa" matches "paid to usa inc" but not "usage", and multi-word keywords like
	 * "new york" match only when both words appear adjacent and whole.
	 *
	 * @param {Object} params
	 * @param {String} params.text
	 * @param {String} params.phrase
	 * @returns {Boolean}
	 */
	containsWholePhrase({ text, phrase }) {
		const textTokens = UtilityService.toWordTokens({ text });
		const phraseTokens = UtilityService.toWordTokens({ text: phrase });
		if (phraseTokens.length === 0) return false;

		const lastPossibleStart = textTokens.length - phraseTokens.length;
		for (let startIndex = 0; startIndex <= lastPossibleStart; startIndex++) {
			let everyTokenMatches = true;
			for (let offset = 0; offset < phraseTokens.length; offset++) {
				if (textTokens[startIndex + offset] !== phraseTokens[offset]) {
					everyTokenMatches = false;
					break;
				}
			}
			if (everyTokenMatches) return true;
		}
		return false;
	},

	/**
	 * Finds the tax a keyword rule assigns to a transaction: the first rule whose keyword
	 * appears as a whole word in the description or merchant. Returns null when none match.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction - needs description and/or merchant
	 * @param {Array<Object>} params.taxRules - each { keyword, tax }
	 * @returns {String|null} the tax to apply, or null
	 */
	findTaxForTransaction({ transaction, taxRules }) {
		const haystack = `${transaction.description || ""} ${transaction.merchant || ""}`;
		const matchingRule = (taxRules || []).find(rule => {
			const keyword = (rule.keyword || "").trim();
			if (!keyword) return false;
			return this.containsWholePhrase({ text: haystack, phrase: keyword });
		});
		return matchingRule ? matchingRule.tax : null;
	},

	/**
	 * Whether a person has decided this row's tax, which is the one test every automatic tax writer
	 * must ask before it touches a row. A row that carries a tax has one to keep; a row a person set
	 * deliberately — recorded as tax_method "manual", including a deliberate no tax that reads as an
	 * empty string — has been decided even though the field is empty. Written once so the three fill
	 * paths cannot drift: if "decided" ever means more, it changes here, not in three copies where
	 * one would be forgotten and quietly re-open the overwrite this is here to prevent.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Boolean}
	 */
	aPersonHasDecidedTheTax({ transaction }) {
		return !!transaction.tax || transaction.tax_method === "manual";
	},

	/**
	 * The tax and its provenance after a person changes a row's account.
	 *
	 * Changing an account is not a decision about tax, so it must not overwrite one a person made.
	 * A tax the person chose in the same action is theirs and manual. Otherwise, if they had already
	 * decided this row's tax — a rate they set, or a deliberate no tax — it stays exactly as it was,
	 * provenance and all. Only when no person has decided does the new account's rule tax apply, and
	 * it is recorded as coming from a rule so a later correction may still adjust it. The account
	 * change made from the single-row modal and the one made in bulk answer this the same way,
	 * because they ask it here rather than each keeping its own version that drifted apart.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @param {String|null} params.chosenTax - a tax the person picked in this same action, or null
	 * @param {String} params.ruleTax - the tax the new account's rules suggest
	 * @returns {Object} { tax, tax_method }
	 */
	taxAfterAnAccountChange({ transaction, chosenTax, ruleTax }) {
		if (chosenTax !== null && chosenTax !== undefined) return { tax: chosenTax, tax_method: "manual" };
		if (this.aPersonHasDecidedTheTax({ transaction })) return { tax: transaction.tax, tax_method: transaction.tax_method || null };
		return { tax: ruleTax, tax_method: "rule" };
	},

	/**
	 * Applies the keyword tax rules to transactions no one has decided yet, leaving any row that
	 * carries a tax or that a person deliberately set — including deliberately to no tax — untouched,
	 * so a manual choice is never overwritten.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array<Object>} params.taxRules
	 * @returns {Object} { updatedTransactions, changedCount }
	 */
	applyTaxRulesToUntaxed({ transactions, taxRules }) {
		let changedCount = 0;
		const updatedTransactions = (transactions || []).map(transaction => {
			if (this.aPersonHasDecidedTheTax({ transaction })) return transaction;
			const ruleTax = this.findTaxForTransaction({ transaction, taxRules });
			if (!ruleTax) return transaction;
			changedCount++;
			return { ...transaction, tax: ruleTax, tax_method: "rule" };
		});
		return { updatedTransactions, changedCount };
	},

	/**
	 * Fetches the canonical trial balance the AFS is built from, for the client's current
	 * transactions — the accurate, tied-out trial balance, not a frontend approximation.
	 * Returns { trialBalance, metadata } or null on failure.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Array} params.transactions
	 * @returns {Promise<Object|null>} { trialBalance, metadata } or null
	 */
	async getTrialBalance({ clientId, transactions, priorEntries }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getBookkeepingTrialBalance({ clientId, transactions, priorEntries }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	async getGlOpeningBalanceLines({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getGlOpeningBalanceLines({ clientId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Converts imported prior-year GL entries (each already carrying a canonical_code and
	 * a credit-positive cash-flow amount) into canonical postings the trial-balance pipeline
	 * understands: a positive amount is a credit, a negative amount a debit.
	 *
	 * @param {Object} params
	 * @param {Array} params.generalLedgerEntries - each { canonical_code, amount }
	 * @returns {Array<Object>} postings like { canonical_code, debit_credit, amount }
	 */
	buildPriorTrialBalanceEntries({ generalLedgerEntries }) {
		return (generalLedgerEntries || []).map(entry => {
			const cashFlowAmount = Number(entry.amount) || 0;
			const debitCredit = cashFlowAmount >= 0 ? "CREDIT" : "DEBIT";
			return { canonical_code: entry.canonical_code || null, debit_credit: debitCredit, amount: Math.abs(cashFlowAmount) };
		});
	},

	/**
	 * Shapes the canonical trial balance into sorted comparative rows: each account's
	 * current-year net balance beside its prior-year net balance (the same figures the AFS
	 * comparatives use). Includes accounts present in either year and drops rows with no
	 * balance in either.
	 *
	 * @param {Object} params
	 * @param {Object} params.trialBalance - canonical_code → current-year entry
	 * @param {Object} params.priorTrialBalance - canonical_code → prior-year entry
	 * @returns {Array<Object>} rows like { canonicalCode, account, gifiCode, current, prior }
	 */
	buildTrialBalanceRows({ trialBalance, priorTrialBalance }) {
		const currentByCode = trialBalance || {};
		const priorByCode = priorTrialBalance || {};
		const currentCodes = Object.keys(currentByCode);
		const priorCodes = Object.keys(priorByCode);
		const uniqueCodes = [...new Set([...currentCodes, ...priorCodes])];

		const rows = uniqueCodes.map(canonicalCode => {
			const currentEntry = currentByCode[canonicalCode];
			const priorEntry = priorByCode[canonicalCode];
			const current = currentEntry ? (currentEntry.net_balance || 0) : 0;
			const prior = priorEntry ? (priorEntry.net_balance || 0) : 0;
			const account = (currentEntry && currentEntry.display_name) || (priorEntry && priorEntry.display_name) || canonicalCode;
			const gifiCode = (currentEntry && currentEntry.gifi_code) || (priorEntry && priorEntry.gifi_code) || "";
			return { canonicalCode, account, gifiCode, current, prior };
		});

		const rowsWithActivity = rows.filter(row => row.current !== 0 || row.prior !== 0);
		const sortedRows = rowsWithActivity.sort((first, second) => first.account.localeCompare(second.account));
		return sortedRows;
	},

	/**
	 * Returns just the account number embedded at the start of an account name (the leading
	 * digits of "6250 Office Supplies"), or an empty string when the name has no leading
	 * number. It never falls back to the name itself: a name is not a number, and a name sent
	 * as one is how a chart came to hold "1000 Scotia bank #27" as an account number.
	 *
	 * @param {Object} params
	 * @param {String} params.accountName
	 * @returns {String}
	 */
	getLeadingAccountNumber({ accountName }) {
		const leadingNumber = UtilityService.getLeadingNumber(accountName);
		return leadingNumber;
	},

	/**
	 * The chart account each detected bank account name already refers to, for the names the
	 * statements screen starts with. A name that names no chart account gets no entry, and the
	 * screen asks for it.
	 *
	 * @param {Object} params
	 * @param {Array} params.clientChart - rows from getClientCoA
	 * @param {Array<String>} params.names
	 * @returns {Object} chart account id by name, for the names that resolved
	 */
	resolvePostsToAccountIdsFromNames({ clientChart, names }) {
		const resolved = {};
		for (const accountName of names || []) {
			const chartRow = this.findChartRowForAccountName({ clientChart, accountName });
			if (chartRow && chartRow.id) resolved[accountName] = chartRow.id;
		}
		return resolved;
	},

	/**
	 * Finds the client chart row an account name refers to: the account number the name
	 * starts with, then an exact name, and nothing else.
	 *
	 * This deliberately mirrors accountIdentityService.resolveAccountIdsByLabel on the API.
	 * The two must stay in step, or the browser and the database will disagree about which
	 * account a row belongs to.
	 *
	 * @param {Object} params
	 * @param {Array} params.clientChart - rows from getClientCoA
	 * @param {String} params.accountName
	 * @returns {Object|null} the chart row, or null when the name refers to no account
	 */
	findChartRowForAccountName({ clientChart, accountName }) {
		const chartRows = clientChart || [];
		if (!accountName || chartRows.length === 0) return null;

		const accountNumber = this.getLeadingAccountNumber({ accountName });
		if (accountNumber) {
			const rowWithThatNumber = chartRows.find(account => String(account.accountNumber || "").trim() === accountNumber);
			if (rowWithThatNumber) return rowWithThatNumber;
		}

		const plainly = text => String(text || "").trim().toLowerCase();
		const rowWithThatName = chartRows.find(account => plainly(account.accountName) === plainly(accountName));
		return rowWithThatName || null;
	},

	/**
	 * The label for one account: its number and name, "1000 Cash", or the name alone when it has no
	 * number. The one place an account is turned into words, so the same account reads the same way
	 * on every screen, in the CSV and in the booking confirmation.
	 *
	 * Two shapes arrive here, because the API serialises a chart account's name under two keys —
	 * `accountName` from the chart routes (and every QuickBooks account), `accountDescription` from
	 * the QuickBooks pull, opening balances and adjusting entry lines. Both are the account's one
	 * name; this reads whichever the row carries. The number holds a number and nothing else, and the
	 * name holds the name and nothing else — both are normalised when the account is written — so
	 * composing is unconditional here.
	 *
	 * Mirrors coaMappingService.describeAccountName on the API, which labels the same accounts
	 * on the transactions endpoint. The two must agree or the same account reads two ways.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.account - { accountNumber, accountName } or { accountNumber, accountDescription }
	 * @returns {String|null} null for no account
	 */
	describeChartAccount({ account }) {
		if (!account) return null;
		const accountNumber = String(account.accountNumber || "").trim();
		const accountName = String(account.accountName || account.accountDescription || "").trim();

		if (!accountNumber) return accountName;
		if (!accountName) return accountNumber;

		return `${accountNumber} ${accountName}`;
	},

	/**
	 * Lists every account the client has, not only the ones the open file posts to.
	 *
	 * A balance-sheet account carries a balance without any transaction classified to it —
	 * 2150 Visa - RBC, 2400 Income taxes payable — so a list scraped from transactions cannot
	 * show it at all, and no lookup fix reaches that. The chart is the record of which
	 * accounts exist; transactions only say which were used.
	 *
	 * A chart row's number is only prefixed onto its name when it really is a number. Some rows
	 * carry the account NAME in account_number — "Interest Income", or "Software Subscriptio"
	 * truncated to the column width — because the importer falls back to the name when a GL has
	 * no numbers. Prefixing those produced "Interest Income Interest Income", which then matched
	 * no chart row and reported a mapped account as unmapped.
	 *
	 * Saved transactions can only name a chart account — account_id is NOT NULL and a foreign
	 * key. But a person can add a classification in the tool and start using it before it is
	 * ever mapped, and that account lives only in the browser until they save. So the chart is
	 * unioned with the names in play, and the two are reconciled.
	 *
	 * Names are deduplicated by the chart row each resolves to, not by the row's id (absent on
	 * some payloads, which would collapse the list into one entry) and not by the string (the
	 * chart's spelling and a transaction's label are two spellings of one account — "2150 Visa
	 * -RBC" and "2150 - Visa -RBC" — which would list it twice, once mapped and once not). A
	 * name resolving to no chart row keeps its own entry: that is the unmapped account, and it
	 * is the one the AFS gate has to stop for.
	 *
	 * @param {Object} params
	 * @param {Array} params.clientChart - rows from getClientCoA
	 * @param {Array<String>} params.usedClassificationNames - names the open file classifies to
	 * @returns {Array<String>} every account name, sorted, one entry per account
	 */
	listEveryClientAccountName({ clientChart, usedClassificationNames }) {
		const chartRows = clientChart || [];
		const namesOnTheChart = chartRows.map(account => this.describeChartAccount({ account })).filter(Boolean);

		const chosenNameForChartRow = new Map();
		const namesWithNoChartRow = [];

		for (const accountName of [...namesOnTheChart, ...(usedClassificationNames || [])]) {
			const chartRow = this.findChartRowForAccountName({ clientChart: chartRows, accountName });
			if (!chartRow) {
				if (!namesWithNoChartRow.includes(accountName)) namesWithNoChartRow.push(accountName);
				continue;
			}
			if (!chosenNameForChartRow.has(chartRow)) chosenNameForChartRow.set(chartRow, accountName);
		}

		const everyAccountName = [...chosenNameForChartRow.values(), ...namesWithNoChartRow];
		return everyAccountName.sort();
	},

	/**
	 * Renames an account across all transactions: every field that carries the old
	 * account name (current, GL label, CaseWare/GL source, AI source) is updated so
	 * the rename doesn't register as a manual reclassification.
	 *
	 * @param {Array} transactions
	 * @param {String} oldName
	 * @param {String} newName
	 * @returns {Array}
	 */
	renameClassificationOnTransactions({ transactions, oldName, newName }) {
		let renameIfMatching = value => (value === oldName ? newName : value);
		const renamedTransactions = transactions.map(transaction => ({
			...transaction,
			classification: renameIfMatching(transaction.classification),
			classification_label: renameIfMatching(transaction.classification_label),
			cwClassification: renameIfMatching(transaction.cwClassification),
			aiClassification: renameIfMatching(transaction.aiClassification),
		}));
		return renamedTransactions;
	},

	/**
	 * Maps every transaction classified under an account name to a canonical FS account
	 * (canonical code, display label, GIFI); other transactions are untouched.
	 *
	 * @param {Array} transactions
	 * @param {String} classificationName
	 * @param {Object} canonicalNode - { canonical_code, display_name, gifi_code }
	 * @returns {Array}
	 */
	remapTransactionsToCanonical({ transactions, classificationName, canonicalNode }) {
		const remappedTransactions = transactions.map(transaction => {
			if (transaction.classification !== classificationName) return transaction;
			return {
				...transaction,
				canonical_code: canonicalNode.canonical_code,
				canonical_label: canonicalNode.display_name || canonicalNode.name || canonicalNode.canonical_code,
				gifi_code: canonicalNode.gifi_code || null,
			};
		});
		return remappedTransactions;
	},

	/**
	 * Reads a general ledger CSV and returns its headers, data rows, and a best-guess
	 * column map. Account and description drive the NLP match; date/debit/credit/tax are
	 * captured so the user can confirm the mapping of their own export.
	 *
	 * @param {File} file
	 * @returns {Promise<Object|null>} { headers, rows, columnMap } or null when there are no data rows
	 */
	async readGeneralLedgerCsv({ file }) {
		let text = await file.text();
		let matrix = UtilityService.parseCsv(text);
		if (matrix.length < 2) return null;

		let headers = matrix[0];
		let rows = matrix.slice(1);
		let columnMap = {
			account: UtilityService.guessColumnIndex(headers, ["account", "acct", "gl", "category"]),
			description: UtilityService.guessColumnIndex(headers, ["description", "memo", "payee", "name", "detail"]),
			date: UtilityService.guessColumnIndex(headers, ["date"]),
			debit: UtilityService.guessColumnIndex(headers, ["debit", "dr"]),
			credit: UtilityService.guessColumnIndex(headers, ["credit", "cr"]),
			tax: UtilityService.guessColumnIndex(headers, ["tax", "hst", "gst"]),
		};

		return { headers, rows, columnMap };
	},

	/**
	 * Reads a bank statement into a grid of text cells. CSVs are parsed in the browser;
	 * Excel statements are read on the API, where the spreadsheet library lives. The grid
	 * still holds every row, including any heading row, so the caller can decide what the
	 * first row is.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {File} params.file - A .csv or .xlsx bank statement
	 * @returns {Promise<Object>} { grid } of string arrays, or { error } describing why it could not be read
	 */
	async readStatementGrid({ clientId, file }) {
		const isExcelStatement = UtilityService.fileNameEndsWith({ fileName: file.name, suffix: ".xlsx" });

		if (!isExcelStatement) {
			const text = await file.text();
			const grid = UtilityService.parseCsv(text);
			return { grid };
		}

		try {
			const formData = new FormData();
			formData.append("files", file);
			const response = await authedFetch(`/tools/bookkeeping/parse_spreadsheet?clientId=${clientId}`, { method: "POST", body: formData });
			const answer = await readJsonAnswer({ response });
			if (answer.error) return answer;
			return { grid: answer.rows || [] };
		} catch (error) {
			console.log(error);
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Decides whether a statement's first row holds column headings rather than a
	 * transaction, by reading the cells rather than matching known heading words: a
	 * heading row has no cell that reads as a date and none that reads as an amount,
	 * while a later row does. Statements with no heading row keep every row as data and
	 * get blank headings, which the column picker shows as "Column 1", "Column 2", ….
	 *
	 * @param {Object} params
	 * @param {Array} params.grid - Rows of text cells
	 * @returns {Object} { hasHeadings, headers, dataRows }
	 */
	detectStatementHeadings({ grid }) {
		const rows = grid || [];
		if (rows.length === 0) return { hasHeadings: false, headers: [], dataRows: [] };

		const firstRow = rows[0];
		const laterRows = rows.slice(1);
		const cellIsDate = cell => normalizeDate(cell) !== null;
		const cellIsAmount = cell => !isNaN(normalizeAmount(cell));

		const firstRowHasNoDate = !firstRow.some(cellIsDate);
		const firstRowHasNoAmount = !firstRow.some(cellIsAmount);
		const firstRowReadsAsLabels = firstRowHasNoDate && firstRowHasNoAmount;
		const someLaterRowReadsAsData = laterRows.some(row => row.some(cellIsDate) || row.some(cellIsAmount));
		const hasHeadings = firstRowReadsAsLabels && someLaterRowReadsAsData;

		const blankHeadings = firstRow.map(() => "");
		const headers = hasHeadings ? firstRow : blankHeadings;
		const dataRows = hasHeadings ? laterRows : rows;
		return { hasHeadings, headers, dataRows };
	},

	/**
	 * Best-guess column map for a bank statement. When the file has headings the guess
	 * comes from the heading words; otherwise it comes from the data itself — the column
	 * whose cells most often read as dates is the date, the longest text column is the
	 * description, and the remaining numeric column is the amount. Every guess is only a
	 * starting point for the user to confirm.
	 *
	 * @param {Object} params
	 * @param {Array} params.headers
	 * @param {Array} params.dataRows
	 * @param {Boolean} params.hasHeadings
	 * @returns {Object} { date, description, amount, moneyOut, moneyIn } column indexes, -1 when unmapped
	 */
	guessStatementColumnMap({ headers, dataRows, hasHeadings }) {
		if (hasHeadings) {
			const columnMap = {
				date: UtilityService.guessColumnIndex(headers, ["date", "posted", "transaction date", "post date"]),
				description: UtilityService.guessColumnIndex(headers, ["description", "details", "memo", "payee", "narrative", "particulars", "transaction"]),
				amount: UtilityService.guessColumnIndex(headers, ["amount", "value"]),
				moneyOut: UtilityService.guessColumnIndex(headers, ["withdrawal", "withdrawals", "debit", "paid out", "money out", "payment"]),
				moneyIn: UtilityService.guessColumnIndex(headers, ["deposit", "deposits", "credit", "paid in", "money in", "receipt"]),
			};
			return columnMap;
		}

		const columnCount = dataRows.reduce((widest, row) => Math.max(widest, row.length), 0);
		const columnIndexes = Array.from({ length: columnCount }, (unused, index) => index);
		const countMatching = (columnIndex, matches) => dataRows.filter(row => matches(row[columnIndex])).length;
		const dateCounts = columnIndexes.map(columnIndex => countMatching(columnIndex, cell => normalizeDate(cell) !== null));
		const amountCounts = columnIndexes.map(columnIndex => countMatching(columnIndex, cell => !isNaN(normalizeAmount(cell))));
		const textLengths = columnIndexes.map(columnIndex => dataRows.reduce((total, row) => total + String(row[columnIndex] || "").length, 0));

		const bestIndex = (scores, excluded) => {
			let winningIndex = -1;
			let winningScore = 0;
			scores.forEach((score, index) => {
				if (excluded.includes(index)) return;
				if (score > winningScore) {
					winningScore = score;
					winningIndex = index;
				}
			});
			return winningIndex;
		};

		const dateColumn = bestIndex(dateCounts, []);
		const amountColumn = bestIndex(amountCounts, [dateColumn]);
		const descriptionColumn = bestIndex(textLengths, [dateColumn, amountColumn]);
		return { date: dateColumn, description: descriptionColumn, amount: amountColumn, moneyOut: -1, moneyIn: -1 };
	},

	/**
	 * Turns mapped statement rows into transactions. The amount comes either from one
	 * signed column or from a money-out / money-in pair, where money in is positive and
	 * money out is negative — the direction the rest of the pipeline expects, and the
	 * reason a mapped pair needs no sign confirmation. Rows whose date or amount cannot
	 * be read are still returned, and counted, so nothing is silently dropped.
	 *
	 * @param {Object} params
	 * @param {Array} params.dataRows
	 * @param {Object} params.columnMap - { date, description, amount, moneyOut, moneyIn }
	 * @param {String} params.bankAccountName
	 * @param {String} [params.bankAccountNumber]
	 * @returns {Object} { transactions, invalidCount }
	 */
	extractTransactionsFromStatementGrid({ dataRows, columnMap, bankAccountName, bankAccountNumber = null }) {
		const cellAt = (row, columnIndex) => (columnIndex >= 0 ? row[columnIndex] || "" : "");
		const usesMoneyOutAndIn = columnMap.moneyOut >= 0 || columnMap.moneyIn >= 0;

		const rawDateStringsInThisColumn = dataRows.map(row => cellAt(row, columnMap.date));
		const isDateColumnDayFirst = detectWhetherTheDateColumnIsDayFirst(rawDateStringsInThisColumn);

		const amountForRow = row => {
			if (!usesMoneyOutAndIn) return normalizeAmount(cellAt(row, columnMap.amount));
			const moneyOut = normalizeAmount(cellAt(row, columnMap.moneyOut));
			const moneyIn = normalizeAmount(cellAt(row, columnMap.moneyIn));
			const hasNeither = isNaN(moneyOut) && isNaN(moneyIn);
			if (hasNeither) return NaN;
			const moneyOutOrZero = isNaN(moneyOut) ? 0 : Math.abs(moneyOut);
			const moneyInOrZero = isNaN(moneyIn) ? 0 : Math.abs(moneyIn);
			return moneyInOrZero - moneyOutOrZero;
		};

		let invalidCount = 0;
		const transactions = dataRows.map(row => {
			const rawDate = cellAt(row, columnMap.date);
			const date = normalizeDate(rawDate, isDateColumnDayFirst) || rawDate;
			const description = cellAt(row, columnMap.description);
			const amount = amountForRow(row);

			const hasInvalidDate = !normalizeDate(rawDate, isDateColumnDayFirst);
			const hasInvalidAmount = isNaN(amount);
			if (hasInvalidDate || hasInvalidAmount) invalidCount++;

			return { date, description, merchant: "", amount, bankAccountName, bankAccountNumber };
		});

		return { transactions, invalidCount };
	},

	/**
	 * Stamps NLP-matched general ledger accounts onto transactions (index-aligned with the
	 * categorized results); unmatched rows keep their existing classification. When the GL
	 * derived a default tax code for the matched account, it is applied unless the
	 * transaction already carries a tax code.
	 *
	 * A match also carries the account's FS mapping across. Matching only set the display
	 * string before, which left every GL-matched row showing an account in the Classification
	 * column and an em dash under FS Account — and left a stale canonical_code from an earlier
	 * classification sitting underneath a name that no longer agreed with it. The chart is
	 * what says which FS account an account maps to, so the mapping is read from there; an
	 * account absent from the chart keeps whatever the transaction already carried rather than
	 * being blanked.
	 *
	 * A matched row also loses its account_id, so the server works the account out from the
	 * ledger's label. The server reads the account a row already points at before its label, and
	 * every row it has sent back points at one — its account, or the client's Unclassified
	 * placeholder — so a match on a row the server already held was thrown away on save, and the
	 * server then stripped the method from a row left on Unclassified. An unmatched row keeps its
	 * account_id.
	 *
	 * A row that already sits on an account is left exactly as it was, the same object, when the
	 * ledger names an account the loaded chart does not have. With no account_id and a label the
	 * server cannot find, the server would fall back to the row's old canonical code and put it
	 * back on the AI's account, now marked as the ledger's answer, so an unconfirmed guess would
	 * leave To Review and nobody would sign it off. The one exception is an earlier gl_reference
	 * or payee_rule row whose own label also names no chart account: that is a ledger label left
	 * sitting on an older account by an earlier pass, so it is sent back to To Review as "ai" with
	 * everything else kept. An empty chart holds nothing back: the chart did not load, every
	 * lookup would miss and blame the ledger, and the server's own chart is the authority, so the
	 * row is stamped as usual. A row still waiting for an account is stamped as usual too.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array} params.categorized - [{ mappedAccount }] index-aligned to transactions
	 * @param {Object} [params.accountTaxes] - { [account]: taxCode } defaults derived from the GL
	 * @param {Array} [params.clientChart] - rows from getClientCoA, the record of FS mappings
	 * @param {Array} [params.canonicalTaxonomyOptions] - canonical nodes, for the display name
	 * @returns {Array}
	 */
	stampGeneralLedgerCategories({ transactions, categorized, accountTaxes = {}, clientChart = [], canonicalTaxonomyOptions = [] }) {
		let isUsableAccount = account => account && !LEDGER_ANSWERS_THAT_NAME_NO_ACCOUNT.includes(account);
		const stampedTransactions = transactions.map((transaction, index) => {
			let mappedAccount = categorized[index] ? categorized[index].mappedAccount : null;
			let matched = isUsableAccount(mappedAccount);

			const chartRow = matched ? this.findChartRowForAccountName({ clientChart, accountName: mappedAccount }) : null;
			const ownLabelChartRow = matched && !chartRow ? this.findChartRowForAccountName({ clientChart, accountName: transaction.classification_label }) : null;
			const howTheChartGapHoldsItBack = theChartLacksTheLedgersAccountForAPlacedRow({ transaction, mappedAccount, chartRow, ownLabelChartRow, clientChart });
			const isKeptExactlyAsItWas = howTheChartGapHoldsItBack === "keptAsAGuess" || howTheChartGapHoldsItBack === "keptOnTheEarlierLedgerAccount";
			if (isKeptExactlyAsItWas) return transaction;
			if (howTheChartGapHoldsItBack === "sentBackToReview") {
				const sentBackToReview = { ...transaction, classification_method: "ai" };
				return sentBackToReview;
			}

			const mappedCanonicalCode = chartRow && chartRow.canonical_code ? chartRow.canonical_code : null;
			const canonicalNode = mappedCanonicalCode ? canonicalTaxonomyOptions.find(option => option.canonical_code === mappedCanonicalCode) : null;

			const summary = categorized[index] || {};
			const aPersonAlreadyPlacedThisPayee = !!summary.decidedByAPerson;

			return {
				...transaction,
				classification: matched ? mappedAccount : transaction.classification,
				classification_label: matched ? mappedAccount : (transaction.classification_label || null),
				account_id: matched ? null : transaction.account_id,
				classification_method: matched ? (aPersonAlreadyPlacedThisPayee ? "payee_rule" : "gl_reference") : transaction.classification_method,
				confidence: matched && summary.confidence !== null && summary.confidence !== undefined ? summary.confidence : transaction.confidence,
				canonical_code: mappedCanonicalCode || transaction.canonical_code || null,
				canonical_label: canonicalNode ? canonicalNode.display_name : (mappedCanonicalCode ? "" : transaction.canonical_label || null),
				gifi_code: mappedCanonicalCode ? (chartRow.gifi_code || (canonicalNode ? canonicalNode.gifi_code : null)) : (transaction.gifi_code || null),
				tax: matched ? (transaction.tax || accountTaxes[mappedAccount] || "") : transaction.tax,
				cwClassification: matched ? mappedAccount : null,
				cwClassified: matched,
				glClassified: matched,
				source_evidence: matched
					? this.describeGeneralLedgerClassification({
						account: mappedAccount,
						similarity: categorized[index] ? categorized[index].score : null,
						matchedLedgerDescription: categorized[index] ? categorized[index].matchedLedgerDescription : null,
					})
					: transaction.source_evidence || null,
			};
		});
		return stampedTransactions;
	},

	/**
	 * The ledger accounts the chart lacks, and how many rows each left in To Review. It walks the
	 * rows exactly as stampGeneralLedgerCategories does and asks the same question of each, so the
	 * two always agree. A row kept as the AI's guess, or sent back to To Review, is counted under
	 * the ledger account that held it back; an earlier ledger placement left where it was is not
	 * waiting in To Review, so it is not counted. An empty chart holds nothing back, so it lists
	 * nothing.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array} params.categorized - [{ mappedAccount }] index-aligned to transactions
	 * @param {Array} [params.clientChart] - rows from getClientCoA
	 * @returns {Array} [{ accountName, rowCount }], most rows first, then by name
	 */
	listLedgerAccountsTheChartLacks({ transactions, categorized, clientChart = [] }) {
		const rowCountByAccountName = new Map();
		transactions.forEach((transaction, index) => {
			let mappedAccount = categorized[index] ? categorized[index].mappedAccount : null;
			const chartRow = this.findChartRowForAccountName({ clientChart, accountName: mappedAccount });
			const ownLabelChartRow = chartRow ? null : this.findChartRowForAccountName({ clientChart, accountName: transaction.classification_label });
			const howTheChartGapHoldsItBack = theChartLacksTheLedgersAccountForAPlacedRow({ transaction, mappedAccount, chartRow, ownLabelChartRow, clientChart });
			const isLeftInToReview = howTheChartGapHoldsItBack === "keptAsAGuess" || howTheChartGapHoldsItBack === "sentBackToReview";
			if (!isLeftInToReview) return;

			const rowsCountedSoFar = rowCountByAccountName.get(mappedAccount) || 0;
			rowCountByAccountName.set(mappedAccount, rowsCountedSoFar + 1);
		});

		const ledgerAccounts = [...rowCountByAccountName].map(([accountName, rowCount]) => ({ accountName, rowCount }));
		const ledgerAccountsMostRowsFirst = ledgerAccounts.sort((first, second) => second.rowCount - first.rowCount || first.accountName.localeCompare(second.accountName));
		return ledgerAccountsMostRowsFirst;
	},



	/**
	 * The GST/HST inside a tax-inclusive amount, as the books will post it.
	 *
	 * Mirrors the server's salesTaxService so the screen and the statements agree: the label says
	 * what rate the price includes, how much of that is GST/HST and what share is recoverable, and
	 * the tax is backed out of the gross. That is what makes "M&E 6.5%" half of a 13% tax rather
	 * than 6.5% of the price, and keeps the PST half of "GST & PST 12%" out of the figure — PST is
	 * never recoverable. The first number in the label used to be treated as recoverable in full.
	 *
	 * @param {Number} amount - signed, tax inclusive
	 * @param {String} tax - the label on the row, e.g. "HST 13%"
	 * @param {Boolean} hasTax - false when this file tracks no sales tax
	 * @returns {Number} the GST/HST, signed like the amount, to the cent
	 */
	calculateTaxAmount({ amount, tax, hasTax }) {
		if (!hasTax) return 0.0;

		const carried = describeTaxCarriedByLabel({ taxCode: tax });
		if (!carried) return 0.0;

		const parsedAmount = parseFloat(amount);
		if (!isFinite(parsedAmount)) return 0.0;

		const recoverable = (parsedAmount * carried.gstHstRate * carried.recoverableShare) / (100 + carried.priceIncludesRate);
		const taxAmount = parseFloat(recoverable.toFixed(2));
		return taxAmount;
	},

	/**
	 * Says, in sentences a preparer can act on, which tax stayed in revenue or expenses and why.
	 *
	 * The books report every taxed line whose tax could not leave its classification, one row at a
	 * time with a reason code. Read as a list it says nothing; grouped by reason it says "the tax on
	 * 46 transactions stayed in the expense because this chart has no GST/HST account", which is a
	 * fact the preparer can change by adding the account, or accept for an exempt practice.
	 *
	 * @param {Array} taxNotSplit - [{ reason, taxLeftInPlace, ... }] from the trial balance
	 * @returns {Array<String>} one sentence per reason present, empty when every tax posted
	 */
	describeTaxNotSplit({ taxNotSplit }) {
		const byReason = {};
		for (const row of taxNotSplit || []) {
			if (!byReason[row.reason]) byReason[row.reason] = { count: 0, total: 0 };
			byReason[row.reason].count += 1;
			byReason[row.reason].total += Math.abs(Number(row.taxLeftInPlace) || 0);
		}

		const sentences = [];
		for (const reason of Object.keys(byReason)) {
			const { count, total } = byReason[reason];
			const rows = `${count} transaction${count === 1 ? "" : "s"}`;
			const amountText = `$${UtilityService.formatCurrency(total)}`;
			if (reason === TAX_NOT_SPLIT_REASONS.NO_GST_HST_ACCOUNT_ON_CHART) {
				sentences.push(`Tax on ${rows} (${amountText}) stayed in revenue and expenses — this chart has no GST/HST account. Add one to post it.`);
			} else if (reason === TAX_NOT_SPLIT_REASONS.CLASSIFIED_TO_A_TAX_ACCOUNT) {
				sentences.push(`${rows} carrying a tax code (${amountText}) are posted to the GST/HST account itself, so their tax was not split out.`);
			} else if (reason === TAX_NOT_SPLIT_REASONS.LABEL_CARRIES_NO_GST_HST) {
				sentences.push(`${rows} carry only provincial tax (${amountText}); it stays where it is because there is no PST or QST account.`);
			} else if (reason === TAX_NOT_SPLIT_REASONS.PST_PORTION_LEFT_IN_PLACE) {
				sentences.push(`The PST on ${rows} (${amountText}) stayed in revenue — there is no PST account to post it to.`);
			} else if (reason === TAX_NOT_SPLIT_REASONS.NOT_YET_CLASSIFIED) {
				sentences.push(`Tax on ${rows} (${amountText}) stays with them in suspense until they are classified.`);
			} else if (reason === TAX_NOT_SPLIT_REASONS.RESTRICTED_CREDIT_LABEL_ON_MONEY_IN) {
				sentences.push(`${rows} of money in carry a meals label (${amountText}); a half credit applies to purchases, not to tax collected, so the tax stayed in revenue — fix the label.`);
			} else {
				sentences.push(`Tax on ${rows} (${amountText}) was not split: ${reason}.`);
			}
		}
		return sentences;
	},



	/**
	 * Maps CaseWare categorized accounts to their full display names.
	 * Returns "Unclassified" for accounts not found in the trial balance.
	 *
	 * @param {Array} casewareCategorizedAccounts
	 * @param {Array} priorYearTrialBalanceExtract
	 * @returns {Array} Array of strings like "12000 - Accounts Receivable"
	 */
	mapAccountDescriptionToAccount({ casewareCategorizedAccounts, priorYearTrialBalanceExtract }) {
		let descriptions = [];

		for (let categorizedAccount of casewareCategorizedAccounts) {
			let mappedAccountNumber = categorizedAccount.mappedAccount;

			let matchingAccount = null;
			for (let trialBalanceItem of priorYearTrialBalanceExtract) {
				if (trialBalanceItem.accountNumber === mappedAccountNumber) {
					matchingAccount = trialBalanceItem;
					break;
				}
			}

			if (!matchingAccount) {
				descriptions.push("Unclassified");
				continue;
			}

			descriptions.push(buildFullAccountDescription(matchingAccount.accountNumber, matchingAccount.accountDescription));
		}

		return descriptions;
	},


	/**
	 * Separates transactions into the ones that still need an account and the ones that do not.
	 *
	 * This decides what the AI is asked to classify, so it asks the same question the Unclassified
	 * tab and the counters ask. A transfer leg waiting on the Transfers tab is not work for the
	 * model: asking anyway pays for a guess and then writes an account onto a row whose whole
	 * point is that it must not carry one until the pair is approved or rejected.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Object} { classifiedTransactions, unclassifiedTransactions }
	 */
	getClassifiedAndUnclassifiedTransactions({ transactions }) {
		const unclassifiedTransactions = this.listTransactionsStillNeedingAnAccount({ transactions });
		const stillWaiting = new Set(unclassifiedTransactions);
		const classifiedTransactions = (transactions || []).filter(transaction => !stillWaiting.has(transaction));

		return { classifiedTransactions, unclassifiedTransactions };
	},


	/**
	 * Merges AI classification results with transactions and fetches tax rates.
	 *
	 * @param {Array} unclassifiedTransactions
	 * @param {Array} openAiClassifications - AI results parallel to unclassifiedTransactions
	 * @param {Array} classifiedTransactions - Already classified (CaseWare or manual)
	 * @param {String} province
	 * @param {String} industry
	 * @returns {Promise<Object>} { openAiAccountsTaxClassifications, updatedTransactions }
	 */
	async getOpenAiAccountsTaxClassifications({ unclassifiedTransactions, openAiClassifications, classifiedTransactions, province, industry }) {
		let aiClassifiedTransactions = this.applyAiClassifications({ unclassifiedTransactions, aiClassifications: openAiClassifications });
		let allTransactions = [...classifiedTransactions, ...aiClassifiedTransactions];
		let uniqueAccountNames = this.getUniqueClassifiedAccountNames({ transactions: allTransactions });
		let accountTaxRates = await this.fetchTaxRatesForAccounts({ accountNames: uniqueAccountNames, province, industry });

		return {
			openAiAccountsTaxClassifications: accountTaxRates,
			updatedTransactions: allTransactions,
		};
	},

	/**
	 * Applies AI classification results to unclassified transactions.
	 *
	 * @param {Array} unclassifiedTransactions
	 * @param {Array} aiClassifications
	 * @returns {Array} Transactions with AI classifications applied
	 */
	applyAiClassifications({ unclassifiedTransactions, aiClassifications }) {
		let results = [];

		for (let transactionIndex = 0; transactionIndex < unclassifiedTransactions.length; transactionIndex++) {
			let transaction = unclassifiedTransactions[transactionIndex];
			let aiResult = (aiClassifications && aiClassifications[transactionIndex]) || {};

			results.push({
				...transaction,
				classification: aiResult.classification || "Unclassified",
				tax: aiResult.tax || "",
				confidence: normalizeConfidence(aiResult.confidence),
			});
		}

		return results;
	},

	/**
	 * Extracts unique classified account names from transactions.
	 *
	 * @param {Array} transactions
	 * @returns {Array} Unique account name strings
	 */
	getUniqueClassifiedAccountNames({ transactions }) {
		let uniqueNames = new Set();

		for (let transaction of transactions) {
			if (isUnclassified(transaction.classification)) continue;
			uniqueNames.add(transaction.classification);
		}

		const accountNames = Array.from(uniqueNames);
		return accountNames;
	},

	/**
	 * Fetches tax rates for a list of account names.
	 *
	 * @param {Array} accountNames
	 * @param {String} province
	 * @param {String} industry
	 * @returns {Promise<Array>} Array of { account, taxRate }
	 */
	async fetchTaxRatesForAccounts({ accountNames, province, industry }) {
		let promises = [];

		for (let accountName of accountNames) {
			promises.push(
				this.getTaxRates({ account: accountName, province, industry }).then(function (taxRate) {
					return { account: accountName, taxRate };
				})
			);
		}

		return await Promise.all(promises);
	},


	/**
	 * Clears a free-text classification that names no account.
	 *
	 * This used to classify by keyword before the model was asked: a cheque went to the clearing
	 * account, anything matching "receiver general" went to Payroll Source Deductions, and any
	 * other government payment went to Income Tax Payable — real accounts, chosen by a regular
	 * expression, at a confidence of 0.80 and marked as a known payee pattern. It was a rule
	 * engine in the browser, and it survived the removal of the one on the server. A description
	 * that merely mentions a government body is not evidence of which account it belongs in, and
	 * a remittance filed to income tax when it was payroll misstates two liabilities at once.
	 *
	 * Those rows are now unclassified, which is what they are, and the model is asked about them
	 * like everything else.
	 *
	 * It also used to file both legs of every transfer it spotted into the clearing account before
	 * anyone had approved them. Detecting a transfer is a proposal, and a proposal is not a place
	 * to put money: a leg parked in suspense on the strength of a guess is indistinguishable from
	 * one a preparer settled there, and the server strips a decision that names no real account, so
	 * the marking did not survive the trip anyway. Proposals are worked out live for the Transfers
	 * tab and recorded only when approved.
	 *
	 * A row that carries an account_id is never cleared: the server placed it on a real account.
	 * After a reload a row on a chart account with no FS mapping has no canonical_code, and the
	 * server has already dropped its method, so without this it read as naming no account, was
	 * cleared, and the model's answer then landed on it.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array}
	 */
	clearClassificationsThatNameNoAccount({ transactions }) {
		const results = [];

		for (const transaction of transactions || []) {
			const theServerPlacedIt = !!transaction.account_id;
			const namesAnAccountThatDoesNotExist = !theServerPlacedIt && !transaction.canonical_code && !transaction.glClassified && !isUnclassified(transaction.classification);
			if (namesAnAccountThatDoesNotExist) {
				results.push({ ...transaction, classification: null });
				continue;
			}
			results.push(transaction);
		}

		return results;
	},


	/**
	 * The transfer pairs a preparer already approved, read from the key the server stored rather
	 * than worked out again.
	 *
	 * Approving a transfer writes mirrors_transaction_id on both legs, so which two rows are the
	 * pair is a recorded fact. Joining on it finds them every time, whatever the rows look like
	 * afterwards.
	 *
	 * They used to be re-derived on every load from the amounts, the dates and the two bank
	 * account names, which found a settled pair back only while it still resembled one. A card
	 * payment leaving the chequing account three days before it reached the card fell outside the
	 * date tolerance, so the pair was never rebuilt: both legs left the Transfers tab, and because
	 * approving had given them a real account they were not in Unclassified either. The transfer
	 * looked like it had disappeared.
	 *
	 * Every settled leg is claimed here even when its counterpart is not in the file — an archived
	 * counterpart, say — so that the guesswork below can never pair a settled leg with a stranger.
	 * That claim is made purely from the stored link, regardless of either leg's current
	 * classification — a row somebody has since reclassified away from "transfer" still has a
	 * real counterpart on file, and re-guessing it into a different pair would be worse than
	 * leaving it unpaired.
	 *
	 * A pair only reaches the Transfers tab, though, while it still reads as one: reclassifying
	 * either leg away from "transfer" (the one signal every other reader of "is this a transfer"
	 * already goes by) takes the pair out of the list the tab and its badge count are built from,
	 * instead of leaving a resolved-looking pair on screen for a transaction the Reason column, the
	 * Review tab and the classification summary all now describe as something else.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Object} { pairs, legsAlreadySettled }
	 */
	readTransferPairsTheServerRecorded({ transactions }) {
		let transactionsById = new Map();
		for (let transaction of transactions) {
			if (transaction.id !== undefined && transaction.id !== null) transactionsById.set(String(transaction.id), transaction);
		}

		let pairs = [];
		let legsAlreadySettled = new Set();

		for (let transaction of transactions) {
			if (!transaction.mirrors_transaction_id) continue;
			if (legsAlreadySettled.has(transaction)) continue;
			legsAlreadySettled.add(transaction);

			let counterpart = transactionsById.get(String(transaction.mirrors_transaction_id));
			if (!counterpart || counterpart === transaction) continue;
			if (legsAlreadySettled.has(counterpart) && counterpart.mirrors_transaction_id) {
				let counterpartIsAlreadyInAPair = pairs.some(pair => pair.outLeg === counterpart || pair.inLeg === counterpart);
				if (counterpartIsAlreadyInAPair) continue;
			}

			legsAlreadySettled.add(counterpart);

			let bothLegsStillReadAsATransfer = transaction.classification_method === "transfer" && counterpart.classification_method === "transfer";
			if (!bothLegsStillReadAsATransfer) continue;
			pairs.push(buildTransferPairSummary({ transactionA: transaction, transactionB: counterpart }));
		}

		return { pairs, legsAlreadySettled };
	},

	/**
	 * Finds every inter-account transfer pair for the Transfers review — the pairs already
	 * approved, read from the server's own record, followed by the pairs still being proposed.
	 * Each transaction is paired at most once.
	 *
	 * A transaction whose description names a shareholder or related party (`RELATED_PARTY_DESCRIPTION_PATTERN`,
	 * matching the backend's `crossCheckRegistry.js` related-party check) is never proposed as a
	 * transfer leg, even when its amount and date happen to match another transaction exactly. The
	 * approved pairing flow force-classifies a leg to whichever account the counterpart money
	 * arrived in — correct for a genuine inter-account transfer, wrong for a shareholder loan draw
	 * or repayment that coincidentally shares an amount and date with something else. Left for
	 * manual classification instead.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array} pairs of { outLeg, inLeg, cadValue, isCrossCurrency, status }
	 */
	getTransferPairs({ transactions }) {
		let { pairs, legsAlreadySettled } = this.readTransferPairsTheServerRecorded({ transactions });
		let usedTransactions = legsAlreadySettled;

		const legs = transactions.map(transaction =>
			isTransferPairCandidate({ transaction }) ? describeLegForPairing({ transaction }) : null);

		for (let sourceIndex = 0; sourceIndex < legs.length; sourceIndex++) {
			let legA = legs[sourceIndex];
			if (!legA || usedTransactions.has(legA.transaction)) continue;

			for (let candidateIndex = sourceIndex + 1; candidateIndex < legs.length; candidateIndex++) {
				let legB = legs[candidateIndex];
				if (!legB || usedTransactions.has(legB.transaction)) continue;
				if (legA.accountName === legB.accountName) continue;
				if (!transferLegsMatchInCad({ legA, legB, allowCrossCurrency: true })) continue;
				if (!datesAreWithinTolerance({ legA, legB })) continue;

				usedTransactions.add(legA.transaction);
				usedTransactions.add(legB.transaction);
				pairs.push(buildTransferPairSummary({ transactionA: legA.transaction, transactionB: legB.transaction }));
				break;
			}
		}

		return pairs;
	},

	/**
	 * Reverses each transfer pair back to an unclassified transaction, so it flows through
	 * normal classification again. Returns a new transactions array with the legs cleared.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array} params.pairs - pairs from getTransferPairs
	 * @returns {Array}
	 */
	reverseTransferPairs({ transactions, pairs }) {
		let legsToReverse = new Set();
		for (let pair of pairs) {
			legsToReverse.add(pair.outLeg);
			legsToReverse.add(pair.inLeg);
		}

		let updatedTransactions = transactions.map(transaction => {
			if (!legsToReverse.has(transaction)) return transaction;
			return { ...transaction, classification: "Unclassified", isTransfer: false, canonical_code: null, canonical_label: null, classification_method: null, confidence: null };
		});

		return updatedTransactions;
	},

	/**
	 * Validates whether two transactions can be manually marked as a transfer pair.
	 * Requires two different accounts and opposite signs. A same-currency pair must
	 * match to the cent — a difference there is a fee or a data error, so it is
	 * surfaced (invalid) rather than silently absorbed. A cross-currency pair is
	 * allowed regardless of the difference, which is the realized FX.
	 *
	 * @param {Object} params
	 * @param {Object} params.transactionA
	 * @param {Object} params.transactionB
	 * @returns {Object} { isValid, error, isCrossCurrency, differenceCad }
	 */
	getManualTransferValidation({ transactionA, transactionB }) {
		const accountA = transferAccountName({ transaction: transactionA });
		const accountB = transferAccountName({ transaction: transactionB });
		if (!accountA || !accountB) return { isValid: false, error: "Both transactions need a bank account." };
		if (accountA === accountB) return { isValid: false, error: "A transfer must be between two different accounts." };

		const cadA = cadEquivalentAmount({ transaction: transactionA });
		const cadB = cadEquivalentAmount({ transaction: transactionB });
		if (Math.sign(cadA) === Math.sign(cadB)) return { isValid: false, error: "A transfer needs one outgoing and one incoming transaction (opposite signs)." };

		const isCrossCurrency = (transactionA.currency || "CAD") !== (transactionB.currency || "CAD");
		const differenceCad = Math.abs(cadA + cadB);

		if (!isCrossCurrency && differenceCad > AMOUNT_TOLERANCE) {
			const differenceText = differenceCad.toFixed(2);
			return { isValid: false, error: `The amounts differ by CAD ${differenceText}. A same-currency transfer should match to the cent — if that difference is a bank fee, record it as its own Bank Charges transaction; if a leg is wrong, fix it first.` };
		}

		if (isCrossCurrency) {
			const largerCadValue = Math.max(Math.abs(cadA), Math.abs(cadB));
			const allowedDifference = largerCadValue * CROSS_CURRENCY_TOLERANCE_RATE;
			if (differenceCad > allowedDifference) {
				const differenceText = differenceCad.toFixed(2);
				return { isValid: false, error: `The CAD-equivalent amounts differ by CAD ${differenceText}, more than the ~3% an exchange-rate spread explains. Check the amounts and that each account's currency is set correctly — this may not be a matching transfer.` };
			}
		}

		return { isValid: true, error: null, isCrossCurrency, differenceCad };
	},

	/**
	 * Whether a pair can be recorded, and why not when it cannot.
	 *
	 * Recording names each leg by the other's transaction id, so a row the server has never seen
	 * cannot be half of a pair. The Transfers tab and the hand-marking modal both ask this, so a
	 * preparer gets one answer in one wording wherever they happen to be standing.
	 *
	 * @param {Object} params
	 * @param {Object} params.pair
	 * @returns {Object} { isRecordable, error }
	 */
	checkTransferPairIsRecordable({ pair }) {
		const outLegIsSaved = !!(pair && pair.outLeg && pair.outLeg.id);
		const inLegIsSaved = !!(pair && pair.inLeg && pair.inLeg.id);
		if (!outLegIsSaved || !inLegIsSaved) return { isRecordable: false, error: "A leg has not been saved yet — save the import first." };

		return { isRecordable: true, error: null };
	},

	/**
	 * The pair a preparer picked by hand, ordered and ready to record, or the reason it cannot be.
	 *
	 * Which leg is the outgoing one follows from the sign of the amount, and it is decided here by
	 * the same function that orders a detected pair — a hand-marked transfer and a detected one are
	 * the same thing once recorded, so they cannot be allowed to order themselves differently. That
	 * decision briefly lived in the table instead, where it had already drifted into a second
	 * spelling of the same test.
	 *
	 * @param {Object} params
	 * @param {Object} params.transactionA
	 * @param {Object} params.transactionB
	 * @returns {Object} { pair, error }
	 */
	buildManualTransferPair({ transactionA, transactionB }) {
		const validation = this.getManualTransferValidation({ transactionA, transactionB });
		if (!validation.isValid) return { pair: null, error: validation.error };

		const pair = buildTransferPairSummary({ transactionA, transactionB });
		const recordable = this.checkTransferPairIsRecordable({ pair });
		if (!recordable.isRecordable) return { pair: null, error: recordable.error };

		return { pair, error: null };
	},

	/**
	 * Normalizes a canonical account code to its top statement category. INCOME folds
	 * into REVENUE; anything unrecognized returns OTHER.
	 *
	 * @param {Object} params
	 * @param {String} params.canonicalCode - dotted canonical code (may be empty)
	 * @returns {String} REVENUE | COGS | EXPENSE | ASSET | LIABILITY | EQUITY | OTHER
	 */
	canonicalTopCategory({ canonicalCode }) {
		const topSegment = (canonicalCode || "").split(".")[0].toUpperCase();
		if (topSegment === "REVENUE" || topSegment === "INCOME") return "REVENUE";
		if (topSegment === "COGS") return "COGS";
		if (topSegment === "EXPENSE") return "EXPENSE";
		if (topSegment === "ASSET") return "ASSET";
		if (topSegment === "LIABILITY") return "LIABILITY";
		if (topSegment === "EQUITY") return "EQUITY";
		return "OTHER";
	},

	/**
	 * Infers an account's nature from its name when no canonical mapping exists yet.
	 * A display-only fallback (never books anything): expense phrases are tested first
	 * because some contain "sales" (e.g. "cost of sales") and must not read as revenue.
	 *
	 * @param {Object} params
	 * @param {String} params.accountName
	 * @returns {String|null} "revenue" | "expense" | null
	 */
	accountNatureFromName({ accountName }) {
		const lowerName = (accountName || "").toLowerCase();
		const expensePhrases = ["cost of sales", "cost of goods", "cogs", "expense", "wage", "salar", "payroll", "rent", "utilit", "subscription", "insurance", "depreciat", "amortiz", "supplies", "advertis", "bank charge", "interest expense", "meals", "repairs"];
		const matchesExpense = expensePhrases.some(phrase => lowerName.includes(phrase));
		if (matchesExpense) return "expense";
		const revenuePhrases = ["revenue", "sales", "income", "turnover", "fees earned"];
		const matchesRevenue = revenuePhrases.some(phrase => lowerName.includes(phrase));
		if (matchesRevenue) return "revenue";
		return null;
	},

	/**
	 * Coarse account nature for the year-over-year favourability colouring: revenue vs
	 * expense vs balance-sheet. Uses the canonical mapping when present, else falls back
	 * to the account name so the review still colours income-statement lines pre-mapping.
	 *
	 * @param {Object} params
	 * @param {String} params.canonicalCode
	 * @param {String} params.accountName
	 * @returns {String|null} "revenue" | "expense" | "balance" | null
	 */
	getAccountNature({ canonicalCode, accountName }) {
		const category = this.canonicalTopCategory({ canonicalCode });
		if (category === "REVENUE") return "revenue";
		if (category === "COGS" || category === "EXPENSE") return "expense";
		if (category === "ASSET" || category === "LIABILITY" || category === "EQUITY") return "balance";
		return this.accountNatureFromName({ accountName });
	},

	/**
	 * Ranks the transactions that could be the other leg of a manual transfer for a
	 * given leg: opposite sign, a different account, best amount match first, then
	 * nearest date. Used to suggest the counterpart in the manual-transfer modal.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Object} params.leg - the already-chosen leg
	 * @returns {Array} candidate transactions, best match first
	 */
	getManualTransferCandidates({ transactions, leg }) {
		if (!leg) return [];
		const legCad = cadEquivalentAmount({ transaction: leg });
		const legAccount = transferAccountName({ transaction: leg });

		const eligibleCandidates = (transactions || []).filter(candidate => {
			if (candidate === leg) return false;
			const candidateCad = cadEquivalentAmount({ transaction: candidate });
			if (!candidateCad) return false;
			if (Math.sign(candidateCad) === Math.sign(legCad)) return false;
			if (transferAccountName({ transaction: candidate }) === legAccount) return false;
			return true;
		});

		const scoredCandidates = eligibleCandidates.map(candidate => {
			const candidateCad = cadEquivalentAmount({ transaction: candidate });
			const amountDifference = Math.abs(Math.abs(candidateCad) - Math.abs(legCad));
			const dayDifference = Math.abs((new Date(candidate.date) - new Date(leg.date)) / 86400000);
			return { candidate, amountDifference, dayDifference };
		});

		scoredCandidates.sort((a, b) => {
			if (a.amountDifference !== b.amountDifference) return a.amountDifference - b.amountDifference;
			return a.dayDifference - b.dayDifference;
		});

		return scoredCandidates.map(entry => entry.candidate);
	},

	/**
	 * Dismisses each proposed pair as "not a transfer", so both legs drop out of the
	 * Transfers review and are never proposed again. The legs stay unclassified and flow
	 * through normal classification. Returns a new transactions array with the legs flagged.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array} params.pairs - pairs from getTransferPairs
	 * @returns {Array}
	 */
	rejectTransferPairs({ transactions, pairs }) {
		let legsToReject = new Set();
		for (let pair of pairs) {
			legsToReject.add(pair.outLeg);
			legsToReject.add(pair.inLeg);
		}

		let updatedTransactions = transactions.map(transaction => {
			if (!legsToReject.has(transaction)) return transaction;
			return { ...transaction, notATransfer: true, isTransfer: false };
		});

		return updatedTransactions;
	},

	/**
	 * Gets the unique bank account names from transactions, sorted. Reads whichever
	 * field the transaction shape carries: bankAccountName (import rows) or
	 * accountName (table rows).
	 *
	 * @param {Array} transactions
	 * @returns {Array} Unique bank account names
	 */
	getUniqueBankAccountNames({ transactions }) {
		let names = new Set();

		for (let transaction of transactions || []) {
			let bankName = transaction.bankAccountName || transaction.accountName;
			if (bankName) {
				names.add(bankName);
			}
		}

		const uniqueNames = Array.from(names);
		const sortedNames = uniqueNames.sort();
		return sortedNames;
	},



};

export default BookkeepingService;
